import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, BehaviorSubject, throwError, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { agGridLocaleText } from '../../assets/vrm-language-scripts/ag-grid-locale-text';

import { CommonService, NumericRatingToTextEnum } from './common.service';

import { Config } from '../config';
import { ReportOption } from '../model/reports';
import { FilterColumn } from '../model/filter';
import {
  ColDef,
  ColGroupDef,
  ColSpanParams,
  GridOptions,
  IServerSideGetRowsRequest,
  ProcessCellForExportParams,
  RowNode,
} from 'ag-grid-community';
import { User, ValueTextObject } from '../model/user';
import { ReportInformation } from '../components/vrm-mentor-reports/vrm-mentor-reports.component';
import {
  isMoreThan90DaysAhead,
  isBetween30And90DaysAhead,
  isLessThan30DaysAhead,
  dateWithinDaysWindow,
} from 'src/app/helpers/date-comparisons';
import * as moment from 'moment';
import { ActivatedRoute, Params } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { PredefinedFiltersService, RawServerFilterConfig } from './predefined-filters.service';
import { GridStateService } from './gridstate.service';
import { DamageTypeExcelStyles } from '../model/ImageData/GICInformation';
import { PinnedRowStyleComponent } from '../components/ag-grid-cell-renderers/pinned-row-style.component';
import { TranslationsService } from './translations.service';
import { courseOutcomeDataMap } from '../data/data-maps/course-outcomes';
import { driveWorkDataMap } from '../data/data-maps/courses';
import { ColumnsService } from './columns.service';
import { DataMapStructure } from '../data/data-maps/data-map';

export interface SsrTotalRows {
  filtered?: number;
  total?: number;
}

export interface SsrPagination {
  rowsPerPage: number;
}

interface ReportBody {
  filter?: any;
  search?: any;
  periodFilter?: PeriodFilter | string;
  course?: string;
  gridParams?: unknown;
  decorate?: string;
  selectedLanguage?: string;
  timeZone?: string;
}

export interface GridState {
  sortModel: any;
  filterModel: any;
  displayedColumns: any;
  overrideAPIColumnsVisibility: any;
  hiddenColumns: any;
  columnGroups: any;
  selectedServerFilter: any;
}

enum VrmColumnTypes {
  text = 'text',
  number = 'number',
  decimal = 'decimal',
  percentage = 'percentage',
  date = 'date',
  email = 'email',
  file = 'file',
  image = 'image',
  operations = 'operations',
}

export enum ReportNameEnum {
  scorecard = 'Scorecard Index report',
  mentor = 'Mentor Index report',
  station = 'Station Performance report',
  stationDelta = 'Station Performance Delta report',
  incidentsCollisions = 'Incidents/Collisions Report',
  coachingRecommendations = 'Coaching Recommendations Report',
  driverReportCompare = 'Mentor Driver Delta Performance report',
}

export enum ColIdEnum {
  trainingComplete = 'trainingComplete',
  trainingAssigned = 'trainingAssigned',
  totalDriverCount = 'totalDriverCount',
  firstName = 'firstName',
  lastName = 'lastName',
  pin = 'pin',
  realStaffNumber = 'realStaffNumber',
  vin = 'vin',
  email = 'email',

  action = 'action',
  actions = 'actions',

  location = 'location',
  location1 = 'location1',
  location2 = 'location2',
  location3 = 'location3',
  location4 = 'location4',
  location5 = 'location5',
  location6 = 'location6',
  location7 = 'location7',
  location8 = 'location8',
  location9 = 'location9',
  location10 = 'location10',
  location11 = 'location11',
  location12 = 'location12',
  location13 = 'location13',
  location14 = 'location14',
  location15 = 'location15',
  location16 = 'location16',
  location17 = 'location17',
  location18 = 'location18',
  location19 = 'location19',
  location20 = 'location20',

  question_pfizer_usa_insurance_type = 'question_pfizer_usa_insurance_type',

  partialIndexRating = 'partialIndexRating',
  indexingCoach = 'indexingCoach',
  virtualSafetyDiscussionStatus = 'virtualSafetyDiscussionStatus',
  indexingCoachCount = 'indexingCoachCount',
  indexingRiskCoach = 'indexingRiskcoach',
  indexingIndexValue = 'indexingIndexValue',
  indexingRiskCoachPlus = 'indexingRiskcoachPlus',
  indexingPcc = 'indexingPcc',
  indexingDiffLastMonth = 'indexingDiffLastMonth',
  indexingDiffLast3Month = 'indexingDiffLast3Month',
  indexingDiffLast6Month = 'indexingDiffLast6Month',
  indexingDiffLast12Month = 'indexingDiffLast12Month',
  indexingViolationsActive = 'indexingViolationsActive',
  indexingLicenceStatus = 'indexingLicenceStatus',
  dvlaLicenceStatus = 'dvlaLicenceStatus',
  indexingViolations = 'indexingViolations',
  ratingTo = 'ratingTo',
  ratingFrom = 'ratingFrom',
  direction = 'direction',
  orderMvr = 'orderMVR',
  alertActionRequired = 'alertActionRequired',
  pccCourseStatus = 'pccCourseStatus',
  derStatus = 'derStatus',
  pccCourseCount = 'pccCourseCount',
  alertResolvedStatus = 'alertResolvedStatus',
  downloadCustomReport = 'downloadCustomReport',

  rating = 'rating',
  profileDriver = 'profileDriver',
  defensiveDriving = 'defensiveDriving',

  courseProgress = 'courseProgress',
  courseStatus = 'courseStatus',
  notificationStatus = 'notificationStatus',
  tooltipStatus = 'tooltipStatus',
  picStatusDt = 'picStatusDt',
  orderStatus = 'orderStatus',
  allowMvr = 'allowMvr',
  requireDocs = 'requireDocs',
  isActive = 'isActive',
  vrmStatus = 'vrmStatus',
  mvrRating = 'mvrRating',
  mvrLicenseClass = 'mvrLicenseClass',
  mvrSvcCodes = 'mvrSvcCodes',
  endorsements = 'endorsements',
  orderType = 'orderType',
  mvrError = 'mvrError',
  mvrWarning = 'mvrWarning',
  mvrCompanyRating = 'mvrCompanyRating',
  consentStatus = 'consentStatus',
  driverDetails = 'driverDetails',
  mentorConsent = 'mentorConsent',
  requiredDoc = 'requiredDoc',
  licenceState = 'licenceState',
  mvrStatus = 'mvrStatus',

  engineOff = 'engineOff',
  accelerationRating = 'accelerationRating',
  brakingRating = 'brakingRating',
  corneringRating = 'corneringRating',
  distractionRating = 'distractionRating',
  speedRating = 'speedRating',

  hardAcceleration = 'hardAcceleration',
  hardAccelerationDelta = 'hardAccelerationDelta',
  hardAccelerationPerMillionKms = 'hardAccelerationPerMillionKms',
  hardAccelerationPerHundredKms = 'hardAccelerationPerHundredKms',
  hardAccelerationPerMillionMiles = 'hardAccelerationPerMillionMiles',
  hardAccelerationPerHundredMiles = 'hardAccelerationPerHundredMiles',

  braking = 'braking',
  hardBraking = 'hardBraking',
  hardBrakingDelta = 'hardBrakingDelta',
  hardBrakingPerMillionKms = 'hardBrakingPerMillionKms',
  hardBrakingPerHundredKms = 'hardBrakingPerHundredKms',
  hardBrakingPerMillionMiles = 'hardBrakingPerMillionMiles',
  hardBrakingPerHundredMiles = 'hardBrakingPerHundredMiles',

  hardCornering = 'hardCornering',
  hardCorneringDelta = 'hardCorneringDelta',
  hardCorneringPerMillionKms = 'hardCorneringPerMillionKms',
  hardCorneringPerHundredKms = 'hardCorneringPerHundredKms',
  hardCorneringPerMillionMiles = 'hardCorneringPerMillionMiles',
  hardCorneringPerHundredMiles = 'hardCorneringPerHundredMiles',

  distraction = 'distraction',
  distractionPerMillionKms = 'distractionPerMillionKms',
  distractionPerHundredKms = 'distractionPerHundredKms',
  distractionPerMillionMiles = 'distractionPerMillionMiles',
  distractionPerHundredMiles = 'distractionPerHundredMiles',

  phoneCalls = 'phoneCalls',
  phoneCallsPerHundredMiles = 'phoneCallsPerHundredMiles',
  phoneCallsPerMillionMiles = 'phoneCallsPerMillionMiles',
  phoneCallsPerHundredKms = 'phoneCallsPerHundredKms',
  phoneCallsPerMillionKms = 'phoneCallsPerMillionKms',

  phoneManipulations = 'phoneManipulations',
  phoneManipulationsPerHundredMiles = 'phoneManipulationsPerHundredMiles',
  phoneManipulationsPerMillionMiles = 'phoneManipulationsPerMillionMiles',
  phoneManipulationsPerHundredKms = 'phoneManipulationsPerHundredKms',
  phoneManipulationsPerMillionKms = 'phoneCallsPerMillionKms',

  speeding = 'speeding',
  speedingPerMillionKms = 'speedingPerMillionKms',
  speedingPerHundredKms = 'speedingPerHundredKms',
  speedingPerMillionMiles = 'speedingPerMillionMiles',
  speedingPerHundredMiles = 'speedingPerHundredMiles',

  hardAccelerationPerHundredMilesLastPeriod = 'hardAccelerationPerHundredMilesLastPeriod',
  hardAccelerationPerHundredMilesThisPeriod = 'hardAccelerationPerHundredMilesThisPeriod',
  hardAccelerationPerHundredKmsLastPeriod = 'hardAccelerationPerHundredKmsLastPeriod',
  hardAccelerationPerHundredKmsThisPeriod = 'hardAccelerationPerHundredKmsThisPeriod',
  hardBrakingPerHundredMilesLastPeriod = 'hardBrakingPerHundredMilesLastPeriod',
  hardBrakingPerHundredMilesThisPeriod = 'hardBrakingPerHundredMilesThisPeriod',
  hardBrakingPerHundredKmsLastPeriod = 'hardBrakingPerHundredKmsLastPeriod',
  hardBrakingPerHundredKmsThisPeriod = 'hardBrakingPerHundredKmsThisPeriod',
  hardCorneringPerHundredMilesLastPeriod = 'hardCorneringPerHundredMilesLastPeriod',
  hardCorneringPerHundredMilesThisPeriod = 'hardCorneringPerHundredMilesThisPeriod',
  hardCorneringPerHundredKmsLastPeriod = 'hardCorneringPerHundredKmsLastPeriod',
  hardCorneringPerHundredKmsThisPeriod = 'hardCorneringPerHundredKmsThisPeriod',
  distractionPerHundredMilesLastPeriod = 'distractionPerHundredMilesLastPeriod',
  distractionPerHundredMilesThisPeriod = 'distractionPerHundredMilesThisPeriod',
  distractionPerHundredKmsLastPeriod = 'distractionPerHundredKmsLastPeriod',
  distractionPerHundredKmsThisPeriod = 'distractionPerHundredKmsThisPeriod',
  speedingPerHundredMilesLastPeriod = 'speedingPerHundredMilesLastPeriod',
  speedingPerHundredMilesThisPeriod = 'speedingPerHundredMilesThisPeriod',
  speedingPerHundredKmsLastPeriod = 'speedingPerHundredKmsLastPeriod',
  speedingPerHundredKmsThisPeriod = 'speedingPerHundredKmsThisPeriod',
  seatBeltOffPerHundredMilesLastPeriod = 'seatBeltOffPerHundredMilesLastPeriod',
  seatBeltOffPerHundredMilesThisPeriod = 'seatBeltOffPerHundredMilesThisPeriod',
  seatBeltOffPerHundredKmsLastPeriod = 'seatBeltOffPerHundredKmsLastPeriod',
  seatBeltOffPerHundredKmsThisPeriod = 'seatBeltOffPerHundredKmsThisPeriod',
  reversePerHundredMilesLastPeriod = 'reversePerHundredMilesLastPeriod',
  reversePerHundredMilesThisPeriod = 'reversePerHundredMilesThisPeriod',
  reversePerHundredKmsLastPeriod = 'reversePerHundredKmsLastPeriod',
  reversePerHundredKmsThisPeriod = 'reversePerHundredKmsThisPeriod',

  hardAccelerationDeltaMiles = 'hardAccelerationDeltaMiles',
  hardAccelerationDeltaKMs = 'hardAccelerationDeltaKMs',
  hardBrakingDeltaMiles = 'hardBrakingDeltaMiles',
  hardBrakingDeltaKMs = 'hardBrakingDeltaKMs',
  hardCorneringDeltaMiles = 'hardCorneringDeltaMiles',
  hardCorneringDeltaKMs = 'hardCorneringDeltaKMs',
  distractionDeltaMiles = 'distractionDeltaMiles',
  distractionDeltaKMs = 'distractionDeltaKMs',
  speedingDeltaMiles = 'speedingDeltaMiles',
  speedingDeltaKMs = 'speedingDeltaKMs',
  seatBeltOffDeltaMiles = 'seatBeltOffDeltaMiles',
  seatBeltOffDeltaKMs = 'seatBeltOffDeltaKMs',
  reverseDeltaMiles = 'reverseDeltaMiles',
  reverseDeltaKMs = 'reverseDeltaKMs',

  distance = 'distance',
  distanceKmsDelta = 'distanceKMsDelta',

  seatBeltOff = 'seatBeltOff',
  reverse = 'reverse',
  idling = 'idling',
  mpg = 'mpg',

  overallRating = 'overallRating',

  hireDate = 'hireDate',
  status = 'status',
  privacy = 'privacy',
  policy = 'policy',
  altPrivacy = 'altPrivacy',
  pledge = 'pledge',
  altPledge = 'altPledge',
  alertSent = 'alertSent',

  //privacy report
  vrmPrivacyStatus = 'vrmPrivacyStatus',
  vrmPrivacyDateDone = 'vrmPrivacyDateDone',
  vrmPrivacyAssignedDate = 'vrmPrivacyAssignedDate',
  vrmPrivacyDueDate = 'vrmPrivacyDueDate',
  vrmPrivacyDaysToOverDue = 'vrmPrivacyDaysToOverDue',
  mentorPrivacyStatus = 'mentorPrivacyStatus',
  mentorPrivacyDateDone = 'mentorPrivacyDateDone',
  ersStatus = 'ersStatus',
  ersDateDone = 'ersDateDone',
  ersAssignedDate = 'ersAssignedDate',
  ersDueDate = 'ersDueDate',
  ersDaysToOverDue = 'ersDaysToOverDue',
  insightPrivacyStatus = 'insightPrivacyStatus',
  insightPrivacyDateDone = 'insightPrivacyDateDone',
  insightSOSPrivacyStatus = 'insightSOSPrivacyStatus',
  insightSOSPrivacyDateDone = 'insightSOSPrivacyDateDone',

  sevenDayDiffPerc = 'sevenDayDiffPerc',
  driveTimeLastSevenPercChange = 'driveTimeLastSevenPercChange',
  distanceLastSeven = 'distanceLastSeven',
  businessTrips = 'businessTrips',
  totalDistance = 'totalDistance',
  totalDriverTime = 'totalDriverTime',
  percTotalBusinessPassenger = 'percTotalBusinessPassenger',
  percWeekBusinessPassenger = 'percWeekBusinessPassenger',
  percTotalPersonal = 'percTotalPersonal',
  percWeekPersonal = 'percWeekPersonal',

  profileRating = 'profileRating',
  driverRating = 'driverRating',
  journeyRating = 'journeyRating',
  operatorRating = 'operatorRating',
  warehouseRating = 'warehouseRating',
  defensiveOperating = 'defensiveOperating',
  vehicleRating = 'vehicleRating',
  attitudeRating = 'attitudeRating',
  knowledgeRating = 'knowledgeRating',
  behaviourRating = 'behaviourRating',
  hazardRating = 'hazardRating',

  companyRiskLevel = 'companyRiskLevel',
  companyPointsScore = 'companyPointsScore',
  indexingPointsValue = 'indexingPointsValue',

  completeStatus = 'completeStatus',
  completionDate = 'completionDate',
  feedbackDate = 'feedbackDate',

  ficoDelta = 'overallScoreDelta',
  distanceDelta = 'distanceDelta',
  accelerationDelta = 'hardAccelerationDelta',
  brakingDelta = 'hardBrakingDelta',
  corneringDelta = 'hardCorneringDelta',
  speedingDelta = 'speedingDelta',
  distractionDelta = 'distractionDelta',
  seatBeltOffDelta = 'seatBeltOffDelta',
  reverseDelta = 'reverseDelta',
  mpgDelta = 'mpgDelta',

  station = 'station',
  dsp = 'dsp',
  preTrip = 'preTrip',
  postTrip = 'postTrip',
  same = 'same',

  derDsp = 'derDsp',
  driverTimeSpent = 'driverTimeSpent',

  omsStatus = 'omsStatus',
  omsSect1 = 'omsSect1',
  omsSect2 = 'omsSect2',
  omsSect3 = 'omsSect3',
  omsSect4 = 'omsSect4',
  omsSect5 = 'omsSect5',
  omsSect6 = 'omsSect6',
  omsSect7 = 'omsSect7',
  omsSect8 = 'omsSect8',

  outcome = 'outcome',
  severity = 'severity',
  driverId = 'driverId',

  riskRating = 'riskRating',
  daysToExpiry = 'daysToExpiry',
  bDaysToExpiry = 'bDaysToExpiry',
  photocardDaysToExpiry = 'photocardDaysToExpiry',
  c1DaysToExpiry = 'c1DaysToExpiry',
  c1eDaysToExpiry = 'c1eDaysToExpiry',
  cDaysToExpiry = 'cDaysToExpiry',
  ceDaysToExpiry = 'ceDaysToExpiry',
  beDaysToExpiry = 'beDaysToExpiry',
  bExpiry = 'bExpiry',
  entitlementExpire = 'entitlementExpire',
  entitlementDays = 'entitlementDays',
  licenceType = 'licenceType',
  hgvDaysToExpiry = 'hgvDaysToExpiry',
  mandateDaysToExpiry = 'mandateDaysToExpiry',
  expiryDate = 'expiryDate',
  photocardExpiry = 'photocardExpiry',
  hgvExpiry = 'hgvExpiry',
  cExpiry = 'cExpiry',
  c1Expiry = 'c1Expiry',
  c1eExpiry = 'c1eExpiry',
  ceExpiry = 'ceExpiry',
  beExpiry = 'beExpiry',
  mandateExpiry = 'mandateExpiry',
  licenceCategories = 'licenceCategories',
  daysOverdue = 'daysOverdue',
  originOfLicence = 'originOfLicence',
  countryOfIssue = 'countryOfIssue',

  daysToOverDue = 'daysToOverDue',

  trainingActivityStatus = 'trainingActivityStatus',
  driverUpToDate = 'driverUpToDate',
  mentorLogin = 'mentorLogin',
  vrmStartDate = 'vrmStartDate',

  vehicleType = 'vehicleType',
  driverInjury = 'driverInjury',
  passengerInjury = 'passengerInjury',
  injuries = 'injuries',
  immediateCause = 'immediateCause',
  roadType = 'roadType',
  roadLighting = 'roadLighting',
  weatherConditions = 'weatherConditions',
  roadConditions = 'roadConditions',
  trafficConditions = 'trafficConditions',
  crashLocation = 'crashLocation',
  directionOfTravel = 'directionOfTravel',
  driverActivity = 'driverActivity',
  journeyPurpose = 'journeyPurpose',
  timeIntoJourney = 'timeIntoJourney',
  driverDisposition = 'driverDisposition',
  category = 'category',
  reportedViaDer = 'reportedViaDer',
  schoolZone = 'schoolZone',
  cellInUse = 'cellInUse',
  seatBeltInUse = 'seatBeltInUse',
  reportedToPolice = 'reportedToPolice',
  vehicleDrivable = 'vehicleDrivable',

  createdBy = 'createdBy',
  coachingCompletions = 'coachingCompletions',
  coachingReasons = 'coachingReasons',
  coachingActions = 'coachingActions',
  licenceDocCount = 'licenceDocCount',
  licenceVerified = 'licenceVerified',

  question1 = 'question1',
  question2 = 'question2',
  question3 = 'question3',
  question4 = 'question4',
  question5 = 'question5',

  driverCount = 'driverCount',
  ratingHigh = 'ratingHigh',
  ratingHighPercent = 'ratingHighPercent',
  ratingMedium = 'ratingMedium',
  ratingMediumPercent = 'ratingMediumPercent',
  ratingLow = 'ratingLow',
  ratingLowPercent = 'ratingLowPercent',
  averageDriverScore = 'averageDriverScore',
  relativeDriverScore = 'relativeDriverScore',
  minDriverScore = 'averageDriverEvents',
  maxDriverScore = 'averageDriverEvents',
  averageDriverEvents = 'averageDriverEvents',
  relativeDriverEvents = 'relativeDriverEvents',
  totalEvents = 'totalEvents',
  totalScore = 'totalScore',
  totalCompanyPoints = 'totalCompanyPoints',
  driverIndexCoachStatus = 'driverIndexCoachStatus',
  pointsHigh = 'pointsHigh',
  pointsMedium = 'pointsMedium',
  pointsLow = 'pointsLow',
  averageCompanyPoints = 'averageCompanyPoints',
  minCompanyPoints = 'minCompanyPoints',
  maxCompanyPoints = 'maxCompanyPoints',
  // RiskExposureSummary - Defensive RoadRisk Profile
  attitude = 'attitude',
  knowledge = 'knowledge',
  behaviour = 'behaviour',
  hazardRecognition = 'hazardRecognition',
  highDefensive = 'highDefensive',
  mediumDefensive = 'mediumDefensive',
  lowDefensive = 'lowDefensive',

  // RiskExposureSummary - Driver RoadRisk Profile
  licenceNotCheckedLast12Months = 'licence_not_checked_last_12_months',
  noSafetyPolicyAwareness = 'no_safety_policy_awareness',
  safetyNotPriorityForEmployer = 'safety_not_priority_for_employer',
  notShownHowToOptimiseSeatingPosition = 'not_shown_how_to_optimise_seating_position',
  drivesBetweenMidnightAnd6am = 'drives_between_midnight_and_6am',
  unsafePersonalityWhenDriving = 'unsafe_personality_when_driving',
  under25AndOver60 = 'under_25_and_over_60',
  heldLicenceLessThan2Years = 'held_licence_less_than_2_years',
  workedForEmployerLessThanYear = 'worked_for_employer_less_than_year',
  noDefensiveDriverTrainingLast3Years = 'no_defensive_driver_training_last_3_years',
  notTrainedInUseOfAbs = 'not_trained_in_use_of_abs',
  usesPhoneWhileDriving = 'uses_phone_while_driving',
  dontKnowOrPoorSafetyCulture = 'dont_know_or_poor_safety_culture',
  highRiskDrivingStyle = 'high_risk_driving_style',
  twoOrMoreSeriousIncidents3Years = 'two_or_more_serious_incidents_3_years',
  moreThan2MinorIncidentsInLast3Years = 'more_than_2_minor_incidents_in_last_3_years',
  moreThan2SpeedingMovingViolationsInLast3Years = 'more_than_2_speeding_moving_violations_in_last_3_years',
  eyesightNotTestedInLast2Years = 'eyesight_not_tested_in_last_2_years',
  healthNotCheckedInLast2Years = 'health_not_checked_in_last_2_years',
  driverHighOrVeryHighRisk = 'driver_high_or_very_high_risk',
  operatorHighOrVeryHighRisk = 'operator_high_or_very_high_risk',
  highRiskOperatingStyle = 'high_risk_operating_style',
  skillsNotAssessedInLast3Years = 'skills_not_assessed_in_last_3_years',
  operatesBetweenMidnightAnd6am = 'operates_between_midnight_and_6am',
  unsafePersonalityWhenOperating = 'unsafe_personality_when_operating',
  noFurtherTrainingIn3Years = 'no_further_training_in_3_years',
  unawareOfPreUseInspection = 'unaware_of_pre_use_inspection',
  usesPhoneWhileOperating = 'uses_phone_while_operating',
  warnedMultipleTimesAboutOperatingPractice = 'warned_multiple_times_about_operating_practice',

  // RiskExposureSummary - Journey RoadRisk Profile
  drivingMoreThan6HoursPerDay = 'driving_more_than_6_hours_per_day',
  workingMoreThan10HoursPerDay = 'working_more_than_10_hours_per_day',
  worksMoreThan25000MilesAnnually = 'works_more_than_25000_miles_annually',
  workLessThan25000MilesAnnually = 'work_less_than_25000_miles_annually',
  drivesOnDirtRoads = 'drives_on_dirt_roads',
  towsATrailer = 'tows_a_trailer',
  drivesOutsideCountryOfResidence = 'drives_outside_country_of_residence',
  drivesMinibusPassengerVan = 'drives_minibus_passenger_van',
  unpredictableWorkingHours = 'unpredictable_working_hours',
  highRiskJourney = 'high_risk_journey',
  ridesOnDirtRoads = 'rides_on_dirt_roads',
  ridesOutsideCountryOfResidence = 'rides_outside_country_of_residence',
  towsATrailerOrSidecar = 'tows_a_trailer_or_sidecar',
  operatingMoreThan6HoursPerDay = 'operating_more_than_6_hours_per_day',
  operatesUnevenSurfaces = 'operates_uneven_surfaces',
  operatesUnfamiliarLocations = 'operates_unfamiliar_locations',
  operatesMultiplePivTypes = 'operates_multiple_piv_types',
  movesHazardousMaterials = 'moves_hazardous_materials',

  // RiskExposureSummary - Rating RoadRisk Profile
  veryHigh = 'veryHigh',
  high = 'high',
  medium = 'medium',
  low = 'low',

  // RiskExposureSummary - Vehicle RoadRisk Profile
  drivesOwnVehicleForWork = 'drives_own_vehicle_for_work',
  notServicedInLineWithManufacturer = 'not_serviced_in_line_with_manufacturer',
  vehicleMoreThan40000Miles = 'vehicle_more_than_40000_miles',
  vehicleMoreThan5YearsOld = 'vehicle_more_than_5_years_old',
  driverMinimalSafetyChecks = 'driver_minimal_safety_checks',
  driverUnawareVehicleMaintenanceReponsibility = 'driver_unaware_vehicle_maintenance_reponsibility',
  driverUnawareVehicleSafetyRatings = 'driver_unaware_vehicle_safety_ratings',
  vehicleHighRisk = 'vehicle_high_risk',
  ridesOwnVehicleForWork = 'rides_own_vehicle_for_work',
  riderMinimalSafetyChecks = 'rider_minimal_safety_checks',
  ridesMoreThan6HoursPerDay = 'rides_more_than_6_hours_per_day',
  ridesMoreThan25000MilesAnnually = 'rides_more_than_25000_miles_annually',
  riderUnawareVehicleMaintenanceReponsibility = 'rider_unaware_vehicle_maintenance_reponsibility',
  ridesBetweenMidnightAnd6am = 'rides_between_midnight_and_6am',
  notShownHowToOptimiseRidingPosition = 'not_shown_how_to_optimise_riding_position',
  unsafePersonalityWhenRiding = 'unsafe_personality_when_riding',
  noDefensiveRiderTrainingLast3Years = 'no_defensive_rider_training_last_3_years',
  notTrainedInBrakingTechniques = 'not_trained_in_braking_techniques',
  usesPhoneWhileRiding = 'uses_phone_while_riding',
  ridesLessThan25000MilesAnnually = 'rides_less_than_25000_miles_annually',
  riderHighOrVeryHighRisk = 'rider_high_or_very_high_risk',
  highRiskRidingStyle = 'high_risk_riding_style',
  pivHighRisk = 'piv_high_risk',
  operatorMinimalSafetyChecks = 'operator_minimal_safety_checks',
  operatorUnawareVehicleMaintenanceResponsibility = 'operator_unaware_vehicle_maintenance_responsibility',
  safetyRating = 'safetyRating',

  // RiskExposureSummary - All Vehicle RoadRisk Exposure Summary
  rrCounter = 'rrCounter',
  rrAttScore = 'rrAttScore',
  rrKnowScore = 'rrKnowScore',
  rrBehavScore = 'rrBehavScore',
  rrHazScore = 'rrHazScore',
  rrHigh = 'rrHigh',
  rrMedium = 'rrMedium',
  rrLow = 'rrLow',
  rrRatingVeryHigh = 'rrRatingVeryHigh',
  rrRatingHigh = 'rrRatingHigh',
  rrRatingMedium = 'rrRatingMedium',
  rrRatingLow = 'rrRatingLow',

  riskFoundationSection1 = 'riskFoundationSection1',
  riskFoundationSection2 = 'riskFoundationSection2',
  riskFoundationSection3 = 'riskFoundationSection3',
  riskFoundationSection4 = 'riskFoundationSection4',

  alternativeRiskFoundationSection1 = 'alternativeRiskFoundationSection1',
  alternativeRiskFoundationSection2 = 'alternativeRiskFoundationSection2',
  alternativeRiskFoundationSection3 = 'alternativeRiskFoundationSection3',
  alternativeRiskFoundationSection4 = 'alternativeRiskFoundationSection4',

  cardStatus = 'cardStatus',
  mvrLicenseStatusDescription = 'mvrLicenseStatusDescription',
  electronicConsentAssigned = 'electronicConsentAssigned',
  requiresLicenceCheck = 'requiresLicenceCheck',
  driveWork = 'driveWork',
  oneToOneStatus = 'oneToOneStatus',
  shortTrip = 'shortTrip',
  mentorAppAccess = 'mentorAppAccess',
  mentorInsightAppAccess = 'mentorInsightAppAccess',
  previousMonthDistanceTravelledReported = 'previousMonthDistanceTravelledReported',

  // Video Courses report columns
  VID_MB = 'VID_MB',
  VID_MBdaysToOverDue = 'VID_MBdaysToOverDue',
  VID_JTI_LAUNCH = 'VID_JTI_LAUNCH',
  VID_JTI_LAUNCHdaysToOverDue = 'VID_JTI_LAUNCHdaysToOverDue',
  VID_SPOUSE = 'VID_SPOUSE',
  VID_SPOUSEdaysToOverDue = 'VID_SPOUSEdaysToOverDue',
  VID_NESTLE_FLT = 'VID_NESTLE_FLT',
  VID_NESTLE_FLTdaysToOverDue = 'VID_NESTLE_FLTdaysToOverDue',
  VID_NESTLE_PALLET = 'VID_NESTLE_PALLET',
  VID_NESTLE_PALLETdaysToOverDue = 'VID_NESTLE_PALLETdaysToOverDue',
  VID_NESTLE_3METER = 'VID_NESTLE_3METER',
  VID_NESTLE_3METERdaysToOverDue = 'VID_NESTLE_3METERdaysToOverDue',
  VID_ROCHE_LAUNCH = 'VID_ROCHE_LAUNCH',
  VID_ROCHE_LAUNCHdaysToOverDue = 'VID_ROCHE_LAUNCHdaysToOverDue',
  VID_RNA = 'VID_RNA',
  VID_RNAdaysToOverDue = 'VID_RNAdaysToOverDue',
  VID_NESTLEUK_LAUNCH = 'VID_NESTLEUK_LAUNCH',
  VID_NESTLEUK_LAUNCHdaysToOverDue = 'VID_NESTLEUK_LAUNCHdaysToOverDue',
  VID_ECOLAB1 = 'VID_ECOLAB1',
  VID_ECOLAB1daysToOverDue = 'VID_ECOLAB1daysToOverDue',
  VID_ECOLAB2 = 'VID_ECOLAB2',
  VID_ECOLAB2daysToOverDue = 'VID_ECOLAB2daysToOverDue',
  VID_LEASEPLAN_FUJI = 'VID_LEASEPLAN_FUJI',
  VID_LEASEPLAN_FUJIdaysToOverDue = 'VID_LEASEPLAN_FUJIdaysToOverDue',
  distanceKms = 'distanceKms',
  overallScoreDelta = 'overallScoreDelta',
  duration = 'duration',
  openCoachingInPopup = 'openCoachingInPopup',
  workHoursSet = 'workHoursSet',
  exportDERSubmission = 'exportDERSubmission',
  exportIDDReport = 'exportIDDReport',
  claimStatus = 'claimStatus',
  questionDamageDefectOnOtherVehicle = 'question_damage_defect_on_other_vehicle',
  totalVins = 'totalVins',
  totalReports = 'totalReports',
  totalCompleted = 'totalCompleted',
  totalAwaitingReview = 'totalAwaitingReview',
  totalReportStarted = 'totalReportStarted',
  totalCollisions = 'totalCollisions',
  total3rdPartyDamages = 'total3rdPartyDamages',
  total3rdPartyInjuries = 'total3rdPartyInjuries',
  totalOtherBodyDamage = 'totalOtherBodyDamage',
  totalVehicleNotSafeToDrive = 'totalVehicleNotSafeToDrive',
  totalElectrical = 'totalElectrical',
  totalMechanical = 'totalMechanical',
  averageTimeToDspReview = 'averageTimeToDspReview',
  averageTimeToCreateDer = 'averageTimeToCreateDer',

  partName = 'partName',
  partSide = 'partSide',
  damagesDetailGrid = 'damagesDetailGrid',
  vehicleManufacturer = 'vehicleManufacturer',
  vehicleModel = 'vehicleModel',
  dspReviewDate = 'dspReviewDate',

  ecoDriveRating = 'ecoDriveRating',
  co2Savings = 'co2Savings',
  ecoComparison = 'ecoComparison',

  totalTripMiles = 'totalTripMiles',
  totalTripKilometers = 'totalTripKilometers',
  totalMovementTripHours = 'totalMovementTripHours',

  coursesAssigned = 'coursesAssigned',
  coursesCompleted = 'coursesCompleted',
  coursesIncomplete = 'coursesIncomplete',
  coursesNotCompleted = 'coursesNotCompleted',
  totalTripCount = 'totalTripCount',
  distanceLastPeriod = 'distanceLastPeriod',
  distanceKmsLastPeriod = 'distanceKmsLastPeriod',
  mpgLastPeriod = 'mpgLastPeriod',
  mpgThisPeriod = 'mpgThisPeriod',
}

enum PercentageColEnums {
  collisionPercIndexScore = 'collision_perc_index_score',
  companyPercIndexScore = 'company_perc_index_score',
  incidentPercIndexScore = 'incident_perc_index_score',
  licencePercIndexScore = 'licence_perc_index_score',
  vrmPercIndexScore = 'vrm_perc_index_score',
}

enum ColumnsWithNAValue {
  totalHoursSet = 'totalHoursSet',
  totalDriverTrips = 'totalDriverTrips',
  totalTripHours = 'totalTripHours',
  totalTruncatedTrips = 'totalTruncatedTrips',
  completedBy = 'completedBy',
  completedDate = 'completedDate',
  completionMonthYear = 'completionMonthYear',
  coachingStatus = 'coachingStatus',
  coachingReason = 'coachingReason',
  onTime = 'onTime',
  q1 = 'q1',
  q2 = 'q2',
  q3 = 'q3',
  q4 = 'q4',
  q5 = 'q5',
  q6 = 'q6',
  q7 = 'q7',
  manualShiftStart = 'manualShiftStart',
  driverUsingAutoShift = 'driverUsingAutoShift',
  datedone = 'datedone',
}

export enum ReportSideNavContentEnum {
  alerts = 'alerts',
}

export type FilterPeriod = 'day' | 'week' | 'month' | 'year';
export type TimeFrame = 'Yearly' | 'Monthly' | 'Weekly' | 'Daily';

/**
 * @date format: YYYY-MM-DD
 */
interface PeriodFilter {
  period: FilterPeriod;
  date: string;
}

export interface PeriodFrequency {
  label: TimeFrame;
  value: FilterPeriod;
}

export enum TimeFrameValueEnums {
  year = 'year',
  month = 'month',
  week = 'week',
  day = 'day',
}

export enum ReportStaticValues {
  good = 'good',
  great = 'great',
  complete = 'complete',
  valid = 'valid',
  active = 'active',
  agree = 'agree',
  yes = 'yes',
  optional = 'optional',
  low = 'low',
  lowRisk = 'low risk',
  resolved = 'resolved',
  veryLowRisk = 'very low risk',
  view = 'view',
  notRequired = 'not required',
  found = 'found',
  average = 'average',
  partial = 'partial',
  incomplete = 'incomplete',
  hold = 'hold',
  seeMvr = 'see mvr',
  seeLicenceCheck = 'see licence check',
  medium = 'medium',
  mediumRisk = 'medium risk',
  poor = 'poor',
  disagree = 'disagree',
  no = 'no',
  notValid = 'not valid',
  invalid = 'invalid',
  notStarted = 'not started',
  unresolved = 'unresolved',
  high = 'high',
  highRisk = 'high risk',
  veryHighRisk = 'very high risk',
  overdue = 'overdue',
  notVerified = 'not verified',
  required = 'required',
  requiresAttention = 'requires attention',
  noCheck = 'no check',
  risky = 'risky',
  inactive = 'inactive',
  testAccount = 'test account',
  inProgress = 'in progress',
  awaitingReview = 'awaiting review',
  completed = 'completed',
  reportStarted = 'report started',
  notAvailable = 'notAvailable',
  notAssigned = 'not assigned',
  noCoursesAssigned = 'no courses assigned',
}

interface SelectedTimeFrame {
  frequency?: PeriodFrequency;
  timeFrame?: PeriodFilter;
}

interface SpecificColWidthSetting {
  columnId: string;
  width?: number;
  flex?: number;
  prioritize?: boolean;
  applicableReports?: Array<string>;
}

interface AgGridConfigInternal {
  common: GridOptions;
  excelStyles: Array<any>;
  statusPanels: Array<any>;
  defaultColumnConfig: any;
  rowClassRules: any;
  specificColWidth: Array<SpecificColWidthSetting>;
}

export interface EndpointFilterValue {
  filterName?: string;
  value: string;
  text?: string;
  translatedText?: string;
  selected?: boolean;
  serverFilters?: { filterGroups: any; selectedServerFilters: any };
}

export interface EndpointFilters {
  default: string | null;
  name: string;
  translatedName?: string;
  values: Array<EndpointFilterValue>;
}

export interface SingleServerFilter {
  key: string;
  options: Array<ValueTextObject>;
  fromUserProfile?: boolean;
  userProfileKey?: string;
  excludeKey?: string;
}

export interface NavigationFilter {
  name: string;
  text: string;
  options: [
    {
      value: string;
      text: string;
      selected?: string;
    }
  ];
  selected: ValueTextObject;
  currentState?: ValueTextObject;
}

export interface DocumentGeneratorData {
  endpoint: string;
  extension: string;
  sendLanguage: boolean;
  language: string;
  generationDate: string;
  showButton: boolean;
}

export interface ReportPermissions {
  permDownloadReport: boolean;
}

export interface ColumnGroupLabel {
  id: string;
  label: string;
}

export interface ReportMeta {
  dataUri?: string;
  metaUri?: string;
  endpoint?: string;
  cellRenderer?: {
    name: string;
    config: { [key: string]: any };
  };
  usesServerSideRendering?: boolean;
  defaultPeriod?: PeriodFilter;
  availablePeriods?: [string];
  moreDetails?: { type?: string; hideRowDetails?: boolean };
  endpointFilters?: Array<EndpointFilters>;
  name?: string;
  structure?: any;
  detailStructure?: any;
  pagination?: SsrPagination;
  information?: ReportInformation;
  operations?: any;
  serverFilters?: RawServerFilterConfig;
  singleServerFilter?: SingleServerFilter;
  navigationFilters?: [NavigationFilter];
  documentGeneratorData?: DocumentGeneratorData;
  reportPermissions?: ReportPermissions;
}

export interface NewCommonReportTriggerParams {
  reportMeta: ReportMeta;
  autoTriggerReportDownload?: boolean;
}

export interface NewCommonReportTriggerParams {
  reportMeta: ReportMeta;
  autoTriggerReportDownload?: boolean;
}

export interface VrmColumnStatesForAgGrid {
  reportName: string;
  columnState: any;
}

export interface ColumnSortModel {
  colId: string;
  sort: 'asc' | 'desc';
}

export interface TransformedSingleData {
  uiValue: string;
  uiColName: string;
  color?: string;
}

@Injectable({ providedIn: 'root' })
export class ReportsService {
  showReportSideNav: boolean = false;
  reportSideNavTitle: string = '';
  activeReportSideNavContent: string = '';

  resolveAlertAdditionalContent: string | null;
  resolveAlertDisplayAuthorisation = false;
  resolveAlertButtonText: string = this.cService.readyTranslations.generic.update;
  resolveAlertFailedSuggestions: Array<any> = [];
  resolveAlertShowActionTaken: boolean = false;
  resolveAlertHasError: boolean = false;

  loginLogoutNotificationsCount: string = '0';

  cachedAgGridServerParams: IServerSideGetRowsRequest = null;
  cachedSsrSearchText: string = '';
  dataStructure: {};
  persistedStateChanged: boolean = false;
  ssrTotalRowsData: SsrTotalRows = {};
  artificialSsrDataFeed: Array<any> = null;
  gridStateDifferentFromDefault: boolean = false;
  selectedRows: Array<any> = [];
  currentReportData: Array<any> = [];
  currentReportMeta: Partial<ReportMeta> = {};
  metadataHasDefaultFilter: boolean = false;
  private _selectedRowsSubject = new BehaviorSubject(null);
  newlySelectedRowAdded$ = this._selectedRowsSubject.asObservable();
  formDataUpdated: boolean = false;
  commonRptPageChangedOnLoad: boolean = false;

  reportSideNavContents = {
    alerts: ReportSideNavContentEnum.alerts,
  };

  private _commonReportDataChangeSubjectOnLoad = new BehaviorSubject(null);
  commonReportDataChangedOnLoad$ = this._commonReportDataChangeSubjectOnLoad.asObservable();

  private _commonReportDataChangeSubject = new BehaviorSubject(null);
  commonReportDataChanged$ = this._commonReportDataChangeSubject.asObservable();

  private _commonReportMetaChangeSubject = new BehaviorSubject(null);
  commonReportMetaChanged$ = this._commonReportMetaChangeSubject.asObservable();

  private _serverFilterChanged = new BehaviorSubject(false);
  serverFilterChanged$ = this._serverFilterChanged.asObservable();

  private _reportDataChanged = new BehaviorSubject(null);
  reportDataChanged$ = this._reportDataChanged.asObservable();

  private _driverPerformanceDialogClosedSubject = new BehaviorSubject(null);
  driverPerformanceDialogClosed$ = this._driverPerformanceDialogClosedSubject.asObservable();

  private _misNotificationsChangedSubject = new BehaviorSubject(null);
  misNotificationsChanged$ = this._misNotificationsChangedSubject.asObservable();

  private _rowsDeleted = new BehaviorSubject(null);
  rowsDeleted$ = this._rowsDeleted.asObservable();

  private _rowAdded = new BehaviorSubject(null);
  rowAdded$ = this._rowAdded.asObservable();

  private _rowUpdated = new BehaviorSubject(null);
  rowUpdated$ = this._rowUpdated.asObservable();

  settings = {
    selectAllLimit: 100,
  };
  hasTimeFrameFilter: boolean = false;
  hasStationOrDspFilter: boolean = false;
  singleEndpointFilters: EndpointFilters;
  multipleEndpointFilters: Array<EndpointFilters> = [];

  currentReportOptions: ReportOption;
  reportEndpoint = {
    roadriskProfileInsight: 'roadrisk/profile_insights',
  };
  shouldDisplayMassCoachingActions = false;
  commonReportOptions = {
    mobileColumns: ['name', 'pin'],
  };
  commonReportDrilldownEndpoints: any;
  commonReportSubscriptions = {
    report: null,
    meta: null,
  };
  adminRowOperations: any;
  agGridConfig: AgGridConfigInternal = {
    common: {
      rowDragManaged: true,
      groupIncludeFooter: true,
      animateRows: true,
      multiSortKey: 'ctrl',
      headerHeight: 35, // <-- default . See setColumnHeaderHeight() in report-component
      rowHeight: 33,
      enableRangeSelection: true,
      enableCharts: true,
      localeText: null,
      autoSizePadding: 30, // <-- allows space for the sort icon
      copyHeadersToClipboard: false,
      enableRtl: false, // <-- This is set in the constructor when rtl is in use

      aggFuncs: {
        Sum: (params) => {
          let sum: number = 0;

          params.values.forEach((value) => {
            if (typeof value === 'number') {
              sum += value;
            }
          });

          return Number(sum.toFixed(2));
        },

        HourSum: (params) => {
          let hour: number,
            min: number = 0,
            totalMin: number = 0;

          params.values.forEach((value) => {
            if (value && typeof value === 'string') {
              const parts = value.split(':');

              hour += parseInt(parts[0]);
              totalMin += parseInt(parts[1]);
            }
          });

          hour += Math.floor(totalMin / 60);
          min = totalMin % 60;

          return hour.toString().padStart(2, '0') + min.toString().padStart(2, '0');
        },
      },
    },
    statusPanels: [
      {
        statusPanel: 'agTotalRowCountComponent',
        align: 'left',
      },
      {
        align: 'left',
        statusPanel: 'statusBarRecommendationActionsComponent',
      },
      { statusPanel: 'agFilteredRowCountComponent' },
      { statusPanel: 'agSelectedRowCountComponent' },
    ],

    defaultColumnConfig: {
      sortable: true,
      resizable: true,
      enableRowGroup: true,
    },

    excelStyles: [
      {
        id: 'agGridForceNumberToString',
        dataType: 'string',
      },
      {
        id: 'agGridBlueTexts',
        font: {
          color: '#0000ff',
          bold: true,
        },
      },
      {
        id: 'agGridLimeGreenTexts',
        font: {
          color: '#32cd32',
          bold: true,
        },
      },
      {
        id: 'agGridGreenTexts',
        font: {
          color: '#32a852',
          bold: true,
        },
      },
      {
        id: 'agGridAmberTexts',
        font: {
          color: '#ff9900',
          bold: true,
        },
      },
      {
        id: 'agGridRedTexts',
        font: {
          color: '#ff0000',
          bold: true,
        },
      },
      {
        id: 'agGridBlackTexts',
        font: {
          color: '#000000',
          bold: true,
        },
      },
      {
        id: 'agGridGreyTexts',
        font: {
          color: '#808080',
          bold: true,
        },
      },
      {
        id: 'agGridShadedBackground',
        interior: {
          color: '#f5f7f7',
          pattern: 'solid',
        },
      },
      {
        id: 'agGridBenchmarkBackground',
        interior: {
          color: '#efefef',
          pattern: 'solid',
        },
      },
      ...DamageTypeExcelStyles,
    ],

    rowClassRules: {
      agGridBenchmarkBackground: (params: any) => {
        let benchmark: boolean = false;

        if (params.data && params.data.dsp) {
          benchmark = params.data.dsp.toLowerCase() === 'benchmark';
        } else if (params.data && params.data.location) {
          benchmark = params.data.location.toLowerCase() === 'benchmark';
        } else if (params.data && params.data.firstName) {
          benchmark = ['benchmark', 'total'].includes(params.data.firstName.toLowerCase());
        }

        return benchmark;
      },

      agGridHighlightBackground: (params: any) => {
        let allStations: boolean = false;

        if (params.data && params.data.station) {
          allStations = params.data.station.toLowerCase() === 'all stations';
        }

        return allStations;
      },
    },

    specificColWidth: [
      { columnId: ColIdEnum.pin, width: 110, prioritize: true },
      { columnId: ColIdEnum.email, width: 125, applicableReports: [ReportNameEnum.scorecard] },

      { columnId: ColIdEnum.indexingViolationsActive, width: 80, prioritize: true },
      { columnId: ColIdEnum.indexingViolations, width: 80, prioritize: true },
      { columnId: ColIdEnum.indexingIndexValue, width: 116, prioritize: true },
      { columnId: ColIdEnum.indexingCoachCount, width: 115, prioritize: true },
      { columnId: ColIdEnum.partialIndexRating, width: 80, prioritize: true },
      { columnId: ColIdEnum.courseProgress, width: 90, prioritize: true },
      { columnId: ColIdEnum.courseStatus, width: 90, prioritize: true },
      { columnId: ColIdEnum.indexingCoach, width: 119, prioritize: true },
      { columnId: ColIdEnum.indexingLicenceStatus, width: 90, prioritize: true },
      { columnId: ColIdEnum.dvlaLicenceStatus, width: 140, prioritize: true },
      { columnId: ColIdEnum.companyRiskLevel, width: 108, prioritize: true },
      { columnId: ColIdEnum.companyPointsScore, width: 108, prioritize: true },
      { columnId: ColIdEnum.indexingPointsValue, width: 108, prioritize: true },

      { columnId: ColIdEnum.indexingDiffLastMonth, width: 84, prioritize: true },
      { columnId: ColIdEnum.indexingDiffLast3Month, width: 86, prioritize: true },
      { columnId: ColIdEnum.indexingDiffLast6Month, width: 86, prioritize: true },
      { columnId: ColIdEnum.indexingDiffLast12Month, width: 86, prioritize: true },
      { columnId: ColIdEnum.alertResolvedStatus, width: 130, prioritize: true },

      { columnId: ColIdEnum.totalVins, width: 120, prioritize: true },
      { columnId: ColIdEnum.totalReports, width: 140, prioritize: true },
      { columnId: ColIdEnum.totalCompleted, width: 160, prioritize: true },
      { columnId: ColIdEnum.totalAwaitingReview, width: 200, prioritize: true },
      { columnId: ColIdEnum.totalReportStarted, width: 200, prioritize: true },
      { columnId: ColIdEnum.totalCollisions, width: 140, prioritize: true },
      { columnId: ColIdEnum.total3rdPartyDamages, width: 140, prioritize: true },
      { columnId: ColIdEnum.total3rdPartyInjuries, width: 140, prioritize: true },
      { columnId: ColIdEnum.totalOtherBodyDamage, width: 160, prioritize: true },
      { columnId: ColIdEnum.totalVehicleNotSafeToDrive, width: 160, prioritize: true },
      { columnId: ColIdEnum.totalElectrical, width: 140, prioritize: true },
      { columnId: ColIdEnum.totalMechanical, width: 150, prioritize: true },

      {
        columnId: ColIdEnum.hardAccelerationPerHundredMilesLastPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.hardAccelerationPerHundredMilesThisPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.hardAccelerationPerHundredKmsLastPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.hardAccelerationPerHundredKmsThisPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.hardBrakingPerHundredMilesLastPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.hardBrakingPerHundredMilesThisPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.hardBrakingPerHundredKmsLastPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.hardBrakingPerHundredKmsThisPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.hardCorneringPerHundredMilesLastPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.hardCorneringPerHundredMilesThisPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.hardCorneringPerHundredKmsLastPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.hardCorneringPerHundredKmsThisPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.distractionPerHundredMilesLastPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.distractionPerHundredMilesThisPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.distractionPerHundredKmsLastPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.distractionPerHundredKmsThisPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.speedingPerHundredMilesLastPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.speedingPerHundredMilesThisPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.speedingPerHundredKmsLastPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.speedingPerHundredKmsThisPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.seatBeltOffPerHundredMilesLastPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.seatBeltOffPerHundredMilesThisPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.seatBeltOffPerHundredKmsLastPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.seatBeltOffPerHundredKmsThisPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.reversePerHundredMilesLastPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.reversePerHundredMilesThisPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.reversePerHundredKmsLastPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.reversePerHundredKmsThisPeriod,
        width: 200,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },

      {
        columnId: ColIdEnum.hardAccelerationDeltaMiles,
        width: 160,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.hardAccelerationDeltaKMs,
        width: 160,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.hardBrakingDeltaMiles,
        width: 150,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.hardBrakingDeltaKMs,
        width: 150,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.hardCorneringDeltaMiles,
        width: 150,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.hardCorneringDeltaKMs,
        width: 150,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.distractionDeltaMiles,
        width: 160,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.distractionDeltaKMs,
        width: 160,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.speedingDeltaMiles,
        width: 150,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.speedingDeltaKMs,
        width: 150,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.seatBeltOffDeltaMiles,
        width: 150,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.seatBeltOffDeltaKMs,
        width: 150,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.reverseDeltaMiles,
        width: 150,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },
      {
        columnId: ColIdEnum.reverseDeltaKMs,
        width: 150,
        prioritize: true,
        applicableReports: [ReportNameEnum.driverReportCompare],
      },

      { columnId: ColIdEnum.licenceCategories, width: 400, prioritize: true },

      { columnId: ColIdEnum.hireDate, width: 87, prioritize: true },
      { columnId: ColIdEnum.status, width: 80 },
      { columnId: ColIdEnum.vrmStatus, width: 80 },
      { columnId: ColIdEnum.isActive, width: 80 },
      { columnId: ColIdEnum.completionDate, width: 100 },
      { columnId: ColIdEnum.feedbackDate, width: 100 },
      { columnId: ColIdEnum.mvrRating, width: 100 },
      { columnId: ColIdEnum.mvrLicenseClass, width: 100 },
      { columnId: ColIdEnum.orderType, width: 100 },
      { columnId: ColIdEnum.orderStatus, width: 100 },
      { columnId: ColIdEnum.mvrError, width: 100 },
      { columnId: ColIdEnum.mvrWarning, width: 100 },
      { columnId: ColIdEnum.mvrCompanyRating, width: 100 },
      { columnId: ColIdEnum.consentStatus, width: 100 },
      { columnId: ColIdEnum.requireDocs, width: 100 },
      { columnId: ColIdEnum.licenceState, width: 100 },
      { columnId: ColIdEnum.allowMvr, width: 100 },
      { columnId: ColIdEnum.pledge, width: 100 },
      { columnId: ColIdEnum.altPledge, width: 100 },
      { columnId: ColIdEnum.mentorConsent, width: 100 },
      { columnId: ColIdEnum.overallRating, width: 100 },

      { columnId: ColIdEnum.sevenDayDiffPerc, width: 80 },
      { columnId: ColIdEnum.driveTimeLastSevenPercChange, width: 100 },
      { columnId: ColIdEnum.distanceLastSeven, width: 100 },
      { columnId: ColIdEnum.businessTrips, width: 80 },
      { columnId: ColIdEnum.totalDistance, width: 80 },
      { columnId: ColIdEnum.totalDriverTime, width: 80 },
      { columnId: ColIdEnum.percTotalBusinessPassenger, width: 80 },
      { columnId: ColIdEnum.percWeekBusinessPassenger, width: 80 },
      { columnId: ColIdEnum.percTotalPersonal, width: 80 },
      { columnId: ColIdEnum.percWeekPersonal, width: 80 },
      { columnId: ColIdEnum.accelerationRating, width: 160 },

      { columnId: ColIdEnum.hardAcceleration, width: 160 },
      { columnId: ColIdEnum.hardAccelerationDelta, width: 180 },
      { columnId: ColIdEnum.hardAccelerationPerMillionKms, width: 160 },
      { columnId: ColIdEnum.hardAccelerationPerHundredKms, width: 160 },
      { columnId: ColIdEnum.hardAccelerationPerMillionMiles, width: 160 },
      { columnId: ColIdEnum.hardAccelerationPerHundredMiles, width: 140 },

      { columnId: ColIdEnum.hardBraking, width: 160 },
      { columnId: ColIdEnum.hardBrakingDelta, width: 160 },
      { columnId: ColIdEnum.hardBrakingPerMillionKms, width: 160 },
      { columnId: ColIdEnum.hardBrakingPerHundredKms, width: 160 },
      { columnId: ColIdEnum.hardBrakingPerMillionMiles, width: 160 },
      { columnId: ColIdEnum.hardBrakingPerHundredMiles, width: 160 },

      { columnId: ColIdEnum.hardCornering, width: 160 },
      { columnId: ColIdEnum.hardCorneringDelta, width: 160 },
      { columnId: ColIdEnum.hardCorneringPerMillionKms, width: 160 },
      { columnId: ColIdEnum.hardCorneringPerHundredKms, width: 160 },
      { columnId: ColIdEnum.hardCorneringPerMillionMiles, width: 160 },
      { columnId: ColIdEnum.hardCorneringPerHundredMiles, width: 160 },

      { columnId: ColIdEnum.distraction, width: 160 },
      { columnId: ColIdEnum.distractionPerMillionKms, width: 160 },
      { columnId: ColIdEnum.distractionPerHundredKms, width: 160 },
      { columnId: ColIdEnum.distractionPerMillionMiles, width: 160 },
      { columnId: ColIdEnum.distractionPerHundredMiles, width: 160 },

      { columnId: ColIdEnum.phoneCalls, width: 160 },
      { columnId: ColIdEnum.phoneCallsPerHundredMiles, width: 160 },
      { columnId: ColIdEnum.phoneCallsPerMillionMiles, width: 160 },
      { columnId: ColIdEnum.phoneCallsPerHundredKms, width: 160 },
      { columnId: ColIdEnum.phoneCallsPerMillionKms, width: 160 },

      { columnId: ColIdEnum.phoneManipulations, width: 190 },
      { columnId: ColIdEnum.phoneManipulationsPerHundredMiles, width: 190 },
      { columnId: ColIdEnum.phoneManipulationsPerMillionMiles, width: 190 },
      { columnId: ColIdEnum.phoneManipulationsPerHundredKms, width: 190 },
      { columnId: ColIdEnum.phoneManipulationsPerMillionKms, width: 190 },

      { columnId: ColIdEnum.speeding, width: 160 },
      { columnId: ColIdEnum.speedingDelta, width: 160 },
      { columnId: ColIdEnum.speedingPerMillionKms, width: 160 },
      { columnId: ColIdEnum.speedingPerHundredKms, width: 160 },
      { columnId: ColIdEnum.speedingPerMillionMiles, width: 160 },
      { columnId: ColIdEnum.speedingPerHundredMiles, width: 160 },

      { columnId: ColIdEnum.braking, width: 160 },
      { columnId: ColIdEnum.distance, width: 160 },
      { columnId: ColIdEnum.distanceDelta, width: 160 },
      { columnId: ColIdEnum.distanceKmsDelta, width: 160 },
      { columnId: ColIdEnum.distraction, width: 160 },
      { columnId: ColIdEnum.distractionDelta, width: 160 },
      { columnId: ColIdEnum.engineOff, width: 160 },
      { columnId: ColIdEnum.brakingRating, width: 140 },
      { columnId: ColIdEnum.corneringRating, width: 140 },
      { columnId: ColIdEnum.distractionRating, width: 160 },
      { columnId: ColIdEnum.speedRating, width: 140 },
      { columnId: ColIdEnum.seatBeltOff, width: 160 },
      { columnId: ColIdEnum.reverse, width: 160 },
      { columnId: ColIdEnum.idling, width: 160 },
      { columnId: ColIdEnum.mpg, width: 160 },
      { columnId: ColIdEnum.mpgDelta, width: 160 },
      { columnId: ColIdEnum.profileRating, width: 120 },
      { columnId: ColIdEnum.driverRating, width: 120 },
      { columnId: ColIdEnum.journeyRating, width: 120 },
      { columnId: ColIdEnum.operatorRating, width: 120 },
      { columnId: ColIdEnum.warehouseRating, width: 120 },
      { columnId: ColIdEnum.defensiveOperating, width: 120 },
      { columnId: ColIdEnum.vehicleRating, width: 120 },
      { columnId: ColIdEnum.driverId, width: 100 },
      { columnId: ColIdEnum.mentorLogin, width: 160 },
      { columnId: ColIdEnum.vrmStartDate, width: 160 },

      { columnId: ColIdEnum.coachingCompletions, width: 140 },
      { columnId: ColIdEnum.coachingReasons, width: 260 },
      { columnId: ColIdEnum.coachingActions, width: 180 },

      { columnId: ColIdEnum.location, width: 160 },
      { columnId: ColIdEnum.driverCount, width: 120 },
      { columnId: ColIdEnum.ratingHigh, width: 120 },
      { columnId: ColIdEnum.ratingMedium, width: 135 },
      { columnId: ColIdEnum.ratingLow, width: 120 },
      { columnId: ColIdEnum.averageDriverScore, width: 130 },
      { columnId: ColIdEnum.minDriverScore, width: 110 },
      { columnId: ColIdEnum.maxDriverScore, width: 110 },
      { columnId: ColIdEnum.averageDriverEvents, width: 120 },
      { columnId: ColIdEnum.totalEvents, width: 125 },
      { columnId: ColIdEnum.totalScore, width: 120 },
      { columnId: ColIdEnum.pointsHigh, width: 140 },
      { columnId: ColIdEnum.pointsMedium, width: 155 },
      { columnId: ColIdEnum.pointsLow, width: 140 },
      { columnId: ColIdEnum.averageCompanyPoints, width: 140 },
      { columnId: ColIdEnum.minCompanyPoints, width: 140 },
      { columnId: ColIdEnum.maxCompanyPoints, width: 140 },
      { columnId: ColIdEnum.totalCompanyPoints, width: 129 },
      // Risk Exposure Summary
      { columnId: ColIdEnum.attitude, width: 200 },
      { columnId: ColIdEnum.knowledge, width: 200 },
      { columnId: ColIdEnum.behaviour, width: 200 },
      { columnId: ColIdEnum.hazardRecognition, width: 200 },
      { columnId: ColIdEnum.highDefensive, width: 200 },
      { columnId: ColIdEnum.mediumDefensive, width: 200 },
      { columnId: ColIdEnum.lowDefensive, width: 200 },
      { columnId: ColIdEnum.licenceNotCheckedLast12Months, width: 200 },
      { columnId: ColIdEnum.noSafetyPolicyAwareness, width: 200 },
      { columnId: ColIdEnum.safetyNotPriorityForEmployer, width: 200 },
      { columnId: ColIdEnum.notShownHowToOptimiseSeatingPosition, width: 200 },
      { columnId: ColIdEnum.drivesBetweenMidnightAnd6am, width: 200 },
      { columnId: ColIdEnum.unsafePersonalityWhenDriving, width: 200 },
      { columnId: ColIdEnum.under25AndOver60, width: 200 },
      { columnId: ColIdEnum.heldLicenceLessThan2Years, width: 200 },
      { columnId: ColIdEnum.workedForEmployerLessThanYear, width: 200 },
      { columnId: ColIdEnum.noDefensiveDriverTrainingLast3Years, width: 200 },
      { columnId: ColIdEnum.notTrainedInUseOfAbs, width: 200 },
      { columnId: ColIdEnum.usesPhoneWhileDriving, width: 230 },
      { columnId: ColIdEnum.dontKnowOrPoorSafetyCulture, width: 200 },
      { columnId: ColIdEnum.highRiskDrivingStyle, width: 200 },
      { columnId: ColIdEnum.twoOrMoreSeriousIncidents3Years, width: 250 },
      { columnId: ColIdEnum.moreThan2MinorIncidentsInLast3Years, width: 220 },
      { columnId: ColIdEnum.moreThan2SpeedingMovingViolationsInLast3Years, width: 230 },
      { columnId: ColIdEnum.eyesightNotTestedInLast2Years, width: 200 },
      { columnId: ColIdEnum.healthNotCheckedInLast2Years, width: 200 },
      { columnId: ColIdEnum.driverHighOrVeryHighRisk, width: 200 },
      { columnId: ColIdEnum.drivingMoreThan6HoursPerDay, width: 200 },
      { columnId: ColIdEnum.workingMoreThan10HoursPerDay, width: 200 },
      { columnId: ColIdEnum.worksMoreThan25000MilesAnnually, width: 250 },
      { columnId: ColIdEnum.workLessThan25000MilesAnnually, width: 250 },
      { columnId: ColIdEnum.drivesOnDirtRoads, width: 200 },
      { columnId: ColIdEnum.towsATrailer, width: 200 },
      { columnId: ColIdEnum.drivesOutsideCountryOfResidence, width: 200 },
      { columnId: ColIdEnum.drivesMinibusPassengerVan, width: 200 },
      { columnId: ColIdEnum.unpredictableWorkingHours, width: 200 },
      { columnId: ColIdEnum.highRiskJourney, width: 200 },
      { columnId: ColIdEnum.veryHigh, width: 220 },
      { columnId: ColIdEnum.high, width: 220 },
      { columnId: ColIdEnum.medium, width: 220 },
      { columnId: ColIdEnum.low, width: 220 },
      { columnId: ColIdEnum.drivesOwnVehicleForWork, width: 200 },
      { columnId: ColIdEnum.notServicedInLineWithManufacturer, width: 200 },
      { columnId: ColIdEnum.vehicleMoreThan40000Miles, width: 200 },
      { columnId: ColIdEnum.vehicleMoreThan5YearsOld, width: 200 },
      { columnId: ColIdEnum.driverMinimalSafetyChecks, width: 200 },
      { columnId: ColIdEnum.driverUnawareVehicleMaintenanceReponsibility, width: 200 },
      { columnId: ColIdEnum.driverUnawareVehicleSafetyRatings, width: 200 },
      { columnId: ColIdEnum.vehicleHighRisk, width: 200 },
      { columnId: ColIdEnum.ridesOwnVehicleForWork, width: 200 },
      { columnId: ColIdEnum.riderMinimalSafetyChecks, width: 200 },
      { columnId: ColIdEnum.ridesMoreThan6HoursPerDay, width: 200 },
      { columnId: ColIdEnum.ridesMoreThan25000MilesAnnually, width: 200 },
      { columnId: ColIdEnum.riderUnawareVehicleMaintenanceReponsibility, width: 200 },
      { columnId: ColIdEnum.ridesOnDirtRoads, width: 200 },
      { columnId: ColIdEnum.ridesOutsideCountryOfResidence, width: 200 },
      { columnId: ColIdEnum.towsATrailerOrSidecar, width: 200 },
      { columnId: ColIdEnum.ridesBetweenMidnightAnd6am, width: 200 },
      { columnId: ColIdEnum.notShownHowToOptimiseRidingPosition, width: 200 },
      { columnId: ColIdEnum.unsafePersonalityWhenRiding, width: 200 },
      { columnId: ColIdEnum.noDefensiveRiderTrainingLast3Years, width: 200 },
      { columnId: ColIdEnum.notTrainedInBrakingTechniques, width: 200 },
      { columnId: ColIdEnum.usesPhoneWhileRiding, width: 220 },
      { columnId: ColIdEnum.ridesLessThan25000MilesAnnually, width: 200 },
      { columnId: ColIdEnum.riderHighOrVeryHighRisk, width: 200 },
      { columnId: ColIdEnum.highRiskRidingStyle, width: 200 },
      { columnId: ColIdEnum.operatorHighOrVeryHighRisk, width: 200 },
      { columnId: ColIdEnum.highRiskOperatingStyle, width: 200 },
      { columnId: ColIdEnum.skillsNotAssessedInLast3Years, width: 210 },
      { columnId: ColIdEnum.operatesBetweenMidnightAnd6am, width: 200 },
      { columnId: ColIdEnum.unsafePersonalityWhenOperating, width: 200 },
      { columnId: ColIdEnum.noFurtherTrainingIn3Years, width: 200 },
      { columnId: ColIdEnum.unawareOfPreUseInspection, width: 200 },
      { columnId: ColIdEnum.usesPhoneWhileOperating, width: 230 },
      { columnId: ColIdEnum.warnedMultipleTimesAboutOperatingPractice, width: 230 },
      { columnId: ColIdEnum.operatingMoreThan6HoursPerDay, width: 200 },
      { columnId: ColIdEnum.operatesUnevenSurfaces, width: 200 },
      { columnId: ColIdEnum.operatesUnfamiliarLocations, width: 200 },
      { columnId: ColIdEnum.operatesMultiplePivTypes, width: 200 },
      { columnId: ColIdEnum.movesHazardousMaterials, width: 200 },
      { columnId: ColIdEnum.pivHighRisk, width: 200 },
      { columnId: ColIdEnum.operatorMinimalSafetyChecks, width: 200 },
      { columnId: ColIdEnum.operatorUnawareVehicleMaintenanceResponsibility, width: 220 },
      { columnId: ColIdEnum.safetyRating, width: 200 },
      { columnId: ColIdEnum.rrCounter, width: 150 },
      { columnId: ColIdEnum.rrAttScore, width: 150 },
      { columnId: ColIdEnum.rrKnowScore, width: 150 },
      { columnId: ColIdEnum.rrBehavScore, width: 150 },
      { columnId: ColIdEnum.rrHazScore, width: 150 },
      { columnId: ColIdEnum.rrHigh, width: 150 },
      { columnId: ColIdEnum.rrMedium, width: 150 },
      { columnId: ColIdEnum.rrLow, width: 150 },
      { columnId: ColIdEnum.rrRatingVeryHigh, width: 150 },
      { columnId: ColIdEnum.rrRatingHigh, width: 150 },
      { columnId: ColIdEnum.rrRatingMedium, width: 150 },
      { columnId: ColIdEnum.rrRatingLow, width: 150 },
      { columnId: ColIdEnum.electronicConsentAssigned, width: 80 },
      { columnId: ColIdEnum.requiresLicenceCheck, width: 80 },

      { columnId: ColIdEnum.vrmPrivacyDateDone, width: 200 },
      { columnId: ColIdEnum.vrmPrivacyAssignedDate, width: 200 },
      { columnId: ColIdEnum.vrmPrivacyDueDate, width: 200 },
      { columnId: ColIdEnum.vrmPrivacyDaysToOverDue, width: 200 },

      { columnId: ColIdEnum.mentorPrivacyDateDone, width: 200 },
      { columnId: ColIdEnum.ersStatus, width: 200 },
      { columnId: ColIdEnum.ersDateDone, width: 200 },
      { columnId: ColIdEnum.ersAssignedDate, width: 200 },
      { columnId: ColIdEnum.ersDueDate, width: 200 },
      { columnId: ColIdEnum.ersDaysToOverDue, width: 200 },
      { columnId: ColIdEnum.insightPrivacyStatus, width: 200 },
      { columnId: ColIdEnum.insightPrivacyDateDone, width: 200 },
      { columnId: ColIdEnum.insightSOSPrivacyStatus, width: 200 },
      { columnId: ColIdEnum.insightSOSPrivacyDateDone, width: 200 },

      //  Shift Report
      { columnId: ColIdEnum.shortTrip, width: 100 },

      // DER Overview Report
      { columnId: ColIdEnum.openCoachingInPopup, width: 100 },
      { columnId: ColIdEnum.exportDERSubmission, width: 140 },
      { columnId: ColIdEnum.exportIDDReport, width: 160 },
      { columnId: ColIdEnum.derDsp, width: 160 },
      { columnId: ColIdEnum.averageTimeToDspReview, width: 160 },
      { columnId: ColIdEnum.averageTimeToCreateDer, width: 160 },
      { columnId: ColIdEnum.driverTimeSpent, width: 200 },
    ],
  };

  iconHeaderColumns = [
    { id: 'engineOff', iconPath: `${this.cService.mentorAssetRoot}engine-off-icon.png` },
    { id: 'accelerationRating', iconPath: `${this.cService.mentorAssetRoot}acceleration-icon.png` },
    { id: 'hardAcceleration', iconPath: `${this.cService.mentorAssetRoot}acceleration-icon.png` },
    { id: 'brakingRating', iconPath: `${this.cService.mentorAssetRoot}braking-icon.png` },
    { id: 'hardBraking', iconPath: `${this.cService.mentorAssetRoot}braking-icon.png` },
    { id: 'corneringRating', iconPath: `${this.cService.mentorAssetRoot}cornering-icon.png` },
    { id: 'hardCornering', iconPath: `${this.cService.mentorAssetRoot}cornering-icon.png` },
    { id: 'distractionRating', iconPath: `${this.cService.mentorAssetRoot}distraction-icon.png` },
    { id: 'distraction', iconPath: `${this.cService.mentorAssetRoot}distraction-icon.png` },
    { id: 'phoneCalls', iconPath: `${this.cService.mentorAssetRoot}distraction-icon.png` },
    { id: 'phoneManipulations', iconPath: `${this.cService.mentorAssetRoot}distraction-icon.png` },
    { id: 'speedRating', iconPath: `${this.cService.mentorAssetRoot}speed-icon.png` },
    { id: 'speeding', iconPath: `${this.cService.mentorAssetRoot}speed-icon.png` },
    { id: 'seatBeltOff', iconPath: `${this.cService.mentorAssetRoot}seat-belt-icon.png` },
    { id: 'reverse', iconPath: `${this.cService.mentorAssetRoot}reversing-icon.png` },
    { id: 'idling', iconPath: `${this.cService.mentorAssetRoot}idling-icon.png` },
    { id: 'mpg', iconPath: `${this.cService.mentorAssetRoot}fuel-icon.png` },
  ];

  // TODO: Backend to set a flag for reports that should show icons
  // then manual listing of report names won't be necessary.
  iconHeaderReportList = [
    'tsp summary report',
    'dsp performance report',
    'mentor driver performance report',
    'mentor driver delta performance report',
    'station performance report',
    'station performance delta report',
    'coaching recommendations report',
    'amazon coaching assignments',
  ];

  currentFilterToPersist: Array<FilterColumn> = [];
  currentDynamicColumn: FilterColumn;

  reportTextMappings: Array<{ rawValue: string; convertedValue: string; color: string }> = [];

  gridSearch = {
    text: '',
    activated: false,
  };
  defaultFilters = {
    status: { filterType: 'set', values: ['Active'] },
  };
  gridState = this.defaultGridState();
  companyDefaultGridState = this.defaultGridState();
  availableFrequencies: Array<PeriodFrequency> = [];
  selectedTimeFrame: SelectedTimeFrame = {};

  endpointFilterValue: EndpointFilterValue;

  private filterData: ReportBody = {};
  requestedServerFilters: any = {};
  agGrid: AgGridAngular;

  constructor(
    private http: HttpClient,
    private cService: CommonService,
    private translation: TranslateService,
    private translationsService: TranslationsService,
    private activatedRoute: ActivatedRoute,
    private predefinedFiltersService: PredefinedFiltersService,
    private gridStateService: GridStateService,
    private columnsService: ColumnsService
  ) {
    this.initialize();
  }

  setGrid(agGrid: AgGridAngular) {
    this.agGrid = agGrid; //V3DDP-338 https://vrm-mentor.atlassian.net/browse/V3DDP-338
  }

  defaultGridState(): GridState {
    const currentGridFilterCache =
      JSON.parse(localStorage.getItem(this.cService.localStorageKeys.gridFilterCache)) ?? {};
    return {
      sortModel: [],
      filterModel: { ...this.defaultFilters, ...currentGridFilterCache },
      displayedColumns: [],
      overrideAPIColumnsVisibility: [],
      hiddenColumns: [],
      columnGroups: [],
      selectedServerFilter: {},
    };
  }

  initialize() {
    this.predefinedFiltersService.getGroupedActiveFilterValues().subscribe({
      next: (filters) => {
        this.applyServerFilters(filters);
      },
    });

    this.cService.userProfileReady$.subscribe({
      next: (userProfile: User) => {
        if (userProfile) {
          console.log(
            '%c Server Persisted Existing USER',
            'color: blue',
            JSON.stringify(userProfile?.preferences?.gridstate?.filterModel)
          );
          if (userProfile.hasCustomGridState) {
            this.gridState = this.gridStateService.deepCopyGridState([
              this.gridState,
              userProfile.preferences.gridstate,
            ]);

            this.gridStateDifferentFromDefault = userProfile.hasCustomGridState;
            console.log(
              '%c Server Persisted Grid State USER',
              'color: purple',
              JSON.stringify(this.gridState.filterModel)
            );
          } else if (userProfile.companyPreferences) {
            this.companyDefaultGridState = this.gridStateService.deepCopyGridState([
              this.defaultGridState(),
              userProfile.companyPreferences.gridstate,
            ]);
            this.gridState = this.gridStateService.deepCopyGridState([this.gridState, this.companyDefaultGridState]);
            console.log(
              '%c Server Persisted Grid State COMPANY',
              'color: purple',
              JSON.stringify(this.gridState.filterModel)
            );
          }
        }
      },
    });

    this.activatedRoute.queryParams.subscribe({
      next: (params: Params) => {
        if (params.period) {
          let period;
          const yearMonthDayFormatRegex = /^\d{4}-\d{2}-\d{2}$/g;
          const yearMonthFormatRegex = /^\d{4}-\d{2}$/g;
          const yearOnlyFormatRegex = /^\d{4}$/g;

          if (params.period.match(yearMonthDayFormatRegex) && params.periodType === 'week') {
            period = moment(params.period, 'YYYY-MM-DD');
            this.selectedTimeFrame.timeFrame = { period: 'week', date: period.format('YYYY-MM-DD') };
          } else if (params.period.match(yearMonthDayFormatRegex)) {
            period = moment(params.period, 'YYYY-MM-DD');
            this.selectedTimeFrame.timeFrame = { period: 'day', date: period.format('YYYY-MM-DD') };
          } else if (params.period.match(yearMonthFormatRegex)) {
            period = moment(params.period, 'YYYY-MM');
            this.selectedTimeFrame.timeFrame = { period: 'month', date: period.format('YYYY-MM-DD') };
          } else if (params.period.match(yearOnlyFormatRegex)) {
            period = moment(params.period, 'YYYY');
            this.selectedTimeFrame.timeFrame = { period: 'year', date: period.format('YYYY-MM-DD') };
          }
        }
      },
    });

    // initializing available frequencies for calendar in custom details pane
    this.availableFrequencies = [
      { label: <TimeFrame>'monthly', value: 'month' },
      { label: <TimeFrame>'weekly', value: 'week' },
      { label: <TimeFrame>'daily', value: 'day' },
    ];

    // initializing default frequency for calendar in custom details pane.
    this.selectedTimeFrame = {
      frequency: this.availableFrequencies[0],
      timeFrame: null,
    };
  }

  // RxJs EVENTS
  addNewlySelectedRowAndTriggerObservable(selectedRow: any) {
    this._selectedRowsSubject.next(selectedRow);
  }

  triggerBehaviourSubjectOnNewCommonReportsDataOnLoad(params: NewCommonReportTriggerParams) {
    if (this.cService.appInitialLoad || this.commonRptPageChangedOnLoad) {
      this._commonReportDataChangeSubjectOnLoad.next(params);
    }
  }

  triggerBehaviourSubjectOnNewCommonReportsData(params: NewCommonReportTriggerParams) {
    this._commonReportDataChangeSubject.next(params);
  }

  triggerBehaviourSubjectOnNewCommonReportsMeta(params: NewCommonReportTriggerParams) {
    this._commonReportMetaChangeSubject.next(params);
  }

  triggerServerFilterChangedObservable(isCommonReport: boolean) {
    this._serverFilterChanged.next(isCommonReport);
  }

  triggerReportDataChangedObservable() {
    this._reportDataChanged.next(null);
  }

  triggerDriverPerformanceDialogClosedObservable(
    filterValue: EndpointFilterValue,
    triggerOnlyWhenFormsChanged: boolean
  ) {
    if (triggerOnlyWhenFormsChanged) {
      if (this.formDataUpdated) {
        this._driverPerformanceDialogClosedSubject.next(filterValue);
      }
    } else {
      this._driverPerformanceDialogClosedSubject.next(filterValue);
    }
  }

  triggerMisNotificationAdminDialogClosedObservable() {
    this._misNotificationsChangedSubject.next({ refresh: true });
  }

  triggerRowsDeleted(rowData: any) {
    this._rowsDeleted.next(rowData);
  }

  triggerRowAdded(rowData: any) {
    this._rowAdded.next(rowData);
  }

  triggerRowUpdated(rowData: any) {
    this._rowUpdated.next(rowData);
  }

  // ---- Grid / Column Helpers ----
  public getColumnNames(structure: {}): string[] {
    const columnNames = [];

    for (const nodeName in structure) {
      if (structure.hasOwnProperty(nodeName)) {
        columnNames.push(nodeName);
      }
    }

    return columnNames;
  }

  public applyAgGridLocale() {
    setTimeout(() => {
      this.translation.get('AGGRIDLOCALE').subscribe({
        next: (translate) => {
          const localeTextKeys = Object.keys(agGridLocaleText);

          localeTextKeys.forEach((localeKey) => {
            if (translate[localeKey]) {
              agGridLocaleText[localeKey] = translate[localeKey];
            }
          });

          this.agGridConfig.common.localeText = agGridLocaleText;
          this.agGridConfig.common.enableRtl =
            this.cService.selectedLanguage && this.cService.selectedLanguage.isRtl
              ? this.cService.selectedLanguage.isRtl
              : false;
        },
      });
    }, 500);
  }

  public getDocumentGenerator(): DocumentGeneratorData {
    if (this.currentReportMeta.documentGeneratorData) {
      this.currentReportMeta.documentGeneratorData.language = this.cService.selectedLanguage.code;
    }

    return this.currentReportMeta.documentGeneratorData;
  }

  public transformSingleDataForDetailsPane(columnName: string, value: any) {
    this.reportTextMappings = [
      { rawValue: 'green', convertedValue: this.cService.readyTranslations.reportData.low, color: 'green' },
      { rawValue: 'yellow', convertedValue: this.cService.readyTranslations.reportData.medium, color: 'orange' },
      { rawValue: 'red', convertedValue: this.cService.readyTranslations.reportData.high, color: 'red' },
      { rawValue: 'na', convertedValue: this.cService.readyTranslations.reportData.na, color: 'grey' },
      {
        rawValue: 'Safe Driver',
        convertedValue: this.cService.readyTranslations.reportData.safeDriver,
        color: 'blue',
      },
      { rawValue: 'Low', convertedValue: this.cService.readyTranslations.reportData.low, color: 'green' },
      { rawValue: 'Medium', convertedValue: this.cService.readyTranslations.reportData.medium, color: 'orange' },
      { rawValue: 'High', convertedValue: this.cService.readyTranslations.reportData.high, color: 'red' },
      {
        rawValue: 'Very High',
        convertedValue: this.cService.readyTranslations.reportData.veryHigh,
        color: 'red',
      },
      {
        rawValue: 'very high',
        convertedValue: this.cService.readyTranslations.reportData.veryHigh,
        color: 'red',
      },
      {
        rawValue: 'Very high',
        convertedValue: this.cService.readyTranslations.reportData.veryHigh,
        color: 'red',
      },

      { rawValue: 'Great', convertedValue: this.cService.readyTranslations.reportData.great, color: 'green' },
      { rawValue: 'Good', convertedValue: this.cService.readyTranslations.reportData.good, color: 'green' },
      {
        rawValue: 'Average',
        convertedValue: this.cService.readyTranslations.reportData.average,
        color: 'orange',
      },
      { rawValue: 'Poor', convertedValue: this.cService.readyTranslations.reportData.poor, color: 'red' },
      { rawValue: 'Risky', convertedValue: this.cService.readyTranslations.reportData.risky, color: 'black' },

      { rawValue: 'Active', convertedValue: this.cService.readyTranslations.reportData.active, color: 'green' },
      {
        rawValue: 'Inactive',
        convertedValue: this.cService.readyTranslations.reportData.inactive,
        color: 'grey',
      },
      {
        rawValue: 'Test Account',
        convertedValue: this.cService.readyTranslations.reportData.testAccount,
        color: 'grey',
      },
      { rawValue: 'Other', convertedValue: this.cService.readyTranslations.reportData.other, color: '' },
      { rawValue: 'Pre Hire', convertedValue: this.cService.readyTranslations.reportData.preHire, color: '' },

      { rawValue: 'Agree', convertedValue: this.cService.readyTranslations.reportData.agree, color: 'green' },
      {
        rawValue: 'Disagree',
        convertedValue: this.cService.readyTranslations.reportData.disagree,
        color: 'orange',
      },
      { rawValue: 'Hold', convertedValue: this.cService.readyTranslations.reportData.hold, color: 'orange' },
      { rawValue: 'Yes', convertedValue: this.cService.readyTranslations.reportData.yes, color: 'green' },
      { rawValue: 'No', convertedValue: this.cService.readyTranslations.reportData.no, color: 'red' },

      {
        rawValue: 'Complete',
        convertedValue: this.cService.readyTranslations.reportData.complete,
        color: 'green',
      },
      {
        rawValue: 'Incomplete',
        convertedValue: this.cService.readyTranslations.reportData.incomplete,
        color: 'orange',
      },
      {
        rawValue: 'In Progress',
        convertedValue: this.cService.readyTranslations.reportData.inProgress,
        color: 'orange',
      },
      {
        rawValue: 'Partial',
        convertedValue: this.cService.readyTranslations.reportData.partial,
        color: 'orange',
      },
      {
        rawValue: 'Not Started',
        convertedValue: this.cService.readyTranslations.reportData.notStarted,
        color: 'red',
      },
      { rawValue: 'Required', convertedValue: this.cService.readyTranslations.reportData.required, color: 'red' },
      {
        rawValue: 'Not Required',
        convertedValue: this.cService.readyTranslations.reportData.notRequired,
        color: 'grey',
      },
      {
        rawValue: 'Optional',
        convertedValue: this.cService.readyTranslations.reportData.optional,
        color: 'green',
      },
      { rawValue: 'Current', convertedValue: this.cService.readyTranslations.reportData.current, color: '' },

      { rawValue: 'Valid', convertedValue: this.cService.readyTranslations.reportData.valid, color: 'green' },
      {
        rawValue: 'Not Valid',
        convertedValue: this.cService.readyTranslations.reportData.notValid,
        color: 'red',
      },
      { rawValue: 'Invalid', convertedValue: this.cService.readyTranslations.reportData.notValid, color: 'red' },
      { rawValue: 'invalid', convertedValue: this.cService.readyTranslations.reportData.notValid, color: 'red' },
      { rawValue: 'See MVR', convertedValue: this.cService.readyTranslations.reportData.seeMvr, color: 'orange' },

      { rawValue: 'Car', convertedValue: this.cService.readyTranslations.reportData['Car'], color: '' },
      {
        rawValue: 'SUV / 4x4',
        convertedValue: this.cService.readyTranslations.reportData['SUV / 4x4'],
        color: '',
      },
      { rawValue: 'Van', convertedValue: this.cService.readyTranslations.reportData['Van'], color: '' },
      {
        rawValue: 'Motorbike',
        convertedValue: this.cService.readyTranslations.reportData['Motorbike'],
        color: '',
      },
      { rawValue: 'Bicycle', convertedValue: this.cService.readyTranslations.reportData['Bicycle'], color: '' },
      {
        rawValue: 'Large Vehicle (Multi-Drop)',
        convertedValue: this.cService.readyTranslations.reportData['Large Vehicle (Multi-Drop)'],
        color: '',
      },
      {
        rawValue: 'Tractor Trailer',
        convertedValue: this.cService.readyTranslations.reportData['Tractor Trailer'],
        color: '',
      },
      {
        rawValue: 'Large Vehicle (Straight)',
        convertedValue: this.cService.readyTranslations.reportData['Large Vehicle (Straight)'],
        color: '',
      },

      { rawValue: 'None', convertedValue: this.cService.readyTranslations.reportData['None'], color: '' },
      { rawValue: 'Slight', convertedValue: this.cService.readyTranslations.reportData['Slight'], color: '' },
      { rawValue: 'Serious', convertedValue: this.cService.readyTranslations.reportData['Serious'], color: '' },
      { rawValue: 'Fatal', convertedValue: this.cService.readyTranslations.reportData['Fatal'], color: '' },

      {
        rawValue: 'Driver: Distraction',
        convertedValue: this.cService.readyTranslations.reportData['Driver: Distraction'],
        color: '',
      },
      {
        rawValue: 'Driver: Lack of Observation',
        convertedValue: this.cService.readyTranslations.reportData['Driver: Lack of Observation'],
        color: '',
      },
      {
        rawValue: 'Driver: Fatigue',
        convertedValue: this.cService.readyTranslations.reportData['Driver: Fatigue'],
        color: '',
      },
      {
        rawValue: 'Driver: Excessive Speed',
        convertedValue: this.cService.readyTranslations.reportData['Driver: Excessive Speed'],
        color: '',
      },
      {
        rawValue: 'Driver: Lack of Experience / Knowledge / Skill',
        convertedValue: this.cService.readyTranslations.reportData['Driver: Lack of Experience / Knowledge / Skill'],
        color: '',
      },
      {
        rawValue: 'Driver: Driving too Close',
        convertedValue: this.cService.readyTranslations.reportData['Driver: Driving too Close'],
        color: '',
      },
      {
        rawValue: 'Driver: Failure to Yield',
        convertedValue: this.cService.readyTranslations.reportData['Driver: Failure to Yield'],
        color: '',
      },
      {
        rawValue: 'Driver: DUI Alcohol / Drugs / Medication',
        convertedValue: this.cService.readyTranslations.reportData['Driver: DUI Alcohol / Drugs / Medication'],
        color: '',
      },
      {
        rawValue: 'Driver: Emotional Breakdown / Road Rage',
        convertedValue: this.cService.readyTranslations.reportData['Driver: Emotional Breakdown / Road Rage'],
        color: '',
      },
      {
        rawValue: 'Other Party: Distraction',
        convertedValue: this.cService.readyTranslations.reportData['Other Party: Distraction'],
        color: '',
      },
      {
        rawValue: 'Other Party: Lack of Observation',
        convertedValue: this.cService.readyTranslations.reportData['Other Party: Lack of Observation'],
        color: '',
      },
      {
        rawValue: 'Other Party: Fatigue',
        convertedValue: this.cService.readyTranslations.reportData['Other Party: Fatigue'],
        color: '',
      },
      {
        rawValue: 'Other Party: Excessive Speed',
        convertedValue: this.cService.readyTranslations.reportData['Other Party: Excessive Speed'],
        color: '',
      },
      {
        rawValue: 'Other Party: Lack of Experience / Knowledge / Skill',
        convertedValue:
          this.cService.readyTranslations.reportData['Other Party: Lack of Experience / Knowledge / Skill'],
        color: '',
      },
      {
        rawValue: 'Other Party: Driving too Close',
        convertedValue: this.cService.readyTranslations.reportData['Other Party: Driving too Close'],
        color: '',
      },
      {
        rawValue: 'Other Party: Failure to Yield',
        convertedValue: this.cService.readyTranslations.reportData['Other Party: Failure to Yield'],
        color: '',
      },
      {
        rawValue: 'Other Party: DUI Alcohol / Drugs/ Medication',
        convertedValue: this.cService.readyTranslations.reportData['Other Party: DUI Alcohol / Drugs/ Medication'],
        color: '',
      },
      {
        rawValue: 'Other Party: Road Rage Attack',
        convertedValue: this.cService.readyTranslations.reportData['Other Party: Road Rage Attack'],
        color: '',
      },
      {
        rawValue: 'Vehicle Failure: Tires / Brakes / Clutch / Other',
        convertedValue: this.cService.readyTranslations.reportData['Vehicle Failure: Tires / Brakes / Clutch / Other'],
        color: '',
      },
      {
        rawValue: 'Act of Nature',
        convertedValue: this.cService.readyTranslations.reportData['Act of Nature'],
        color: '',
      },
      {
        rawValue: 'Cause not identified',
        convertedValue: this.cService.readyTranslations.reportData['Cause not identified'],
        color: '',
      },

      { rawValue: 'Motorway', convertedValue: this.cService.readyTranslations.reportData['Motorway'], color: '' },
      {
        rawValue: 'Country / Rural',
        convertedValue: this.cService.readyTranslations.reportData['Country / Rural'],
        color: '',
      },
      {
        rawValue: 'Town / City',
        convertedValue: this.cService.readyTranslations.reportData['Town / City'],
        color: '',
      },
      {
        rawValue: 'Residential',
        convertedValue: this.cService.readyTranslations.reportData['Residential'],
        color: '',
      },
      { rawValue: 'Off-Road', convertedValue: this.cService.readyTranslations.reportData['Off-Road'], color: '' },
      { rawValue: 'Other', convertedValue: this.cService.readyTranslations.reportData['Other'], color: '' },

      { rawValue: 'Day', convertedValue: this.cService.readyTranslations.reportData['Day'], color: '' },
      { rawValue: 'Dawn', convertedValue: this.cService.readyTranslations.reportData['Dawn'], color: '' },
      { rawValue: 'Dusk', convertedValue: this.cService.readyTranslations.reportData['Dusk'], color: '' },
      {
        rawValue: 'Night (With Lighting)',
        convertedValue: this.cService.readyTranslations.reportData['Night (With Lighting)'],
        color: '',
      },
      {
        rawValue: 'Night (No Lighting)',
        convertedValue: this.cService.readyTranslations.reportData['Night (No Lighting)'],
        color: '',
      },

      {
        rawValue: 'Fine / Clear',
        convertedValue: this.cService.readyTranslations.reportData['Fine / Clear'],
        color: '',
      },
      {
        rawValue: 'Bright Sun',
        convertedValue: this.cService.readyTranslations.reportData['Bright Sun'],
        color: '',
      },
      {
        rawValue: 'Dull / Overcast',
        convertedValue: this.cService.readyTranslations.reportData['Dull / Overcast'],
        color: '',
      },
      {
        rawValue: 'Fog / Mist',
        convertedValue: this.cService.readyTranslations.reportData['Fog / Mist'],
        color: '',
      },
      {
        rawValue: 'Hail / Sleet',
        convertedValue: this.cService.readyTranslations.reportData['Hail / Sleet'],
        color: '',
      },
      {
        rawValue: 'Light Rain',
        convertedValue: this.cService.readyTranslations.reportData['Light Rain'],
        color: '',
      },
      {
        rawValue: 'Heavy Rain',
        convertedValue: this.cService.readyTranslations.reportData['Heavy Rain'],
        color: '',
      },
      {
        rawValue: 'Light Snow',
        convertedValue: this.cService.readyTranslations.reportData['Light Snow'],
        color: '',
      },
      {
        rawValue: 'Heavy Snow',
        convertedValue: this.cService.readyTranslations.reportData['Heavy Snow'],
        color: '',
      },
      { rawValue: 'Ice', convertedValue: this.cService.readyTranslations.reportData['Ice'], color: '' },
      {
        rawValue: 'Light Wind',
        convertedValue: this.cService.readyTranslations.reportData['Light Wind'],
        color: '',
      },
      {
        rawValue: 'Strong Wind',
        convertedValue: this.cService.readyTranslations.reportData['Strong Wind'],
        color: '',
      },
      {
        rawValue: 'Hurricane / Tornado',
        convertedValue: this.cService.readyTranslations.reportData['Hurricane / Tornado'],
        color: '',
      },
      { rawValue: 'Flood', convertedValue: this.cService.readyTranslations.reportData['Flood'], color: '' },
      {
        rawValue: 'Earthquake',
        convertedValue: this.cService.readyTranslations.reportData['Earthquake'],
        color: '',
      },

      {
        rawValue: 'Excellent',
        convertedValue: this.cService.readyTranslations.reportData['Excellent'],
        color: '',
      },
      { rawValue: 'Good', convertedValue: this.cService.readyTranslations.reportData['Good'], color: '' },
      {
        rawValue: 'Satisfactory',
        convertedValue: this.cService.readyTranslations.reportData['Satisfactory'],
        color: '',
      },
      { rawValue: 'Poor', convertedValue: this.cService.readyTranslations.reportData['Poor'], color: '' },
      {
        rawValue: 'Dangerous',
        convertedValue: this.cService.readyTranslations.reportData['Dangerous'],
        color: '',
      },
      {
        rawValue: 'Under Construction',
        convertedValue: this.cService.readyTranslations.reportData['Under Construction'],
        color: '',
      },

      {
        rawValue: 'Congested',
        convertedValue: this.cService.readyTranslations.reportData['Congested'],
        color: '',
      },
      { rawValue: 'Flowing', convertedValue: this.cService.readyTranslations.reportData['Flowing'], color: '' },
      { rawValue: 'Normal', convertedValue: this.cService.readyTranslations.reportData['Normal'], color: '' },
      { rawValue: 'Light', convertedValue: this.cService.readyTranslations.reportData['Light'], color: '' },

      { rawValue: 'Airport', convertedValue: this.cService.readyTranslations.reportData['Airport'], color: '' },
      { rawValue: 'Driveway', convertedValue: this.cService.readyTranslations.reportData['Driveway'], color: '' },
      { rawValue: 'Junction', convertedValue: this.cService.readyTranslations.reportData['Junction'], color: '' },
      { rawValue: 'Car Park', convertedValue: this.cService.readyTranslations.reportData['Car Park'], color: '' },
      {
        rawValue: 'Retail Park',
        convertedValue: this.cService.readyTranslations.reportData['Retail Park'],
        color: '',
      },
      {
        rawValue: 'Road / Street',
        convertedValue: this.cService.readyTranslations.reportData['Road / Street'],
        color: '',
      },
      {
        rawValue: 'Roundabout',
        convertedValue: this.cService.readyTranslations.reportData['Roundabout'],
        color: '',
      },

      { rawValue: 'Forward', convertedValue: this.cService.readyTranslations.reportData['Forward'], color: '' },
      { rawValue: 'Reverse', convertedValue: this.cService.readyTranslations.reportData['Reverse'], color: '' },
      {
        rawValue: 'Stationary',
        convertedValue: this.cService.readyTranslations.reportData['Stationary'],
        color: '',
      },
      { rawValue: 'Parked', convertedValue: this.cService.readyTranslations.reportData['Parked'], color: '' },

      {
        rawValue: 'Accelerating',
        convertedValue: this.cService.readyTranslations.reportData['Accelerating'],
        color: '',
      },
      { rawValue: 'Braking', convertedValue: this.cService.readyTranslations.reportData['Braking'], color: '' },
      {
        rawValue: 'Changing Lanes',
        convertedValue: this.cService.readyTranslations.reportData['Changing Lanes'],
        color: '',
      },
      {
        rawValue: 'Reversing',
        convertedValue: this.cService.readyTranslations.reportData['Reversing'],
        color: '',
      },
      { rawValue: 'Parking', convertedValue: this.cService.readyTranslations.reportData['Parking'], color: '' },
      {
        rawValue: 'Overtaking / Being Overtaken',
        convertedValue: this.cService.readyTranslations.reportData['Overtaking / Being Overtaken'],
        color: '',
      },
      { rawValue: 'Turning', convertedValue: this.cService.readyTranslations.reportData['Turning'], color: '' },
      { rawValue: 'U-Turn', convertedValue: this.cService.readyTranslations.reportData['U-Turn'], color: '' },

      {
        rawValue: 'To / From Work',
        convertedValue: this.cService.readyTranslations.reportData['To / From Work'],
        color: '',
      },
      { rawValue: 'Work', convertedValue: this.cService.readyTranslations.reportData['Work'], color: '' },
      { rawValue: 'Personal', convertedValue: this.cService.readyTranslations.reportData['Personal'], color: '' },

      { rawValue: '0 - 1 hr', convertedValue: this.cService.readyTranslations.reportData['0 - 1 hr'], color: '' },
      { rawValue: '1 - 2 hr', convertedValue: this.cService.readyTranslations.reportData['1 - 2 hr'], color: '' },
      { rawValue: '2 - 3 hr', convertedValue: this.cService.readyTranslations.reportData['2 - 3 hr'], color: '' },
      { rawValue: '3 - 4 hr', convertedValue: this.cService.readyTranslations.reportData['3 - 4 hr'], color: '' },
      { rawValue: '4+ hrs', convertedValue: this.cService.readyTranslations.reportData['4+ hrs'], color: '' },

      { rawValue: 'Fatigued', convertedValue: this.cService.readyTranslations.reportData['Fatigued'], color: '' },
      {
        rawValue: 'Aggressive',
        convertedValue: this.cService.readyTranslations.reportData['Aggressive'],
        color: '',
      },
      {
        rawValue: 'Under the influence of alcohol or drugs',
        convertedValue: this.cService.readyTranslations.reportData['Under the influence of alcohol or drugs'],
        color: '',
      },
      {
        rawValue: 'Distracted',
        convertedValue: this.cService.readyTranslations.reportData['Distracted'],
        color: '',
      },

      { rawValue: 'Speeding', convertedValue: this.cService.readyTranslations.reportData['Speeding'], color: '' },
      {
        rawValue: 'Crash / Collision / Incident',
        convertedValue: this.cService.readyTranslations.reportData['Crash / Collision / Incident'],
        color: '',
      },
      {
        rawValue: 'Licence Violation',
        convertedValue: this.cService.readyTranslations.reportData['Licence Violation'],
        color: '',
      },
      {
        rawValue: 'Near Miss',
        convertedValue: this.cService.readyTranslations.reportData['Near Miss'],
        color: '',
      },

      {
        rawValue: 'FICO Safe Driving Score between 560 and 709 for two consecutive weeks',
        convertedValue: this.cService.readyTranslations.reportData.ficoSafeScoreBetween560And709,
        color: '',
      },
      {
        rawValue: 'Weekly FICO Safe Driving Score <500',
        convertedValue: this.cService.readyTranslations.reportData.weeklyFicoLessThan500,
        color: '',
      },
      {
        rawValue: 'driverIndexCoachCoachingReason',
        convertedValue: this.cService.readyTranslations.reportData.driverIndexCoachCoachingReason,
        color: '',
      },
      {
        rawValue: 'pccCoachingReason',
        convertedValue: this.cService.readyTranslations.reportData.pccCoachingReason,
        color: '',
      },
      {
        rawValue: 'driverIndexProCoachingReason',
        convertedValue: this.cService.readyTranslations.reportData.driverIndexProCoachingReason,
        color: '',
      },
      {
        rawValue: 'weeklyFicoGreaterThan500',
        convertedValue: this.cService.readyTranslations.reportData.weeklyFicoLessThan500,
        color: '',
      },
      {
        rawValue: 'Weekly FICO Safe Driving Score <559',
        convertedValue: this.cService.readyTranslations.reportData.weeklyFicoLessThan559,
        color: '',
      },
      {
        rawValue: 'ficoSafeScoreBetween560And709',
        convertedValue: this.cService.readyTranslations.reportData.ficoSafeScoreBetween560And709,
        color: '',
      },
      {
        rawValue: 'ficoSafeScoreBetween560And709',
        convertedValue: this.cService.readyTranslations.reportData.ficoSafeScoreBetween560And709,
        color: '',
      },
      {
        rawValue: 'Any Driver who had a FICO® Safe Driving Score between 560-709 for each of the last two weeks',
        convertedValue: this.cService.readyTranslations.reportData.ficoSafeScoreBetween560And709,
        color: '',
      },
      {
        rawValue: 'Any Driver who has an FICO® Safe Driving Score between 560-709 for two weeks in a row.',
        convertedValue: this.cService.readyTranslations.reportData.ficoSafeScoreBetween560And709,
        color: '',
      },

      { rawValue: 'INITIAL', convertedValue: this.cService.readyTranslations.reportData.initial, color: '' },
      { rawValue: 'Initial', convertedValue: this.cService.readyTranslations.reportData.initial, color: '' },
      { rawValue: 'RECHECK', convertedValue: this.cService.readyTranslations.reportData.initial, color: '' },
      { rawValue: 'Recheck', convertedValue: this.cService.readyTranslations.reportData.initial, color: '' },
      { rawValue: 'Re-check', convertedValue: this.cService.readyTranslations.reportData.initial, color: '' },
      { rawValue: 'Re-Check', convertedValue: this.cService.readyTranslations.reportData.initial, color: '' },
      { rawValue: 'RE-CHECK', convertedValue: this.cService.readyTranslations.reportData.initial, color: '' },

      { rawValue: 'private', convertedValue: this.cService.readyTranslations.reportData.private, color: '' },
      {
        rawValue: 'company_pool_or_utility',
        convertedValue: this.cService.readyTranslations.reportData.company_pool_or_utility,
        color: '',
      },
      {
        rawValue: 'company_hire_or_rental',
        convertedValue: this.cService.readyTranslations.reportData.company_hire_or_rental,
        color: '',
      },
      {
        rawValue: 'company_owned_or_leased',
        convertedValue: this.cService.readyTranslations.reportData.company_owned_or_leased,
        color: '',
      },
      {
        rawValue: 'tool_of_trade',
        convertedValue: this.cService.readyTranslations.reportData.tool_of_trade,
        color: '',
      },
      {
        rawValue: 'benefit_vehicle',
        convertedValue: this.cService.readyTranslations.reportData.benefit_vehicle,
        color: '',
      },
      {
        rawValue: 'benefit_cash',
        convertedValue: this.cService.readyTranslations.reportData.benefit_cash,
        color: '',
      },
      {
        rawValue: ReportStaticValues.awaitingReview,
        convertedValue: this.cService.getSingleTranslation(
          'REPORTDATA.awaitingReview',
          ReportStaticValues.awaitingReview
        ),
        color: 'orange',
      },
      {
        rawValue: ReportStaticValues.completed,
        convertedValue: this.cService.getSingleTranslation('REPORTDATA.completed', ReportStaticValues.completed),
        color: 'green',
      },
      {
        rawValue: ReportStaticValues.reportStarted,
        convertedValue: this.cService.getSingleTranslation(
          'REPORTDATA.reportStarted',
          ReportStaticValues.reportStarted
        ),
        color: 'red',
      },
      {
        rawValue: ReportStaticValues.notAvailable,
        convertedValue: this.cService.getSingleTranslation('REPORTDATA.notAvailable', ReportStaticValues.notAvailable),
        color: '',
      },
      {
        rawValue: ReportStaticValues.view,
        convertedValue: this.cService.getSingleTranslation('GENERIC.view', ReportStaticValues.view),
        color: '',
      },
    ];

    const valueStructure: { name: string; type: string } = this.dataStructure[columnName];
    let transformedData: TransformedSingleData = null;

    if (valueStructure && columnName) {
      transformedData = {
        uiValue: value,
        uiColName:
          valueStructure.name && valueStructure.name.toLowerCase() === 'pin'
            ? valueStructure.name.toUpperCase()
            : valueStructure.name,
      };
      const translatedColName = this.translation.instant('REPORTCOLUMNS')[transformedData.uiColName];

      if (translatedColName) {
        transformedData.uiColName = translatedColName;
      }

      if (valueStructure.type === VrmColumnTypes.percentage) {
        transformedData.uiValue = this.cService.displayNumberAsPercentage(value);
        if (isNaN(value) && this.isColumnToTranslate(columnName, valueStructure.type)) {
          transformedData.uiValue = this.cService.getSingleTranslation('REPORTDATA.' + value, value) as string;
        }
      } else if (valueStructure.type === VrmColumnTypes.decimal) {
        if (ReportNameEnum.mentor && columnName.toLowerCase().includes('time')) {
          transformedData.uiValue = this.cService.numberToDecimalPlace(value, 1);
        } else if (columnName.includes('indexingDiffLast')) {
          transformedData.uiValue = this.cService.prefixNumberWithPlusSign(value, true);
          if (transformedData.uiValue.toString().startsWith('+')) {
            transformedData.color = 'red';
          } else {
            transformedData.color = 'green';
          }
        } else {
          transformedData.uiValue = this.cService.numberToDecimalPlace(value);
        }
      } else if (valueStructure.type === VrmColumnTypes.number) {
        if (
          columnName.toLowerCase() === ColIdEnum.partialIndexRating.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.profileDriver.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.defensiveDriving.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.rating.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.profileRating.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.driverRating.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.journeyRating.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.operatorRating.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.warehouseRating.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.defensiveOperating.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.vehicleRating.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.attitudeRating.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.knowledgeRating.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.behaviourRating.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.hazardRating.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.ratingTo.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.ratingFrom.toLowerCase()
        ) {
          transformedData.uiValue = this.cService.mapNumericRatingToText(
            NumericRatingToTextEnum.highMediumLowNa,
            value
          );
        } else if (
          columnName.toLowerCase() === ColIdEnum.companyRiskLevel.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.companyPointsScore.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.indexingPointsValue.toLowerCase()
        ) {
          if (typeof value === 'number') {
            const companyRiskLevelValues = this.cService.userProfile.companyRiskLevelValues;

            if (value < companyRiskLevelValues.low) {
              transformedData.uiValue = `${this.cService.readyTranslations.reportData.safeDriver} (${value})`;
              transformedData.color = 'blue';
            }

            if (value >= companyRiskLevelValues.low && value < companyRiskLevelValues.medium) {
              transformedData.uiValue = `${this.cService.readyTranslations.reportData.low} (${value})`;
              transformedData.color = 'green';
            }

            if (value >= companyRiskLevelValues.medium && value < companyRiskLevelValues.high) {
              transformedData.uiValue = `${this.cService.readyTranslations.reportData.medium} (${value})`;
              transformedData.color = 'orange';
            }

            if (value >= companyRiskLevelValues.high) {
              transformedData.uiValue = `${this.cService.readyTranslations.reportData.high} (${value})`;
              transformedData.color = 'red';
            }
          }
        } else if (
          columnName.toLowerCase() === ColIdEnum.accelerationRating.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.brakingRating.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.corneringRating.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.distractionRating.toLowerCase() ||
          columnName.toLowerCase() === ColIdEnum.speedRating.toLowerCase()
        ) {
          transformedData.uiValue = this.cService.mapNumericRatingToText(
            NumericRatingToTextEnum.poorAverageGoodNa,
            value
          );
        }
      } else if (
        value &&
        (valueStructure.type === VrmColumnTypes.text ||
          valueStructure.type === VrmColumnTypes.number ||
          valueStructure.type === VrmColumnTypes.date ||
          valueStructure.type === VrmColumnTypes.file)
      ) {
        if (columnName.toLowerCase() !== ColIdEnum.firstName && columnName.toLowerCase() !== ColIdEnum.lastName) {
          for (let i = 0; i < this.reportTextMappings.length; i++) {
            const mapping = this.reportTextMappings[i];

            if (value && typeof value === 'string') {
              if (columnName.toLowerCase() === ColIdEnum.questionDamageDefectOnOtherVehicle.toLowerCase()) {
                transformedData.uiValue = this.getTranslationsForMultiValuedColumn(value);
              } else if (this.isColumnToTranslate(columnName, valueStructure.type)) {
                transformedData.uiValue = this.cService.getSingleTranslation('REPORTDATA.' + value, value) as string;
              } else {
                if (value.trim().toLowerCase() === mapping.rawValue.toLowerCase()) {
                  transformedData.color = mapping.color;

                  const caseInsensitiveRawVal = new RegExp(mapping.rawValue, 'i');
                  transformedData.uiValue = value.replace(caseInsensitiveRawVal, mapping.convertedValue);

                  if (!transformedData.uiValue.includes('@')) {
                    transformedData.uiValue = this.cService.toTitleCase(transformedData.uiValue);
                  }
                  break;
                }
              }
            }
          }
        }
      }
      const columnsWithNAValue = Object.keys(ColumnsWithNAValue).map((tag) => tag.toLowerCase());
      if (columnsWithNAValue.includes(columnName.toLowerCase())) {
        if (value.toUpperCase() === 'NA' || value.toUpperCase() === 'N/A') {
          transformedData.uiValue = this.cService.readyTranslations.reportData.na;
        }
      }

      // Apply colours based on matched uiValue
      for (let i = 0; i < this.reportTextMappings.length; i++) {
        const mapping = this.reportTextMappings[i];

        if (transformedData.uiValue && typeof transformedData.uiValue === 'string') {
          if (transformedData.uiValue.trim().toLowerCase() === mapping.rawValue.toLowerCase()) {
            if (
              mapping.rawValue.toLowerCase() === 'not started' &&
              this.cService?.userProfile?.userPermissions?.permDueDates === this.cService.permLevels.TRUE
            ) {
              transformedData.color = 'orange';
            } else {
              transformedData.color = mapping.color;
            }
            break;
          }
        }
      }
    }

    return transformedData;
  }

  convertToAgColumnDefinition(structure: any, reportName?: string): Array<ColDef> {
    const columnNames = Object.keys(structure);
    const agGridColumnDef: Array<ColDef> = [];

    const ratingsDataMapping = {
      red: this.cService.readyTranslations.reportData.high,
      Red: this.cService.readyTranslations.reportData.high,
      yellow: this.cService.readyTranslations.reportData.medium,
      Yellow: this.cService.readyTranslations.reportData.medium,
      green: this.cService.readyTranslations.reportData.low,
      Green: this.cService.readyTranslations.reportData.low,

      low: this.cService.readyTranslations.reportData.low,
      Low: this.cService.readyTranslations.reportData.low,
      'low risk': this.cService.readyTranslations.reportData.lowRisk,
      'Low Risk': this.cService.readyTranslations.reportData.lowRisk,
      'very low Risk': this.cService.readyTranslations.reportData.veryLowRisk,
      'Very Low Risk': this.cService.readyTranslations.reportData.veryLowRisk,

      medium: this.cService.readyTranslations.reportData.medium,
      Medium: this.cService.readyTranslations.reportData.medium,
      'medium risk': this.cService.readyTranslations.reportData.mediumRisk,
      'Medium Risk': this.cService.readyTranslations.reportData.mediumRisk,

      high: this.cService.readyTranslations.reportData.high,
      High: this.cService.readyTranslations.reportData.high,
      'high risk': this.cService.readyTranslations.reportData.highRisk,
      'High Risk': this.cService.readyTranslations.reportData.highRisk,
      'very high': this.cService.readyTranslations.reportData.veryHigh,
      'Very High': this.cService.readyTranslations.reportData.veryHigh,
      'very high risk': this.cService.readyTranslations.reportData.veryHighRisk,
      'Very High Risk': this.cService.readyTranslations.reportData.veryHighRisk,
    };
    const directionDataMapping = {
      up: this.cService.readyTranslations.generic.up,
      Up: this.cService.readyTranslations.generic.up,
      down: this.cService.readyTranslations.generic.down,
      Down: this.cService.readyTranslations.generic.down,
      'n/a': this.cService.readyTranslations.generic.notApplicable,
      'N/A': this.cService.readyTranslations.generic.notApplicable,
    };
    const coachingReasonsDataMapping = {
      'Not Available': this.cService.readyTranslations.reportData.notAvailable,
      ficoLt500Week: this.cService.readyTranslations.reportData.weeklyFicoLessThan500,
      'Weekly FICO Safe Driving Score <559': this.cService.readyTranslations.reportData.weeklyFicoLessThan559,
      'FICO Safe Driving Score between 560 and 709 for two consecutive weeks':
        this.cService.readyTranslations.reportData.ficoSafeScoreBetween560And709,
      'fico560-709TwoWeeks': this.cService.readyTranslations.reportData.ficoSafeScoreBetween560And709,
      driverIndexCoachCoachingReason: this.cService.readyTranslations.reportData.driverIndexCoachCoachingReason,
      pccCoachingReason: this.cService.readyTranslations.reportData.pccCoachingReason,
      driverIndexProCoachingReason: this.cService.readyTranslations.reportData.driverIndexProCoachingReason,
      'Any Driver who had a FICO® Safe Driving Score between 560-709 for each of the last two weeks':
        this.cService.readyTranslations.reportData.ficoSafeScoreBetween560And709,
      'Any Driver who has an FICO® Safe Driving Score between 560-709 for two weeks in a row.':
        this.cService.readyTranslations.reportData.ficoSafeScoreBetween560And709,
    };

    const coachingCreatedByDataMapping = {
      'Coach Auto Assign': this.cService.readyTranslations.reportData.coachAutoAssign,
      'Coaching API Service': this.cService.readyTranslations.reportData.coachingApiService,
    };

    const progressDataMapping = {
      Complete: this.cService.readyTranslations.reportData.complete,
      complete: this.cService.readyTranslations.reportData.complete,
      incomplete: this.cService.readyTranslations.reportData.incomplete,
      Incomplete: this.cService.readyTranslations.reportData.incomplete,
      'Not Started': this.cService.readyTranslations.reportData.notStarted,
      'not Started': this.cService.readyTranslations.reportData.notStarted,
      'Not started': this.cService.readyTranslations.reportData.notStarted,
      'not started': this.cService.readyTranslations.reportData.notStarted,
      Required: this.cService.readyTranslations.reportData.required,
      required: this.cService.readyTranslations.reportData.required,
      Optional: this.cService.readyTranslations.reportData.optional,
      optional: this.cService.readyTranslations.reportData.optional,
      'In Progress': this.cService.readyTranslations.reportData.inProgress,
      'In progress': this.cService.readyTranslations.reportData.inProgress,
      'in progress': this.cService.readyTranslations.reportData.inProgress,
      Closed: this.cService.readyTranslations.reportData.closed,
      closed: this.cService.readyTranslations.reportData.closed,
      NA: this.cService.readyTranslations.reportData.na,
      Na: this.cService.readyTranslations.reportData.na,
      'N/A': this.cService.readyTranslations.reportData.na,
      Current: this.cService.readyTranslations.reportData.current,
      current: this.cService.readyTranslations.reportData.current,
      Archive: this.cService.readyTranslations.reportData.archive,
      archive: this.cService.readyTranslations.reportData.archive,
      Other: this.cService.readyTranslations.reportData.other,
      other: this.cService.readyTranslations.reportData.other,
      'Pre Hire': this.cService.readyTranslations.reportData.preHire,
      'Pre hire': this.cService.readyTranslations.reportData.preHire,
      overdue: this.cService.readyTranslations.reportData.overDue,
      Overdue: this.cService.readyTranslations.reportData.overDue,
      'Not Required': this.cService.readyTranslations.reportData.notRequired,
      'Not required': this.cService.readyTranslations.reportData.notRequired,
      'not Required': this.cService.readyTranslations.reportData.notRequired,
      'not required': this.cService.readyTranslations.reportData.notRequired,
      INITIAL: this.cService.readyTranslations.reportData.initial,
      Initial: this.cService.readyTranslations.reportData.initial,
      RECHECK: this.cService.readyTranslations.reportData.recheck,
      Recheck: this.cService.readyTranslations.reportData.recheck,
      'Re-check': this.cService.readyTranslations.reportData['re-check'],
      'Re-Check': this.cService.readyTranslations.reportData['re-check'],
      'RE-CHECK': this.cService.readyTranslations.reportData['re-check'],
    };
    const statusDataMapping = {
      active: this.cService.readyTranslations.reportData.active,
      Active: this.cService.readyTranslations.reportData.active,
      inactive: this.cService.readyTranslations.reportData.inactive,
      Inactive: this.cService.readyTranslations.reportData.inactive,
      notStarted: this.cService.readyTranslations.reportData.notStarted,
      noCoursesAssigned: this.cService.readyTranslations.reportData.noCoursesAssigned,
      Pending: this.cService.readyTranslations.reportData.pending,
      Suppressed: this.cService.readyTranslations.reportData.suppressed,
      Hold: this.cService.readyTranslations.reportData.hold,
      hold: this.cService.readyTranslations.reportData.hold,
      'Test Account': this.cService.readyTranslations.reportData.testAccount,
      'Pre Hire': this.cService.readyTranslations.reportData.preHire,
      Other: this.cService.readyTranslations.reportData.other,
      'Test account': this.cService.readyTranslations.reportData.hold,
      'test account': this.cService.readyTranslations.reportData.hold,
      archive: this.cService.readyTranslations.reportData.archive,
      Archive: this.cService.readyTranslations.reportData.archive,
      'n/a': this.cService.readyTranslations.reportData.na,

      agree: this.cService.readyTranslations.reportData.agree,
      Agree: this.cService.readyTranslations.reportData.agree,
      disagree: this.cService.readyTranslations.reportData.disagree,
      Disagree: this.cService.readyTranslations.reportData.disagree,
      'In Progress': this.cService.readyTranslations.reportData.inProgress,
      'In progress': this.cService.readyTranslations.reportData.inProgress,
      'in progress': this.cService.readyTranslations.reportData.inProgress,
      'Not Started': this.cService.readyTranslations.reportData.notStarted,
      'Not started': this.cService.readyTranslations.reportData.notStarted,
      'not started': this.cService.readyTranslations.reportData.notStarted,
      'Not Required': this.cService.readyTranslations.reportData.notRequired,
      Required: this.cService.readyTranslations.reportData.required,
      complete: this.cService.readyTranslations.reportData.complete,
      completed: this.cService.readyTranslations.reportData.complete,
      Complete: this.cService.readyTranslations.reportData.complete,
      incomplete: this.cService.readyTranslations.reportData.incomplete,
      Incomplete: this.cService.readyTranslations.reportData.incomplete,
      na: this.cService.readyTranslations.reportData.na,
      NA: this.cService.readyTranslations.reportData.na,
      Na: this.cService.readyTranslations.reportData.na,

      Overdue: this.cService.readyTranslations.reportData.overDue,
      overdue: this.cService.readyTranslations.reportData.overDue,

      Yes: this.cService.readyTranslations.reportData.yes,
      yes: this.cService.readyTranslations.reportData.yes,
      No: this.cService.readyTranslations.reportData.no,
      no: this.cService.readyTranslations.reportData.no,
      TRUE: this.cService.readyTranslations.reportData.yes,
      True: this.cService.readyTranslations.reportData.yes,
      true: this.cService.readyTranslations.reportData.yes,
      FALSE: this.cService.readyTranslations.reportData.no,
      False: this.cService.readyTranslations.reportData.no,
      false: this.cService.readyTranslations.reportData.no,
      Valid: this.cService.readyTranslations.reportData.valid,
      valid: this.cService.readyTranslations.reportData.valid,
      'Not Valid': this.cService.readyTranslations.reportData.notValid,
      Invalid: this.cService.readyTranslations.reportData.notValid,
      invalid: this.cService.readyTranslations.reportData.notValid,
      'See MVR': this.cService.readyTranslations.reportData.seeMvr,
      'see MVR': this.cService.readyTranslations.reportData.seeMvr,
      'see Mvr': this.cService.readyTranslations.reportData.seeMvr,
      'see mvr': this.cService.readyTranslations.reportData.seeMvr,
      'see licence check': this.cService.readyTranslations.reportData.seeLicenceCheck,
      'See Licence Check': this.cService.readyTranslations.reportData.seeLicenceCheck,
      required: this.cService.readyTranslations.reportData.required,
      found: this.cService.readyTranslations.reportData.found,
      Found: this.cService.readyTranslations.reportData.found,
      'Awaiting Review': this.cService.getSingleTranslation(
        'REPORTDATA.awaitingReview',
        ReportStaticValues.awaitingReview
      ),
      Completed: this.cService.getSingleTranslation('REPORTDATA.completed', ReportStaticValues.completed),
      'Report Started': this.cService.getSingleTranslation(
        'REPORTDATA.reportStarted',
        ReportStaticValues.reportStarted
      ),
    };
    const incidentDataMapping = {
      Car: this.cService.readyTranslations.reportData['Car'],
      'SUV / 4x4': this.cService.readyTranslations.reportData['SUV / 4x4'],
      Van: this.cService.readyTranslations.reportData['Van'],
      Motorbike: this.cService.readyTranslations.reportData['Motorbike'],
      Bicycle: this.cService.readyTranslations.reportData['Bicycle'],
      'Large Vehicle (Multi-Drop)': this.cService.readyTranslations.reportData['Large Vehicle (Multi-Drop)'],
      'Tractor Trailer': this.cService.readyTranslations.reportData['Tractor Trailer'],
      'Large Vehicle (Straight)': this.cService.readyTranslations.reportData['Large Vehicle (Straight)'],

      None: this.cService.readyTranslations.reportData['None'],
      Slight: this.cService.readyTranslations.reportData['Slight'],
      Serious: this.cService.readyTranslations.reportData['Serious'],
      Fatal: this.cService.readyTranslations.reportData['Fatal'],

      'Driver: Distraction': this.cService.readyTranslations.reportData['Driver: Distraction'],
      'Driver: Lack of Observation': this.cService.readyTranslations.reportData['Driver: Lack of Observation'],
      'Driver: Fatigue': this.cService.readyTranslations.reportData['Driver: Fatigue'],
      'Driver: Excessive Speed': this.cService.readyTranslations.reportData['Driver: Excessive Speed'],
      'Driver: Lack of Experience / Knowledge / Skill':
        this.cService.readyTranslations.reportData['Driver: Lack of Experience / Knowledge / Skill'],
      'Driver: Driving too Close': this.cService.readyTranslations.reportData['Driver: Driving too Close'],
      'Driver: Failure to Yield': this.cService.readyTranslations.reportData['Driver: Failure to Yield'],
      'Driver: DUI Alcohol / Drugs / Medication':
        this.cService.readyTranslations.reportData['Driver: DUI Alcohol / Drugs / Medication'],
      'Driver: Emotional Breakdown / Road Rage':
        this.cService.readyTranslations.reportData['Driver: Emotional Breakdown / Road Rage'],
      'Other Party: Distraction': this.cService.readyTranslations.reportData['Other Party: Distraction'],
      'Other Party: Lack of Observation':
        this.cService.readyTranslations.reportData['Other Party: Lack of Observation'],
      'Other Party: Fatigue': this.cService.readyTranslations.reportData['Other Party: Fatigue'],
      'Other Party: Excessive Speed': this.cService.readyTranslations.reportData['Other Party: Excessive Speed'],
      'Other Party: Lack of Experience / Knowledge / Skill':
        this.cService.readyTranslations.reportData['Other Party: Lack of Experience / Knowledge / Skill'],
      'Other Party: Driving too Close': this.cService.readyTranslations.reportData['Other Party: Driving too Close'],
      'Other Party: Failure to Yield': this.cService.readyTranslations.reportData['Other Party: Failure to Yield'],
      'Other Party: DUI Alcohol / Drugs/ Medication':
        this.cService.readyTranslations.reportData['Other Party: DUI Alcohol / Drugs/ Medication'],
      'Other Party: Road Rage Attack': this.cService.readyTranslations.reportData['Other Party: Road Rage Attack'],
      'Vehicle Failure: Tires / Brakes / Clutch / Other':
        this.cService.readyTranslations.reportData['Vehicle Failure: Tires / Brakes / Clutch / Other'],
      'Act of Nature': this.cService.readyTranslations.reportData['Act of Nature'],
      'Cause not identified': this.cService.readyTranslations.reportData['Cause not identified'],

      Motorway: this.cService.readyTranslations.reportData['Motorway'],
      'Country / Rural': this.cService.readyTranslations.reportData['Country / Rural'],
      'Town / City': this.cService.readyTranslations.reportData['Town / City'],
      Residential: this.cService.readyTranslations.reportData['Residential'],
      'Off-Road': this.cService.readyTranslations.reportData['Off-Road'],
      Other: this.cService.readyTranslations.reportData['Other'],

      Day: this.cService.readyTranslations.reportData['Day'],
      Dawn: this.cService.readyTranslations.reportData['Dawn'],
      Dusk: this.cService.readyTranslations.reportData['Dusk'],
      'Night (With Lighting)': this.cService.readyTranslations.reportData['Night (With Lighting)'],
      'Night (No Lighting)': this.cService.readyTranslations.reportData['Night (No Lighting)'],

      'Fine / Clear': this.cService.readyTranslations.reportData['Fine / Clear'],
      'Bright Sun': this.cService.readyTranslations.reportData['Bright Sun'],
      'Dull / Overcast': this.cService.readyTranslations.reportData['Dull / Overcast'],
      'Fog / Mist': this.cService.readyTranslations.reportData['Fog / Mist'],
      'Hail / Sleet': this.cService.readyTranslations.reportData['Hail / Sleet'],
      'Light Rain': this.cService.readyTranslations.reportData['Light Rain'],
      'Heavy Rain': this.cService.readyTranslations.reportData['Heavy Rain'],
      'Light Snow': this.cService.readyTranslations.reportData['Light Snow'],
      'Heavy Snow': this.cService.readyTranslations.reportData['Heavy Snow'],
      Ice: this.cService.readyTranslations.reportData['Ice'],
      'Light Wind': this.cService.readyTranslations.reportData['Light Wind'],
      'Strong Wind': this.cService.readyTranslations.reportData['Strong Wind'],
      'Hurricane / Tornado': this.cService.readyTranslations.reportData['Hurricane / Tornado'],
      Flood: this.cService.readyTranslations.reportData['Flood'],
      Earthquake: this.cService.readyTranslations.reportData['Earthquake'],

      Excellent: this.cService.readyTranslations.reportData['Excellent'],
      Good: this.cService.readyTranslations.reportData['Good'],
      Satisfactory: this.cService.readyTranslations.reportData['Satisfactory'],
      Poor: this.cService.readyTranslations.reportData['Poor'],
      Dangerous: this.cService.readyTranslations.reportData['Dangerous'],
      'Under Construction': this.cService.readyTranslations.reportData['Under Construction'],

      Congested: this.cService.readyTranslations.reportData['Congested'],
      Flowing: this.cService.readyTranslations.reportData['Flowing'],
      Normal: this.cService.readyTranslations.reportData['Normal'],
      Light: this.cService.readyTranslations.reportData['Light'],

      Airport: this.cService.readyTranslations.reportData['Airport'],
      Driveway: this.cService.readyTranslations.reportData['Driveway'],
      Junction: this.cService.readyTranslations.reportData['Junction'],
      'Car Park': this.cService.readyTranslations.reportData['Car Park'],
      'Retail Park': this.cService.readyTranslations.reportData['Retail Park'],
      'Road / Street': this.cService.readyTranslations.reportData['Road / Street'],
      Roundabout: this.cService.readyTranslations.reportData['Roundabout'],

      Forward: this.cService.readyTranslations.reportData['Forward'],
      Reverse: this.cService.readyTranslations.reportData['Reverse'],
      Stationary: this.cService.readyTranslations.reportData['Stationary'],
      Parked: this.cService.readyTranslations.reportData['Parked'],

      Accelerating: this.cService.readyTranslations.reportData['Accelerating'],
      Braking: this.cService.readyTranslations.reportData['Braking'],
      'Changing Lanes': this.cService.readyTranslations.reportData['Changing Lanes'],
      Reversing: this.cService.readyTranslations.reportData['Reversing'],
      Parking: this.cService.readyTranslations.reportData['Parking'],
      'Overtaking / Being Overtaken': this.cService.readyTranslations.reportData['Overtaking / Being Overtaken'],
      Turning: this.cService.readyTranslations.reportData['Turning'],
      'U-Turn': this.cService.readyTranslations.reportData['U-Turn'],

      'To / From Work': this.cService.readyTranslations.reportData['To / From Work'],
      Work: this.cService.readyTranslations.reportData['Work'],
      Personal: this.cService.readyTranslations.reportData['Personal'],

      '0 - 1 hr': this.cService.readyTranslations.reportData['0 - 1 hr'],
      '1 - 2 hr': this.cService.readyTranslations.reportData['1 - 2 hr'],
      '2 - 3 hr': this.cService.readyTranslations.reportData['2 - 3 hr'],
      '3 - 4 hr': this.cService.readyTranslations.reportData['3 - 4 hr'],
      '4+ hrs': this.cService.readyTranslations.reportData['4+ hrs'],

      Fatigued: this.cService.readyTranslations.reportData['Fatigued'],
      Aggressive: this.cService.readyTranslations.reportData['Aggressive'],
      'Under the influence of alcohol or drugs':
        this.cService.readyTranslations.reportData['Under the influence of alcohol or drugs'],
      Distracted: this.cService.readyTranslations.reportData['Distracted'],

      Speeding: this.cService.readyTranslations.reportData['Speeding'],
      'Crash / Collision / Incident': this.cService.readyTranslations.reportData['Crash / Collision / Incident'],
      'Licence Violation': this.cService.readyTranslations.reportData['Licence Violation'],
      'Near Miss': this.cService.readyTranslations.reportData['Near Mis'],

      private: this.cService.readyTranslations.reportData['private'],
      company_pool_or_utility: this.cService.readyTranslations.reportData['company_pool_or_utility'],
      company_hire_or_rental: this.cService.readyTranslations.reportData['company_hire_or_rental'],
      company_owned_or_leased: this.cService.readyTranslations.reportData['company_owned_or_leased'],
      tool_of_trade: this.cService.readyTranslations.reportData['tool_of_trade'],
      benefit_vehicle: this.cService.readyTranslations.reportData['benefit_vehicle'],
      benefit_cash: this.cService.readyTranslations.reportData['benefit_cash'],
    };

    const driverDetailsQuestionaireAnswerMappings = {
      pfz_isa_ins_type1: this.cService.readyTranslations.reportData.pfz_isa_ins_type1,
      pfz_isa_ins_type2: this.cService.readyTranslations.reportData.pfz_isa_ins_type2,
      pfz_isa_ins_type3: this.cService.readyTranslations.reportData.pfz_isa_ins_type3,
      pfz_isa_ins_type4: this.cService.readyTranslations.reportData.pfz_isa_ins_type4,
    };

    this.translation.get('REPORTCOLUMNS').subscribe({
      next: (translate) => {
        columnNames.forEach((col: string) => {
          if (col) {
            const defaultColumnWidth = 80;
            const colHeaderName: string = structure[col].name;
            const colHeaderTooltipTag = structure[col].headerTooltip ? structure[col].headerTooltip : `${col}Tooltip`;

            const translatedColName: string = translate[colHeaderName] ? translate[colHeaderName] : colHeaderName;
            const translatedColHeaderTooltip: string =
              colHeaderTooltipTag && translate[colHeaderTooltipTag]
                ? translate[colHeaderTooltipTag]
                : translatedColName;

            const colHeaderIcon = this.iconHeaderColumns.find((headerIconCol: { id: string; iconPath: string }) =>
              col.includes(headerIconCol.id)
            );

            let headerColImage = '';
            if (reportName && colHeaderIcon) {
              if (this.iconHeaderReportList.includes(reportName.toLowerCase())) {
                headerColImage = `<img src="${colHeaderIcon.iconPath}" class="report-header-icon" />`;
              }
            }

            const calculatedMinColWidth = this.cService.predictMinWidthByColName(translatedColName);

            const noPersistedSort = this.gridState.sortModel.length === 0;
            let synthBackendSortIndicator = '';
            let synthSortApplicableColId = '';

            // Show sort icon against back-end's default sort
            if (noPersistedSort && structure[col].defaultSort) {
              let sortDirectionIconClass: string =
                structure[col].defaultSort.direction == 'desc' ? 'ag-desc-sort-icon' : 'ag-asc-sort-icon';

              const columnHasLimitedWidth: SpecificColWidthSetting = this.agGridConfig.specificColWidth.find(
                (colWidth) => colWidth.columnId.toLowerCase() === col.toLowerCase()
              );

              if (col !== ColIdEnum.firstName && col !== ColIdEnum.lastName && columnNames.length > 10) {
                const potentialColWidth =
                  calculatedMinColWidth && defaultColumnWidth > calculatedMinColWidth
                    ? defaultColumnWidth
                    : calculatedMinColWidth;

                if ((columnHasLimitedWidth && columnHasLimitedWidth.width < 120) || potentialColWidth < 120) {
                  sortDirectionIconClass = `${sortDirectionIconClass} apply-less-space`;
                }
              }

              synthBackendSortIndicator = `<i class="${sortDirectionIconClass} dummy-sort-indicator"></i>`;
              synthSortApplicableColId = col;
            }

            const colSpan = structure[col].hasOwnProperty('colSpan') ? structure[col].colSpan : null;
            const cellStyle = structure[col].hasOwnProperty('cellStyle') ? structure[col].cellStyle : null;

            let column: ColDef = {
              headerName:
                colHeaderName && colHeaderName.toLowerCase() === 'pin'
                  ? translatedColName.toUpperCase()
                  : translatedColName,
              field: col,
              colId: col,
              filter: !structure[col]['disableFilter'] ?? true,
              enableRowGroup: false,
              colSpan: colSpan,
              cellStyle: cellStyle,
              filterParams: { clearButton: true },
              headerComponentParams: {
                template: `
                <div class="ag-cell-label-container ag-tooltip-trigger" role="presentation" title="${translatedColHeaderTooltip}">
                  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>

                  <div ref="eLabel" class="ag-header-cell-label" role="presentation">

                    ${headerColImage}
                    <span ref="eText" class="ag-header-cell-text" role="columnheader"></span>

                    <span ref="eSortOrder" class="ag-header-icon ag-sort-order" ></span>
                    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon" ></span>
                    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon" ></span>
                    ${col === synthSortApplicableColId ? synthBackendSortIndicator : ''}
                    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon" ></span>

                    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
                  </div>
                </div>
                `,
              },
            };

            if (structure[col].benchmarkColspan) {
              column.colSpan = (params: ColSpanParams) => {
                let spanLength = 1;

                if (['benchmark', 'total'].includes(params.data.firstName?.toLowerCase())) {
                  spanLength = structure[col].benchmarkColspan;
                }

                return spanLength;
              };
            }

            if (structure[col].chartDataType) {
              column.chartDataType = structure[col].chartDataType;
            }

            if (this.currentReportMeta && this.currentReportMeta.usesServerSideRendering) {
              if (structure[col].disableSorting) {
                column.sortable = false;
              }
            }

            // Set master column defaults
            if (structure[col].master) {
              if (!this.cService.isMobileViewport && !this.cService.selectedLanguage.isRtl) {
                column.pinned = 'left';
                column.cellClass = 'lock-pinned';
              }
              if (structure[col].headerSelection) {
                column.headerCheckboxSelection = true;
              }

              column.checkboxSelection = true;
              column.lockPosition = true;
            }
            if (structure[col].pinned) {
              if (!this.cService.isMobileViewport && !this.cService.selectedLanguage.isRtl) {
                column.pinned = 'left';
                column.cellClass = 'lock-pinned';
              }

              column.lockPosition = true;
            }
            if (col.toLowerCase() === ColIdEnum.pin) {
              column.lockPosition = true;
            }
            if (structure[col].type && structure[col].type.toLowerCase() === VrmColumnTypes.file) {
              column.cellRenderer = 'fileExtToIconAndLinkButtonComponent';
            }

            // Hide / persist hidden columns
            const columnHidden = this.gridState.hiddenColumns.find((hiddenCol) => hiddenCol === col);
            if (columnHidden) {
              column.hide = true;
            }

            const apiVisibilityOverridden = this.gridState.overrideAPIColumnsVisibility.find(
              (overriddenColumn) => overriddenColumn === col
            );
            if (apiVisibilityOverridden) {
              column.hide = false;
            }

            // Persist existing sort
            const existingColumnSort: ColumnSortModel = this.gridState.sortModel.find(
              (sortModel: ColumnSortModel) => sortModel.colId === col
            );
            if (existingColumnSort) {
              column.sort = existingColumnSort.sort;
            }

            if (!structure[col]['disableFilter'] ?? true) {
              if (
                structure[col].type &&
                (structure[col].type.toLowerCase() === VrmColumnTypes.text ||
                  structure[col].type.toLowerCase() === VrmColumnTypes.email ||
                  structure[col].type.toLowerCase() === VrmColumnTypes.image ||
                  structure[col].type.toLowerCase() === VrmColumnTypes.file)
              ) {
                if (this.currentReportMeta && this.currentReportMeta.usesServerSideRendering) {
                  column.filter = 'agSetColumnFilter';

                  // Determine filter type
                  if (
                    (structure[col].distinctValues && structure[col].distinctValues.length) ||
                    structure[col].usesTextFilter
                  ) {
                    if (structure[col].distinctValues && structure[col].distinctValues.length) {
                      column.filterParams = {
                        values: structure[col].distinctValues,
                      };
                    } else {
                      if (structure[col].usesTextFilter) {
                        column.filter = 'agTextColumnFilter';
                      }
                    }
                  } else {
                    column.filterParams = {
                      values: (fParams: any) => {
                        this.getAsyncFilterValuesFromServer(col).subscribe({
                          next: (asyncFilterValues: Array<string>) => {
                            fParams.success(asyncFilterValues);
                          },
                        });
                      },
                    };
                  }
                }
              } else {
                // Apply distinct filter values if provided still
                if (structure[col].distinctValues && structure[col].distinctValues.length) {
                  column.filter = 'agSetColumnFilter';
                  column.filterParams = {
                    values: structure[col].distinctValues,
                  };
                }
              }
            }

            // Apply percentage transformations
            if (structure[col].type && structure[col].type.toLowerCase() === VrmColumnTypes.percentage) {
              column.cellRenderer = 'numberToPercentageComponent';

              // NOTE: Tagging this as numericColumn helps agGrid to align it right.
              // column.type = 'numericColumn';

              // Be graceful to reports with width set independently
              if (!structure[col].width) {
                const colWidth =
                  defaultColumnWidth > calculatedMinColWidth ? defaultColumnWidth : calculatedMinColWidth;

                column.width = colWidth;
              }
            }

            // Apply number type transformations
            if (
              structure[col].type &&
              (structure[col].type.toLowerCase() === VrmColumnTypes.number ||
                structure[col].type.toLowerCase() === VrmColumnTypes.decimal)
            ) {
              if (structure[col].type.toLowerCase() === VrmColumnTypes.decimal) {
                column.cellRenderer = 'numberToDecimalPlaceComponent';
              }

              column.aggFunc = 'Sum';
              column.enableValue = true;
              column.sortingOrder = ['desc', 'asc', null];

              // NOTE: Tagging this as numericColumn helps agGrid to align it right.
              // column.type = 'numericColumn';
              column.filter = 'agNumberColumnFilter';

              // Be graceful to reports with width set independently
              if (!structure[col].width) {
                const colWidth =
                  defaultColumnWidth > calculatedMinColWidth ? defaultColumnWidth : calculatedMinColWidth;

                column.width = colWidth;
              }
            }

            if (
              structure[col].name &&
              structure[col].name.toLowerCase().includes('change last') &&
              structure[col].name.toLowerCase().includes('month')
            ) {
              column.cellRenderer = 'prefixPlusSignToPositiveNumComponent';
            }

            if (
              col.toLowerCase() === ColIdEnum.co2Savings.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.ecoComparison.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.totalMovementTripHours.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.totalTripMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.totalTripKilometers.toLowerCase()
            ) {
              if (
                [VrmColumnTypes.number, VrmColumnTypes.percentage, VrmColumnTypes.decimal].includes(
                  structure[col].type.toLowerCase()
                )
              ) {
                column.filterValueGetter = (params) => {
                  return !isNaN(params.data[col])
                    ? parseFloat(params.data[col])
                    : this.cService.getSingleTranslation('REPORTDATA.' + params.data[col], params.data[col]);
                };

                column.comparator = (valueA, valueB, nodeA, nodeB, isInverted) => {
                  valueA = !isNaN(valueA) ? parseFloat(valueA) : -9999;
                  valueB = !isNaN(valueB) ? parseFloat(valueB) : -9999;
                  return valueA > valueB ? 1 : -1;
                };
              }
            }

            if (structure[col].columnDefinition) {
              const matchDataParameter = new RegExp(/:(\w+)/, 'g');
              if (structure[col].columnDefinition.valueGetter) {
                column.valueGetter = (params) => {
                  let value = structure[col].columnDefinition.valueGetter;
                  let match = null;
                  const matcher = value.matchAll(matchDataParameter);
                  while ((match = matcher.next())) {
                    if (match.done) {
                      break;
                    }
                    const paramTag = match.value != null ? match.value[0] : null;
                    const paramName = match.value != null ? match.value[1] : null;
                    const replaceValue =
                      paramName != null && params.data[paramName] != null ? params.data[paramName] : '';
                    value = value.replace(paramTag, replaceValue);
                  }

                  if (value.toUpperCase() === 'NA' || value.toUpperCase === 'N/A') {
                    value = this.cService.readyTranslations.reportData.na;
                  }

                  return value;
                };
              }

              if (structure[col].columnDefinition.filterValueGetter) {
                column.filterValueGetter = (params) => {
                  const value = structure[col].columnDefinition.filterValueGetter;
                  const match = value.matchAll(matchDataParameter).next().value;
                  const paramTag = match != null ? match[0] : null;
                  const paramName = match != null ? match[1] : null;
                  return parseFloat(params.data[paramName]);
                };
              }

              if (structure[col].columnDefinition.cellClass) {
                column.cellClass = (params) => {
                  const value = structure[col].columnDefinition.cellClass;
                  const match = value.matchAll(matchDataParameter).next().value;
                  const paramTag = match != null ? match[0] : null;
                  const paramName = match != null ? match[1] : null;

                  let colorMetaData = match ? params.data[paramName] : value;
                  let mapping = {
                    red: 'agGridRedTexts',
                    green: 'agGridGreenTexts',
                    orange: 'agGridAmberTexts',
                    amber: 'agGridAmberTexts',
                    black: 'agGridBlackTexts',
                    grey: 'agGridGreyTexts',
                    'red-cell': 'sev-red',
                    'amber-cell': 'sev-amber',
                  };
                  return mapping[colorMetaData] ?? '';
                };
              }

              if (structure[col].columnDefinition.comparator) {
                column.comparator = (valueA, valueB, nodeA, nodeB, isInverted) => {
                  const value = structure[col].columnDefinition.comparator;
                  const match = value.matchAll(matchDataParameter).next().value;
                  const paramTag = match != null ? match[0] : null;
                  const paramName = match != null ? match[1] : null;
                  return parseFloat(nodeA.data[paramName]) > parseFloat(nodeB.data[paramName]) ? 1 : -1;
                };
              }

              if (structure[col].columnDefinition.width) {
                column.width = structure[col].columnDefinition.width;
              }
            }

            if (
              col.toLowerCase() === ColIdEnum.accelerationRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.brakingRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.corneringRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distractionRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.speedRating.toLowerCase()
            ) {
              if (structure[col].type.toLowerCase() === VrmColumnTypes.number) {
                column.sortingOrder = ['asc', 'desc', null];
                column.cellRenderer = 'maskPoorAverageGoodRatingComponent';
                column.filter = 'customDrivingRatingFilterComponent';
              }
            }

            if (
              col.toLowerCase() === ColIdEnum.companyRiskLevel.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.indexingPointsValue.toLowerCase()
            ) {
              if (structure[col].type.toLowerCase() === VrmColumnTypes.number) {
                column.sortingOrder = ['desc', 'asc', null];
                column.cellRenderer = 'maskCompanyRiskLevelPointsComponent';
                column.filter = 'customCompanyRiskFilterComponent';
              }
            }

            if (col.toLowerCase() === ColIdEnum.companyPointsScore.toLowerCase()) {
              if (structure[col].type.toLowerCase() === VrmColumnTypes.number) {
                column.sortingOrder = ['desc', 'asc', null];
                column.cellRenderer = 'maskCompanyRiskLevelScoreComponent';
              }
            }

            if (
              col.toLowerCase() === ColIdEnum.partialIndexRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.rating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.profileDriver.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.defensiveDriving.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.attitudeRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.knowledgeRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.behaviourRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hazardRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.profileRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.driverRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.journeyRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.operatorRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.warehouseRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.defensiveOperating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.vehicleRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.ratingTo.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.ratingFrom.toLowerCase()
            ) {
              if (structure[col].type.toLowerCase() === VrmColumnTypes.number) {
                column.cellRenderer = 'maskStandardHighMedLowRatingsComponent';
                column.filter = 'customStandardHighMedLowRatingComponent';
              }
            }

            if (col.toLowerCase() === ColIdEnum.licenceCategories.toLowerCase()) {
              column.cellRenderer = 'licenceCategoriesTooltipsComponent';
            }

            if (structure[col].cellStaticTooltipField) {
              column.cellRenderer = 'cellStaticTooltipsComponent';
              column.cellRendererParams = { tooltipIDField: structure[col].cellStaticTooltipField };
              if (structure[col].columnDefinition?.cellClass) {
                column.cellRendererParams['cellClassField'] = structure[col].columnDefinition.cellClass.replace(
                  ':',
                  ''
                );
              }
            }

            if (
              col.toLowerCase() === ColIdEnum.mvrSvcCodes.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.mvrLicenseClass.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.endorsements.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.licenceType.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.cardStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.mvrStatus.toLowerCase()
            ) {
              column.cellRenderer = 'cellTooltipsComponent';
            }

            if (
              col.toLowerCase() === ColIdEnum.riskFoundationSection1.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.riskFoundationSection2.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.riskFoundationSection3.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.riskFoundationSection4.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.alternativeRiskFoundationSection1.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.alternativeRiskFoundationSection2.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.alternativeRiskFoundationSection3.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.alternativeRiskFoundationSection4.toLowerCase()
            ) {
              column.cellRenderer = 'composedCourseStatusComponent';
            }

            if (
              col.toLowerCase() === ColIdEnum.indexingCoach.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.driverIndexCoachStatus.toLowerCase()
            ) {
              column.cellRenderer = 'gridIndexingCoachLinkButtonComponent';
            } else if (col.toLowerCase() === ColIdEnum.oneToOneStatus.toLowerCase()) {
              column.cellRenderer = 'gridSeeOneToOneLinkButtonComponent';
            } else if (col.toLowerCase() === ColIdEnum.indexingLicenceStatus.toLowerCase()) {
              column.cellRenderer = 'gridSeeMvrLinkButtonComponent';
            } else if (col.toLowerCase() === ColIdEnum.dvlaLicenceStatus.toLowerCase()) {
              column.cellRenderer = 'gridSeeLicenceCheckLinkButton';
            } else if (col.toLowerCase() === ColIdEnum.orderMvr.toLowerCase()) {
              column.cellRenderer = 'gridOrderMvrLinkButtonComponent';
            } else if (col.toLowerCase() === ColIdEnum.alertActionRequired.toLowerCase()) {
              column.cellRenderer = 'gridAlertActionRequiredButtonComponent';
            } else if (col.toLowerCase() === ColIdEnum.downloadCustomReport.toLowerCase()) {
              column.cellRenderer = 'gridCustomReportDownloadButtonComponent';
            } else if (col.toLowerCase() === ColIdEnum.pccCourseStatus.toLowerCase()) {
              column.cellRenderer = 'gridSeePccLinkButtonComponent';
            } else if (col.toLowerCase() === ColIdEnum.virtualSafetyDiscussionStatus.toLowerCase()) {
              column.cellRenderer = 'gridSeeVirtualSafetyDiscussionLinkButtonComponent';
            } else if (col.toLowerCase() === ColIdEnum.coachingActions.toLowerCase()) {
              column.filter = false;
              column.cellRenderer = 'coachingRecommendationActionsComponent';
            } else if (col.toLowerCase() === ColIdEnum.shortTrip.toLowerCase()) {
              column.cellRenderer = 'gridShortTripFlagComponent';
            }

            // Apply and overwrite cellRenderer
            // TODO: Possibly remove, can't see any uses in backend API
            if (structure[col].cellRenderer && typeof structure[col].cellRenderer === 'string') {
              column.cellRenderer = structure[col].cellRenderer;
            }

            if (structure[col].cellRenderer && typeof structure[col].cellRenderer === 'object') {
              column.cellRenderer = structure[col].cellRenderer.name;
            }

            // Apply date type transformations
            if (structure[col].type && structure[col].type.toLowerCase() === VrmColumnTypes.date) {
              // Be graceful to reports with width set independently
              if (!structure[col].width) {
                const colWidth =
                  defaultColumnWidth + 20 > calculatedMinColWidth ? defaultColumnWidth + 20 : calculatedMinColWidth;

                column.width = colWidth;
              }

              column.filter = 'agDateColumnFilter';
              column.filterParams = {
                comparator: (filterLocalDateAtMidnight: number | any, cellValue: string) => {
                  let finalDate: number = -1;

                  if (cellValue && typeof cellValue === 'string') {
                    const dateAsString = cellValue.trim().split(' ')[0];

                    if (dateAsString) {
                      const dateParts = dateAsString.includes(`/`) ? dateAsString.split('/') : dateAsString.split('-');
                      const cellDate = new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]));

                      if (filterLocalDateAtMidnight.getTime() == cellDate.getTime()) {
                        finalDate = 0;
                      } else if (cellDate < filterLocalDateAtMidnight) {
                        finalDate = -1;
                      } else if (cellDate > filterLocalDateAtMidnight) {
                        finalDate = 1;
                      }
                    }
                  }

                  return finalDate;
                },

                browserDatePicker: true,
                clearButton: true,
              };
            }

            // Process specific width recommendations
            // NOTE: This must come after setting generic width for all numeric / percentage columns
            this.agGridConfig.specificColWidth.forEach((columnSetting: SpecificColWidthSetting) => {
              if (col.toLowerCase() === columnSetting.columnId.toLowerCase()) {
                let colWidth =
                  columnSetting.width > calculatedMinColWidth ? columnSetting.width : calculatedMinColWidth;

                // Assign width based on priority and report where applicable
                if (columnSetting.prioritize) {
                  colWidth = columnSetting.width;
                } else if (
                  columnSetting.applicableReports &&
                  reportName &&
                  columnSetting.applicableReports.includes(reportName)
                ) {
                  colWidth = columnSetting.width;
                } else {
                  if (columnSetting.columnId && columnSetting.columnId.toLowerCase() === 'email') {
                    colWidth = 0; // unset
                  }
                }

                column.width = colWidth;
              }
            });

            // Add cellClass if there is one
            if (structure[col].cellClassRules) {
              column.cellClassRules = structure[col].cellClassRules;
            }

            // Add hide attribute if there is in structure
            if (structure[col].hide && !apiVisibilityOverridden) {
              column.hide = structure[col].hide;
            }

            // Add width attribute if there is in structure
            if (structure[col].width) {
              column.width = structure[col].width;
            }

            if (structure[col].shaded) {
              column.cellClass = 'agGridShadedBackground';
            }

            // Transform cell data
            if (
              col.toLowerCase() === ColIdEnum.indexingRiskCoach.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.indexingRiskCoachPlus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.partialIndexRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.profileRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.driverRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.journeyRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.operatorRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.warehouseRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.defensiveOperating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.vehicleRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.mvrRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.overallRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.ratingTo.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.ratingFrom.toLowerCase()
            ) {
              column.refData = ratingsDataMapping;
            }

            if ([ColIdEnum.driveWork.toLowerCase(), ColIdEnum.vehicleType.toLowerCase()].includes(col.toLowerCase())) {
              column.refData = this.translationsService.translateDataMap(driveWorkDataMap);
            }

            if (col.toLowerCase() === ColIdEnum.direction.toLowerCase()) {
              column.refData = directionDataMapping;
            }

            if (col.toLowerCase().endsWith('_outcome')) {
              column.valueFormatter = (params) =>
                this.translationsService.getDataMapValue(courseOutcomeDataMap, params.value);
            }

            if (
              col.toLowerCase() === ColIdEnum.courseProgress.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.indexingCoach.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.virtualSafetyDiscussionStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.indexingPcc.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.picStatusDt.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.orderStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.orderType.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.consentStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.driverDetails.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.completeStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.driverIndexCoachStatus.toLowerCase()
            ) {
              column.refData = progressDataMapping;
            }

            if (col.toLowerCase() === ColIdEnum.coachingReasons.toLowerCase()) {
              column.valueGetter = (params) => {
                let coachingReason = '';

                if (params && params.data) {
                  coachingReason = this.cService.getSingleTranslation(
                    'REPORTDATA.' + params.data.coachingReasons,
                    params.data.coachingReasons
                  ) as string;
                }

                return coachingReason;
              };
            }

            if (col.toLowerCase() === ColIdEnum.questionDamageDefectOnOtherVehicle.toLowerCase()) {
              column.valueGetter = (params) => {
                let translatedAnswer = '';

                if (params && params.data) {
                  const currentValue = params.data[col];
                  translatedAnswer = this.getTranslationsForMultiValuedColumn(currentValue) as string;
                }

                return translatedAnswer;
              };
            } else if (structure[col].type && this.isColumnToTranslate(col, structure[col].type)) {
              column.valueGetter = (params) => {
                let translatedAnswer = '';

                if (params && params.data) {
                  const currentValue = params.data[col];
                  translatedAnswer = this.cService.getSingleTranslation(
                    'REPORTDATA.' + currentValue,
                    currentValue
                  ) as string;
                }

                return translatedAnswer;
              };
            }

            if (col.toLowerCase() === ColIdEnum.createdBy) {
              column.valueGetter = function (params) {
                let createdBy = '';

                if (params && params.data) {
                  if (coachingCreatedByDataMapping.hasOwnProperty(params.data.createdBy)) {
                    createdBy = coachingCreatedByDataMapping[params.data.createdBy];
                  } else {
                    createdBy = params.data.createdBy;
                  }
                }

                return createdBy;
              };
            }

            if (col.toLowerCase() === ColIdEnum.location.toLowerCase()) {
              column.valueFormatter = (params: { value: string }) => {
                let benchmark = params.value;

                if (params.value && typeof params.value === 'string' && params.value.toLowerCase() === 'benchmark') {
                  benchmark = this.cService.readyTranslations.reportData.benchmark;
                }
                if (params.value && typeof params.value === 'string' && params.value.toLowerCase() === 'no value') {
                  benchmark = this.cService.readyTranslations.reportData.noValue;
                }

                return benchmark;
              };
            }

            if (col.toLowerCase() === ColIdEnum.ratingHigh.toLowerCase()) {
              column.valueGetter = function (params) {
                return `${params.data.ratingHigh} (${params.data.ratingHighPercent}%)`;
              };
              column.filterValueGetter = function (params) {
                return params.data.ratingHigh;
              };
              column.comparator = (valueA, valueB, nodeA, nodeB, isInverted) => {
                return nodeA.data.ratingHighPercent > nodeB.data.ratingHighPercent ? 1 : -1;
              };
              column.cellClass = (params: { value: any }) => {
                return 'agGridRedTexts';
              };
            }

            if (col.toLowerCase() === ColIdEnum.ratingMedium.toLowerCase()) {
              column.valueGetter = function (params) {
                return `${params.data.ratingMedium} (${params.data.ratingMediumPercent}%)`;
              };
              column.filterValueGetter = function (params) {
                return params.data.ratingMedium;
              };
              column.comparator = (valueA, valueB, nodeA, nodeB, isInverted) => {
                return nodeA.data.ratingMediumPercent > nodeB.data.ratingMediumPercent ? 1 : -1;
              };
              column.cellClass = (params: { value: any }) => {
                return 'agGridAmberTexts';
              };
            }

            if (col.toLowerCase() === ColIdEnum.ratingLow.toLowerCase()) {
              column.valueGetter = function (params) {
                return `${params.data.ratingLow} (${params.data.ratingLowPercent}%)`;
              };
              column.filterValueGetter = function (params) {
                return params.data.ratingLow;
              };
              column.comparator = (valueA, valueB, nodeA, nodeB, isInverted) => {
                return nodeA.data.ratingLowPercent > nodeB.data.ratingLowPercent ? 1 : -1;
              };
              column.cellClass = (params: { value: any }) => {
                return 'agGridGreenTexts';
              };
            }

            if (col.toLowerCase() === ColIdEnum.pointsHigh.toLowerCase()) {
              column.valueGetter = function (params) {
                return `${params.data.pointsHigh} (${params.data.pointsHighPercent}%)`;
              };
              column.filterValueGetter = function (params) {
                return params.data.pointsHigh;
              };
              column.comparator = (valueA, valueB, nodeA, nodeB, isInverted) => {
                return nodeA.data.pointsHighPercent > nodeB.data.pointsHighPercent ? 1 : -1;
              };
              column.cellClass = (params: { value: any }) => {
                return 'agGridRedTexts';
              };
            }

            if (col.toLowerCase() === ColIdEnum.pointsMedium.toLowerCase()) {
              column.valueGetter = function (params) {
                return `${params.data.pointsMedium} (${params.data.pointsMediumPercent}%)`;
              };
              column.filterValueGetter = function (params) {
                return params.data.pointsMedium;
              };
              column.comparator = (valueA, valueB, nodeA, nodeB, isInverted) => {
                return nodeA.data.pointsMediumPercent > nodeB.data.pointsMediumPercent ? 1 : -1;
              };
              column.cellClass = (params: { value: any }) => {
                return 'agGridAmberTexts';
              };
            }

            if (col.toLowerCase() === ColIdEnum.pointsLow.toLowerCase()) {
              column.valueGetter = function (params) {
                return `${params.data.pointsLow} (${params.data.pointsLowPercent}%)`;
              };
              column.filterValueGetter = function (params) {
                return params.data.pointsLow;
              };
              column.comparator = (valueA, valueB, nodeA, nodeB, isInverted) => {
                return nodeA.data.pointsLowPercent > nodeB.data.pointsLowPercent ? 1 : -1;
              };
              column.cellClass = (params: { value: any }) => {
                return 'agGridGreenTexts';
              };
            }

            if (col.toLowerCase() === ColIdEnum.averageDriverScore.toLowerCase()) {
              column.cellClass = (params: { data: any }) => {
                let cellDataColour = '';

                if (params.data.relativeDriverScore == 'green') {
                  cellDataColour = 'agGridGreenTexts';
                } else if (params.data.relativeDriverScore == 'red') {
                  cellDataColour = 'agGridRedTexts';
                }

                return cellDataColour;
              };
            }

            if (col.toLowerCase() === ColIdEnum.averageDriverEvents.toLowerCase()) {
              column.cellClass = (params: { data: any }) => {
                let cellDataColour = '';

                if (params.data.relativeDriverEvents == 'green') {
                  cellDataColour = 'agGridGreenTexts';
                } else if (params.data.relativeDriverEvents == 'red') {
                  cellDataColour = 'agGridRedTexts';
                }

                return cellDataColour;
              };
            }

            if (col.toLowerCase() === ColIdEnum.averageCompanyPoints.toLowerCase()) {
              column.cellClass = (params: { data: any }) => {
                let cellDataColour = '';

                if (params.data.relativeCompanyPoints == 'green') {
                  cellDataColour = 'agGridGreenTexts';
                } else if (params.data.relativeCompanyPoints == 'red') {
                  cellDataColour = 'agGridRedTexts';
                }

                return cellDataColour;
              };
            }

            if (
              col.toLowerCase() === ColIdEnum.totalTripCount.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.totalDriverCount.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardAcceleration.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardBraking.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardCornering.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distraction.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.speeding.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.seatBeltOff.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.reverse.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.mpg.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.mpgLastPeriod.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.mpgThisPeriod.toLowerCase()
            ) {
              // Needed to keep the format sent from the backend and support proper filtering and sorting
              column.filterValueGetter = (params) => {
                const value = params.data[col];

                if (!isNaN(value)) {
                  return Number.parseInt(value);
                }

                return value === 'N/A' ? -1 : Number.parseInt(value.replace(/,/g, ''));
              };

              column.comparator = (valueA, valueB, nodeA: RowNode, nodeB: RowNode, isInverted: boolean): number => {
                if (valueA === 'N/A') {
                  return -1;
                }

                if (valueB === 'N/A') {
                  return 1;
                }

                valueA = isNaN(valueA) ? parseInt(valueA.replace(/,/g, '')) : valueA;
                valueB = isNaN(valueB) ? parseInt(valueB.replace(/,/g, '')) : valueB;
                return valueA - valueB;
              };
            }

            if (
              col.toLowerCase() === ColIdEnum.distance.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distanceKms.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distanceDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distanceKmsDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distanceKmsLastPeriod.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distanceLastPeriod.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.overallScoreDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardAccelerationDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardBrakingDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardCorneringDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distractionDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.speedingDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.duration.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardAccelerationPerHundredMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardAccelerationPerMillionMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardAccelerationPerHundredKms.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardAccelerationPerMillionKms.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardAccelerationDeltaKMs.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardAccelerationDeltaMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.accelerationDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardBrakingPerHundredMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardBrakingPerMillionMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardBrakingPerHundredKms.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardBrakingPerMillionKms.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardBrakingDeltaKMs.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardBrakingDeltaMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardCorneringPerHundredMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardCorneringPerMillionMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardCorneringPerHundredKms.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardCorneringPerMillionKms.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardCorneringDeltaKMs.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardCorneringDeltaMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.corneringDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distractionPerHundredMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distractionPerMillionMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distractionPerHundredKms.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distractionPerMillionKms.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distractionDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distractionDeltaKMs.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distractionDeltaMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.speedingPerHundredMilesLastPeriod.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.speedingPerHundredMilesThisPeriod.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.speedingPerHundredKmsLastPeriod.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.speedingPerHundredKmsThisPeriod.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.speedingDeltaKMs.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.speedingDeltaMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.seatBeltOffPerHundredMilesLastPeriod.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.seatBeltOffPerHundredMilesThisPeriod.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.seatBeltOffPerHundredKmsLastPeriod.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.seatBeltOffPerHundredKmsThisPeriod.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.seatBeltOffDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.seatBeltOffDeltaKMs.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.seatBeltOffDeltaMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.reversePerHundredMilesLastPeriod.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.reversePerHundredMilesThisPeriod.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.reversePerHundredKmsLastPeriod.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.reversePerHundredKmsThisPeriod.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.reverseDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.reverseDeltaKMs.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.reverseDeltaMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.mpgDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.phoneCallsPerHundredMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.phoneCallsPerHundredKms.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.phoneManipulationsPerHundredMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.phoneManipulationsPerHundredKms.toLowerCase()
            ) {
              // Needed to keep the format sent from the backend and support proper filtering and sorting
              column.filterValueGetter = (params) => {
                const value = params.data[col];

                if (!isNaN(value)) {
                  return Number.parseFloat(value);
                }

                return value === 'N/A' ? -1 : Number.parseFloat(value.replace(/,/g, ''));
              };
              column.comparator = (valueA, valueB, nodeA, nodeB, isInverted) => {
                if (valueA === 'N/A') {
                  return -1;
                }

                if (valueB === 'N/A') {
                  return 1;
                }

                if (
                  col.toLowerCase() === ColIdEnum.duration.toLowerCase() &&
                  typeof valueA === 'string' &&
                  typeof valueB === 'string'
                ) {
                  valueA = valueA.replace(':', '.');
                  valueB = valueB.replace(':', '.');
                }
                valueA = parseFloat(valueA);
                valueB = parseFloat(valueB);
                return valueA - valueB;
              };
            }

            if (
              col.toLowerCase() === ColIdEnum.status.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.workHoursSet.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.courseStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.notificationStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.tooltipStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.vrmStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.privacy.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.vrmPrivacyStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.mentorPrivacyStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.ersStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.insightPrivacyStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.insightSOSPrivacyStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.altPrivacy.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.pledge.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.altPledge.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.mentorConsent.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.allowMvr.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.requireDocs.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.isActive.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.oneToOneStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.alertSent.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.reportedViaDer.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.schoolZone.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.cellInUse.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.seatBeltInUse.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.reportedToPolice.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.vehicleDrivable.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.question1.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.question2.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.question3.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.question4.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.question5.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_MB.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_JTI_LAUNCH.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_SPOUSE.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_NESTLE_FLT.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_NESTLE_PALLET.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_NESTLE_3METER.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_ROCHE_LAUNCH.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_RNA.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_NESTLEUK_LAUNCH.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_ECOLAB1.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_ECOLAB2.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_LEASEPLAN_FUJI.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.derStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.mentorAppAccess.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.mentorInsightAppAccess.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.previousMonthDistanceTravelledReported.toLowerCase()
            ) {
              column.refData = statusDataMapping;
            }

            if (
              col.toLowerCase() === ColIdEnum.driverInjury.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.passengerInjury.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.injuries.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.immediateCause.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.roadType.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.roadLighting.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.weatherConditions.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.roadConditions.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.trafficConditions.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.crashLocation.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.directionOfTravel.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.driverActivity.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.journeyPurpose.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.timeIntoJourney.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.driverDisposition.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.category.toLowerCase()
            ) {
              column.refData = incidentDataMapping;
            }

            if (col.toLowerCase() === ColIdEnum.question_pfizer_usa_insurance_type.toLowerCase()) {
              column.refData = driverDetailsQuestionaireAnswerMappings;
            }

            if (col.toLowerCase() === ColIdEnum.dsp.toLowerCase()) {
              column.cellRenderer = 'switchCompanyLinkButtonComponent';
            }

            if (
              col.toLowerCase() === ColIdEnum.action.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.actions.toLowerCase()
            ) {
              if (structure[col].type && structure[col].type.toLowerCase() === VrmColumnTypes.image) {
                column.cellRenderer = 'viewImageButtonComponent';
              }

              if (structure[col].type && structure[col].type.toLowerCase() === VrmColumnTypes.file) {
                column.cellRenderer = 'fileExtToIconAndLinkButtonComponent';
              }

              if (structure[col].type && structure[col].type.toLowerCase() === VrmColumnTypes.operations) {
                column.cellRenderer = 'adminRowOperationsComponent';
                column.filter = 'adminRowOperationsFilterComponent';
              }
            }

            if (col.toLowerCase() === ColIdEnum.station.toLowerCase()) {
              column.valueFormatter = (params: { value: string }) => {
                let allStations = params.value;

                if (params.value && typeof params.value === 'string') {
                  if (params.value.toLowerCase() === 'all stations') {
                    allStations = this.cService.readyTranslations.reportData.allStations;
                  }
                }

                return allStations;
              };
            }

            // Cell data color configuration
            // NOTE: Data export also maps to suggested texts.With cellClass, the
            // assigned text colours are retained when exported
            if (
              col.toLowerCase() === ColIdEnum.realStaffNumber.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location1.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location2.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location3.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location4.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location5.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location6.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location7.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location8.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location9.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location10.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location11.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location12.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location13.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location14.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location15.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location16.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location17.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location18.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location19.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.location20.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.pin.toLowerCase()
            ) {
              column.cellClass = (params: { value: any }) => {
                return 'agGridForceNumberToString';
              };
            }

            if (col.toLowerCase() === ColIdEnum.requireDocs.toLowerCase()) {
              column.cellClass = (params: { value: any }) => {
                let cellDataColour = '';

                if (typeof params.value === 'string') {
                  if (params.value.toLowerCase() === 'yes') {
                    cellDataColour = 'agGridRedTexts';
                  } else if (params.value.toLowerCase() === 'no') {
                    cellDataColour = 'agGridGreenTexts';
                  }
                }

                return cellDataColour;
              };
            }

            if (col.toLowerCase() === ColIdEnum.openCoachingInPopup.toLowerCase()) {
              column.cellRenderer = 'gridViewPopupFormButtonComponent';
            }

            if (
              col.toLowerCase() === ColIdEnum.exportDERSubmission.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.exportIDDReport.toLowerCase()
            ) {
              column.cellRenderer = 'gridDownloadReportFileButtonComponent';
            }

            if (col.toLowerCase() === ColIdEnum.claimStatus.toLowerCase()) {
              column.cellRenderer = 'gridViewCreateClaimButtonComponent';
            }

            if (
              col.toLowerCase() === ColIdEnum.accelerationRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.brakingRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.corneringRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distractionRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.speedRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.status.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.workHoursSet.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.courseStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.overallRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.privacy.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.vrmPrivacyStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.mentorPrivacyStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.ersStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.insightPrivacyStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.insightSOSPrivacyStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.policy.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.altPrivacy.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.pledge.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.altPledge.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.alertSent.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.mentorConsent.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.allowMvr.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.isActive.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.indexingLicenceStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.dvlaLicenceStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.completeStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.consentStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.omsStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.omsSect1.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.omsSect2.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.omsSect3.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.omsSect4.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.omsSect5.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.omsSect6.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.omsSect7.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.omsSect8.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.outcome.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.alertResolvedStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.severity.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.oneToOneStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.pccCourseStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.derStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.riskRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.driverUpToDate.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.driverDetails.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.licenceVerified.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.mvrLicenseStatusDescription.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.driveWork.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.mentorAppAccess.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.mentorInsightAppAccess.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.previousMonthDistanceTravelledReported.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_MB.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_JTI_LAUNCH.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_SPOUSE.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_NESTLE_FLT.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_NESTLE_PALLET.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_NESTLE_3METER.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_ROCHE_LAUNCH.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_RNA.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_NESTLEUK_LAUNCH.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_ECOLAB1.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_ECOLAB2.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_LEASEPLAN_FUJI.toLowerCase()
            ) {
              column.cellClass = (params: { value: any }) => {
                let cellDataColour = '';

                if (typeof params.value === 'string') {
                  if (
                    params.value.toLowerCase() === ReportStaticValues.good ||
                    params.value.toLowerCase() === ReportStaticValues.great ||
                    params.value.toLowerCase() === ReportStaticValues.complete ||
                    params.value.toLowerCase() === ReportStaticValues.valid ||
                    params.value.toLowerCase() === ReportStaticValues.active ||
                    params.value.toLowerCase() === ReportStaticValues.agree ||
                    params.value.toLowerCase() === ReportStaticValues.yes ||
                    params.value.toLowerCase() === ReportStaticValues.optional ||
                    params.value.toLowerCase() === ReportStaticValues.low ||
                    params.value.toLowerCase() === ReportStaticValues.lowRisk ||
                    params.value.toLowerCase() === ReportStaticValues.resolved ||
                    params.value.toLowerCase() === ReportStaticValues.veryLowRisk ||
                    params.value.toLowerCase() === ReportStaticValues.view ||
                    params.value.toLowerCase() === ReportStaticValues.notRequired ||
                    params.value.toLowerCase() === ReportStaticValues.found ||
                    params.value.toLowerCase() === ReportStaticValues.completed
                  ) {
                    cellDataColour = 'agGridGreenTexts';
                  } else if (
                    params.value.toLowerCase() === ReportStaticValues.average ||
                    params.value.toLowerCase() === ReportStaticValues.partial ||
                    params.value.toLowerCase() === ReportStaticValues.incomplete ||
                    params.value.toLowerCase() === ReportStaticValues.hold ||
                    params.value.toLowerCase() === ReportStaticValues.seeMvr ||
                    params.value.toLowerCase() === ReportStaticValues.seeLicenceCheck ||
                    params.value.toLowerCase() === ReportStaticValues.medium ||
                    params.value.toLowerCase() === ReportStaticValues.mediumRisk ||
                    params.value.toLowerCase() === ReportStaticValues.inProgress ||
                    params.value.toLowerCase() === ReportStaticValues.awaitingReview ||
                    (params.value.toLowerCase() === ReportStaticValues.notStarted &&
                      this.cService.userProfile?.userPermissions?.permDueDates === this.cService.permLevels.TRUE)
                  ) {
                    cellDataColour = 'agGridAmberTexts';
                  } else if (
                    params.value.toLowerCase() === ReportStaticValues.poor ||
                    params.value.toLowerCase() === ReportStaticValues.disagree ||
                    params.value.toLowerCase() === ReportStaticValues.no ||
                    params.value.toLowerCase() === ReportStaticValues.notValid ||
                    params.value.toLowerCase() === ReportStaticValues.invalid ||
                    params.value.toLowerCase() === ReportStaticValues.notStarted ||
                    params.value.toLowerCase() === ReportStaticValues.unresolved ||
                    params.value.toLowerCase() === ReportStaticValues.high ||
                    params.value.toLowerCase() === ReportStaticValues.highRisk ||
                    params.value.toLowerCase() === ReportStaticValues.veryHighRisk ||
                    params.value.toLowerCase() === ReportStaticValues.overdue ||
                    params.value.toLowerCase() === ReportStaticValues.notVerified ||
                    params.value.toLowerCase() === ReportStaticValues.required ||
                    params.value.toLowerCase() === ReportStaticValues.requiresAttention ||
                    params.value.toLowerCase() === ReportStaticValues.noCheck ||
                    params.value.toLowerCase() === ReportStaticValues.reportStarted
                  ) {
                    cellDataColour = 'agGridRedTexts';
                  } else if (params.value.toLowerCase() === ReportStaticValues.risky) {
                    cellDataColour = 'agGridBlackTexts';
                  } else if (
                    params.value.toLowerCase() === ReportStaticValues.inactive ||
                    params.value.toLowerCase() === ReportStaticValues.testAccount
                  ) {
                    cellDataColour = 'agGridGreyTexts';
                  }
                }

                return cellDataColour;
              };
            }

            // numeric value coloring
            if (
              col.toLowerCase() === ColIdEnum.daysToOverDue.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_MBdaysToOverDue.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_JTI_LAUNCHdaysToOverDue.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_SPOUSEdaysToOverDue.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_NESTLE_FLTdaysToOverDue.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_NESTLE_PALLETdaysToOverDue.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_NESTLE_3METERdaysToOverDue.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_ROCHE_LAUNCHdaysToOverDue.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_RNAdaysToOverDue.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_NESTLEUK_LAUNCHdaysToOverDue.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_ECOLAB1daysToOverDue.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_ECOLAB2daysToOverDue.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.VID_LEASEPLAN_FUJIdaysToOverDue.toLowerCase()
            ) {
              column.cellClass = (params: { value: any }) => {
                let cellDataColour = '';

                if (typeof params.value === 'number') {
                  if (params.value < 0) {
                    cellDataColour = 'agGridRedTexts';
                  }
                }

                return cellDataColour;
              };
            }

            if (
              col.toLowerCase() === ColIdEnum.courseProgress.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.indexingRiskCoach.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.partialIndexRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.ratingTo.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.ratingFrom.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.indexingCoach.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.virtualSafetyDiscussionStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.indexingRiskCoachPlus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.indexingPcc.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.orderStatus.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.mvrRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.companyRiskLevel.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.indexingPointsValue.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.rating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.profileDriver.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.defensiveDriving.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.attitudeRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.knowledgeRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.behaviourRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hazardRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.profileRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.driverRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.journeyRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.operatorRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.warehouseRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.defensiveOperating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.vehicleRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.driverIndexCoachStatus.toLowerCase()
            ) {
              column.cellClass = (params: { value: any }) => {
                let cellDataColour = '';

                if (typeof params.value === 'string') {
                  if (
                    params.value.toLowerCase() === 'green' ||
                    params.value.toLowerCase() === 'complete' ||
                    params.value.toLowerCase().startsWith('low') ||
                    params.value.toLowerCase() === 'optional'
                  ) {
                    cellDataColour = 'agGridGreenTexts';
                  } else if (
                    params.value.toLowerCase() === 'yellow' ||
                    params.value.toLowerCase().startsWith('medium') ||
                    params.value.toLowerCase() === 'incomplete' ||
                    params.value.toLowerCase() === 'in progress' ||
                    (params.value.toLowerCase() === 'not started' &&
                      this.cService.userProfile &&
                      this.cService.userProfile.userPermissions &&
                      this.cService.userProfile.userPermissions.permDueDates === this.cService.permLevels.TRUE)
                  ) {
                    cellDataColour = 'agGridAmberTexts';
                  } else if (
                    params.value.toLowerCase() === 'red' ||
                    (params.value.toLowerCase() === 'not started' &&
                      this.cService.userProfile &&
                      this.cService.userProfile.userPermissions &&
                      this.cService.userProfile.userPermissions.permDueDates === this.cService.permLevels.FALSE) ||
                    params.value.toLowerCase() === 'high' ||
                    params.value.toLowerCase() === 'very high' ||
                    params.value.toLowerCase().startsWith('high') ||
                    params.value.toLowerCase() === 'required' ||
                    params.value.toLowerCase() === 'not required' ||
                    (params.value.toLowerCase() === 'overdue' &&
                      this.cService.userProfile &&
                      this.cService.userProfile.userPermissions &&
                      this.cService.userProfile.userPermissions.permDueDates === this.cService.permLevels.TRUE)
                  ) {
                    cellDataColour = 'agGridRedTexts';
                  } else if (params.value.toLowerCase().startsWith('safe driver')) {
                    cellDataColour = 'agGridBlueTexts';
                  }
                }

                return cellDataColour;
              };
            }

            if (col.toLowerCase() === ColIdEnum.direction.toLowerCase()) {
              column.cellClass = (params: { value: any }) => {
                let cellDataColour = '';

                if (typeof params.value === 'string') {
                  if (params.value.toLowerCase() === 'up') {
                    cellDataColour = 'agGridRedTexts';
                  } else if (params.value.toLowerCase() === 'down') {
                    cellDataColour = 'agGridGreenTexts';
                  }
                }

                return cellDataColour;
              };
            }

            if (
              col.toLowerCase() === ColIdEnum.expiryDate.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hgvExpiry.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.photocardExpiry.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.bExpiry.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.cExpiry.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.c1Expiry.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.c1eExpiry.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.ceExpiry.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.beExpiry.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.mandateExpiry.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.entitlementExpire.toLowerCase()
            ) {
              column.cellClassRules = {
                agGridGreenTexts: (params: { value: any }) => {
                  let inGreen = false;

                  if (params.value || !isNaN(params.value)) {
                    inGreen = isMoreThan90DaysAhead(params.value);
                  }

                  return inGreen;
                },
                agGridAmberTexts: (params: { value: any }) => {
                  let inAmber = false;

                  if (params.value || !isNaN(params.value)) {
                    inAmber = isBetween30And90DaysAhead(params.value);
                  }

                  return inAmber;
                },
                agGridRedTexts: (params: { value: any }) => {
                  let inRed = false;

                  if (params.value || !isNaN(params.value)) {
                    inRed = isLessThan30DaysAhead(params.value);
                  }

                  return inRed;
                },
              };
            }

            if (
              col.toLowerCase() === ColIdEnum.indexingDiffLastMonth.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.indexingDiffLast3Month.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.indexingDiffLast6Month.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.indexingDiffLast12Month.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.brakingDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardBrakingDeltaKMs.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardBrakingDeltaMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.accelerationDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardAccelerationDeltaKMs.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardAccelerationDeltaMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.corneringDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardCorneringDeltaKMs.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hardCorneringDeltaMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.speedingDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.speedingDeltaKMs.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.speedingDeltaMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distractionDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distractionDeltaKMs.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distractionDeltaMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.reverseDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.seatBeltOffDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.seatBeltOffDeltaKMs.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.seatBeltOffDeltaMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.reverseDeltaKMs.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.reverseDeltaMiles.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.daysOverdue.toLowerCase()
            ) {
              column.cellClassRules = {
                agGridGreenTexts: (params: { value: any }) => {
                  let applyGreenText = false;

                  if (params.value || !isNaN(params.value)) {
                    applyGreenText = Number(params.value) < 0;
                  }

                  return applyGreenText;
                },
                agGridRedTexts: (params: { value: any }) => {
                  let applyRedText = false;

                  if (params.value || !isNaN(params.value)) {
                    applyRedText = Number(params.value) > 0;
                  }

                  return applyRedText;
                },
              };
            }

            if (
              col.toLowerCase() === ColIdEnum.ficoDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.mpgDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distanceKmsDelta.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distanceDelta.toLowerCase()
            ) {
              column.cellClassRules = {
                agGridGreenTexts: (params: { value: any }) => {
                  let greenText = false;

                  if (params.value || !isNaN(params.value)) {
                    greenText = Number(params.value) > 0;
                  }

                  return greenText;
                },
                agGridRedTexts: (params: { value: any }) => {
                  let redText = false;

                  if (params.value || !isNaN(params.value)) {
                    redText = Number(params.value) < 0;
                  }

                  return redText;
                },
              };
            }

            if (
              col.toLowerCase() === ColIdEnum.mandateDaysToExpiry.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.bDaysToExpiry.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.photocardDaysToExpiry.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.c1DaysToExpiry.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.c1eDaysToExpiry.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.cDaysToExpiry.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.ceDaysToExpiry.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.beDaysToExpiry.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hgvDaysToExpiry.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.daysToExpiry.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.entitlementDays.toLowerCase()
            ) {
              column.cellClassRules = {
                agGridGreenTexts: (params: { value: any }) => {
                  let greenText = false;

                  if (params.value || !isNaN(params.value)) {
                    greenText = Number(params.value) > 90;
                  }

                  return greenText;
                },
                agGridRedTexts: (params: { value: any }) => {
                  let redText = false;

                  if (params.value || !isNaN(params.value)) {
                    redText = Number(params.value) <= 30;
                  }

                  return redText;
                },
                agGridAmberTexts: (params: { value: any }) => {
                  let amberText = false;

                  if (params.value || !isNaN(params.value)) {
                    amberText = Number(params.value) > 30 && Number(params.value) <= 90;
                  }

                  return amberText;
                },
              };
            }

            if (col.toLowerCase() === ColIdEnum.station.toLowerCase()) {
              column.cellClass = () => {
                return 'agGridBlackTexts';
              };
            }

            if (
              col.toLowerCase() === ColIdEnum.accelerationRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.brakingRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.corneringRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.distractionRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.speedRating.toLowerCase()
            ) {
              // NOTE: cellClassRules used here so that color applied to
              // cell-reference's text would not be overridden when exporting to Excel
              column.cellClassRules = {
                agGridRedTexts: (params: { value: any }) => {
                  let redText = false;

                  if (typeof params.value === 'number') {
                    redText =
                      params.value >= this.cService.mentorDrivingRatingValues.highRisk.min &&
                      params.value <= this.cService.mentorDrivingRatingValues.highRisk.max;
                  }

                  return redText;
                },
                agGridAmberTexts: (params: { value: number }) => {
                  let amberText = false;

                  if (typeof params.value === 'number') {
                    amberText =
                      params.value >= this.cService.mentorDrivingRatingValues.mediumRisk.min &&
                      params.value <= this.cService.mentorDrivingRatingValues.mediumRisk.max;
                  }

                  return amberText;
                },
                agGridGreenTexts: (params: { value: number }) => {
                  let greenText = false;

                  if (typeof params.value === 'number') {
                    greenText = params.value >= this.cService.mentorDrivingRatingValues.lowRisk.min;
                  }

                  return greenText;
                },
              };
            }

            if (
              col.toLowerCase() === ColIdEnum.companyRiskLevel.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.companyPointsScore.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.indexingPointsValue.toLowerCase()
            ) {
              const companyRiskLevelValues = this.cService.userProfile.companyRiskLevelValues;

              column.cellClassRules = {
                agGridBlueTexts: (params: { value: any }) => {
                  let blueText = false;

                  if (typeof params.value === 'number' && !isNaN(params.value)) {
                    blueText = params.value < companyRiskLevelValues.low;
                  }

                  return blueText;
                },

                agGridGreenTexts: (params: { value: number }) => {
                  let greenText = false;

                  if (typeof params.value === 'number' && !isNaN(params.value)) {
                    greenText =
                      params.value >= companyRiskLevelValues.low && params.value < companyRiskLevelValues.medium;
                  }

                  return greenText;
                },

                agGridAmberTexts: (params: { value: number }) => {
                  let amberText = false;

                  if (typeof params.value === 'number' && !isNaN(params.value)) {
                    amberText =
                      params.value >= companyRiskLevelValues.medium && params.value < companyRiskLevelValues.high;
                  }

                  return amberText;
                },

                agGridRedTexts: (params: { value: number }) => {
                  let redText = false;

                  if (typeof params.value === 'number' && !isNaN(params.value)) {
                    redText = params.value >= companyRiskLevelValues.high;
                  }

                  return redText;
                },
              };
            }

            if (
              col.toLowerCase() === ColIdEnum.partialIndexRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.vehicleRating.toLowerCase()
            ) {
              const ratings = this.cService.highMediumLowRatingValues;

              // NOTE: cellClassRules used here so that color applied to
              // cell-reference's text would not be overridden when exporting to Excel
              column.cellClassRules = {
                agGridBlackTexts: (params: { value: number }) => {
                  let veryHighRating = false;

                  if (typeof params.value === 'number' && !isNaN(params.value)) {
                    veryHighRating = params.value === ratings.veryHigh;
                  }

                  return veryHighRating;
                },

                agGridRedTexts: (params: { value: number }) => {
                  let highRating = false;

                  if (typeof params.value === 'number' && !isNaN(params.value)) {
                    highRating = params.value === ratings.high;
                  }

                  return highRating;
                },

                agGridAmberTexts: (params: { value: number }) => {
                  let mediumRating = false;

                  if (typeof params.value === 'number' && !isNaN(params.value)) {
                    mediumRating = params.value === ratings.medium;
                  }

                  return mediumRating;
                },

                agGridGreenTexts: (params: { value: number }) => {
                  let lowRating = false;

                  if (typeof params.value === 'number' && !isNaN(params.value)) {
                    lowRating = params.value === ratings.low;
                  }

                  return lowRating;
                },
              };
            }

            if (
              col.toLowerCase() === ColIdEnum.profileDriver.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.defensiveDriving.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.attitudeRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.knowledgeRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.behaviourRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.hazardRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.profileRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.driverRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.journeyRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.operatorRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.warehouseRating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.defensiveOperating.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.ratingTo.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.ratingFrom.toLowerCase() ||
              col.toLowerCase() === ColIdEnum.rating.toLowerCase()
            ) {
              const ratings = this.cService.highMediumLowRatingValues;

              // NOTE: cellClassRules used here so that color applied to
              // cell-reference's text would not be overridden when exporting to Excel
              column.cellClassRules = {
                agGridBlackTexts: (params: { value: number }) => {
                  let blackText = false;

                  if (typeof params.value === 'number' && !isNaN(params.value)) {
                    blackText = params.value === ratings.veryHigh;
                  }

                  return blackText;
                },

                agGridRedTexts: (params: { value: number }) => {
                  let redText = false;

                  if (typeof params.value === 'number' && !isNaN(params.value)) {
                    redText = params.value === ratings.high || params.value === ratings.veryHigh;
                  }

                  return redText;
                },

                agGridAmberTexts: (params: { value: number }) => {
                  let amberText = false;

                  if (typeof params.value === 'number' && !isNaN(params.value)) {
                    amberText = params.value === ratings.medium;
                  }

                  return amberText;
                },

                agGridGreenTexts: (params: { value: number }) => {
                  let greenText = false;

                  if (typeof params.value === 'number' && !isNaN(params.value)) {
                    greenText = params.value === ratings.low;
                  }

                  return greenText;
                },
              };
            }

            const percColEnumTags = Object.keys(PercentageColEnums);
            percColEnumTags.forEach((tag, key) => {
              if (col.toLowerCase() === PercentageColEnums[tag].toLowerCase()) {
                column.cellClassRules = {
                  agGridRedTexts: (params: { value: number }) => {
                    return params.value >= 50;
                  },

                  agGridGreenTexts: (params: { value: number }) => {
                    return params.value < 50;
                  },
                };
              }
            });

            const columnsWithNAValue = Object.keys(ColumnsWithNAValue).map((tag) => tag.toLowerCase());
            if (columnsWithNAValue.includes(col.toLowerCase())) {
              column.valueGetter = (params) => {
                if (!params.data[col]) {
                  return;
                }

                if (typeof params.data[col] !== 'string') {
                  return params.data[col];
                }

                if (params.data[col].toUpperCase() === 'NA' || params.data[col].toUpperCase() === 'N/A') {
                  return this.cService.readyTranslations.reportData.na;
                }

                return params.data[col];
              };
            }

            column.cellRendererSelector = (params) => {
              if (params.node.rowPinned) {
                return {
                  component: PinnedRowStyleComponent,
                };
              }

              return { component: column.cellRenderer ?? undefined };
            };

            column = { ...column, ...this.columnsService.getColumnDefinition(col) };

            agGridColumnDef.push(column);
          }
        });
      },
    });

    console.log('Transformed column structure', agGridColumnDef);
    // FICO GROUP RENDERER
    //this.attachSpecificRenderForColumns(agGridColumnDef);

    // HOURS SUM
    this.attachSpecificAggFunctions(agGridColumnDef);
    //https://vrm-mentor.atlassian.net/browse/V3DDP-527
    if (agGridColumnDef[0].headerName === '' && this.currentReportMeta.name === 'Provisioning') {
      agGridColumnDef[0].filter = false;
      agGridColumnDef[0].resizable = false;
      agGridColumnDef[0].sortable = false;
    }
    //column.comparator

    return agGridColumnDef;
  }

  private isColumnToTranslate(columnId: string, columnType: string): boolean {
    return (
      (columnType.toLowerCase() === VrmColumnTypes.text ||
        columnType.toLowerCase() === VrmColumnTypes.number ||
        columnType.toLowerCase() === VrmColumnTypes.date ||
        columnType.toLowerCase() === VrmColumnTypes.percentage) &&
      (columnId.toLowerCase() === ColIdEnum.coachingReasons.toLowerCase() ||
        columnId.toLowerCase() === ColIdEnum.partName.toLowerCase() ||
        columnId.toLowerCase() === ColIdEnum.partSide.toLowerCase() ||
        columnId.toLowerCase() === ColIdEnum.driverTimeSpent.toLowerCase() ||
        columnId.toLowerCase() === ColIdEnum.damagesDetailGrid.toLowerCase() ||
        columnId.toLowerCase() === ColIdEnum.vehicleManufacturer.toLowerCase() ||
        columnId.toLowerCase() === ColIdEnum.vehicleModel.toLowerCase() ||
        columnId.toLowerCase() === ColIdEnum.ecoDriveRating.toLowerCase() ||
        columnId.toLowerCase() === ColIdEnum.ecoComparison.toLowerCase() ||
        columnId.toLowerCase() === ColIdEnum.co2Savings.toLowerCase() ||
        columnId.toLowerCase() === ColIdEnum.dvlaLicenceStatus.toLowerCase() ||
        columnId.toLowerCase() === ColIdEnum.indexingLicenceStatus.toLowerCase() ||
        columnId.toLowerCase().startsWith('question_'))
    );
  }

  private attachSpecificAggFunctions(agGridColumnDef: ColDef[]) {
    const duration = agGridColumnDef.find((a) => a.field == 'duration');
    if (duration) {
      duration.aggFunc = 'HourSum';
    }
  }

  setCellValue(params: { value: string }, mappings: any) {
    const mapping = mappings[params.value];
    return mapping ? mapping : params.value;
  }

  getExportDataCustomization(reportStructure: any, exportAsCsv?: boolean): any {
    const canApplyCalendarDate =
      this.selectedTimeFrame && this.selectedTimeFrame.timeFrame && this.selectedTimeFrame.timeFrame.date;

    const fileName = canApplyCalendarDate
      ? this.cService.pageHeader.pageTitle + '_' + this.selectedTimeFrame.timeFrame.date.replace(/\//g, '-')
      : this.cService.pageHeader.pageTitle;

    return {
      fileName: this.cService.replaceSpecialChars(fileName),
      sheetName: `${this.cService.replaceSpecialChars(this.cService.pageHeader.pageTitle)} (VRM)`,

      processCellCallback: (params: ProcessCellForExportParams) => {
        const columnName = params.column.getColId();
        const value = params.value;
        let columnDataType: string;
        const completeColumnDefinition = this.columnsService.getColumnDefinition(columnName);

        if (completeColumnDefinition) {
          return this.getOutputForExport(params);
        }

        if (reportStructure && reportStructure[columnName]) {
          columnDataType = reportStructure[columnName].type;
        }

        let exportValue: any = value;

        if (value || !isNaN(value)) {
          if (typeof value === 'number' && columnDataType) {
            if (columnDataType.toLowerCase() === VrmColumnTypes.percentage) {
              if (!exportAsCsv) {
                exportValue = this.cService.displayNumberAsPercentage(value, this.currentReportMeta.name);
              }
            } else if (columnDataType.toLowerCase() === VrmColumnTypes.decimal) {
              if (!exportAsCsv) {
                if (ReportNameEnum.mentor && columnName.toLowerCase().includes('time')) {
                  exportValue = this.cService.numberToDecimalPlace(value, 1);
                } else if (columnName.includes('indexingDiffLast')) {
                  exportValue = this.cService.prefixNumberWithPlusSign(value, true);
                } else {
                  exportValue = this.cService.numberToDecimalPlace(value);
                }
              }
            } else if (columnDataType.toLowerCase() === VrmColumnTypes.number) {
              if (
                columnName.toLowerCase() === ColIdEnum.partialIndexRating.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.ratingTo.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.ratingFrom.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.rating.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.profileDriver.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.defensiveDriving.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.attitudeRating.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.knowledgeRating.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.behaviourRating.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.hazardRating.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.profileRating.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.driverRating.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.journeyRating.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.operatorRating.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.warehouseRating.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.defensiveOperating.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.vehicleRating.toLowerCase()
              ) {
                exportValue = this.cService.mapNumericRatingToText(NumericRatingToTextEnum.highMediumLowNa, value);
              } else if (
                columnName.toLowerCase() === ColIdEnum.companyRiskLevel.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.indexingPointsValue.toLowerCase()
              ) {
                const companyRiskLevelValues = this.cService.userProfile.companyRiskLevelValues;
                const numberFormatted = this.cService.numberToDecimalPlace(params.value);

                if (value < companyRiskLevelValues.low) {
                  exportValue = `${this.cService.readyTranslations.reportData.safeDriver} (${numberFormatted})`;
                }

                if (value >= companyRiskLevelValues.low && value < companyRiskLevelValues.medium) {
                  exportValue = `${this.cService.readyTranslations.reportData.low} (${numberFormatted})`;
                }

                if (value >= companyRiskLevelValues.medium && value < companyRiskLevelValues.high) {
                  exportValue = `${this.cService.readyTranslations.reportData.medium} (${numberFormatted})`;
                }

                if (value >= companyRiskLevelValues.high) {
                  exportValue = `${this.cService.readyTranslations.reportData.high} (${numberFormatted})`;
                }
              } else if (
                columnName.toLowerCase() === ColIdEnum.accelerationRating.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.brakingRating.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.corneringRating.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.distractionRating.toLowerCase() ||
                columnName.toLowerCase() === ColIdEnum.speedRating.toLowerCase()
              ) {
                exportValue = this.cService.mapNumericRatingToText(NumericRatingToTextEnum.poorAverageGoodNa, value);
              }
            }
          }

          if (exportValue && typeof exportValue === 'string') {
            const exemptedColumns = [
              ColIdEnum.vin.toLowerCase(),
              ColIdEnum.driverId.toLowerCase(),
              ColIdEnum.pin.toLowerCase(),
              ColIdEnum.station.toLowerCase(),
              ColIdEnum.preTrip.toLowerCase(),
              ColIdEnum.postTrip.toLowerCase(),
              ColIdEnum.same.toLowerCase(),
              ColIdEnum.originOfLicence.toLowerCase(),
              ColIdEnum.countryOfIssue.toLowerCase(),
            ];

            if (columnDataType === VrmColumnTypes.text) {
              if (
                columnName.toLowerCase() !== ColIdEnum.firstName.toLowerCase() &&
                columnName.toLowerCase() !== ColIdEnum.lastName.toLowerCase() &&
                columnName.toLowerCase() !== ColIdEnum.email.toLowerCase()
              ) {
                const translatedValue = this.transformSingleDataForDetailsPane(columnName, exportValue);
                if (translatedValue) {
                  exportValue = translatedValue.uiValue;
                }
              }
            }

            if (!exportValue.includes('@') && !exemptedColumns.includes(columnName.toLowerCase())) {
              const firstLetterIsUppercase: RegExp = /^[A-Z]/;

              if (Number.isNaN(Number(exportValue.split('')[0])) && !firstLetterIsUppercase.test(exportValue)) {
                exportValue = this.cService.toTitleCase(exportValue);
              }

              exportValue = this.cService.toVrmCaps(exportValue);
            }

            if (
              columnName.toLowerCase() === ColIdEnum.dsp.toLowerCase() ||
              columnName.toLowerCase() === ColIdEnum.station.toLowerCase() ||
              columnName.toLowerCase() === ColIdEnum.derDsp.toLowerCase()
            ) {
              exportValue = exportValue.toUpperCase();
            }
          }
        }

        return exportValue;
      },
    };
  }

  public getOutputForExport(params: ProcessCellForExportParams): string {
    const colDef = params.column.getColDef();
    const value = params.value;

    if (colDef.refData) {
      return colDef.refData[value] ?? value;
    }

    if (typeof colDef.valueFormatter === 'function') {
      return colDef.valueFormatter({
        ...params,
        data: params.node.data,
        node: params.node,
        colDef: colDef,
      });
    }

    return value;
  }

  // Used when regenerating fewer columns for mobile
  convertColNamesToAgColDefinition(agGridColumns: Array<ColDef>, colNames: Array<string>): Array<ColDef | ColGroupDef> {
    const agGridColumnDef: Array<ColDef> = [];

    colNames.forEach((col: string) => {
      const agColumn = agGridColumns.find((agCol) => agCol.field === col);

      if (agColumn) {
        agGridColumnDef.push(agColumn);
      }
    });

    return agGridColumnDef;
  }

  convertToAgGroupedColumnDefinition(groupStructure: any): Array<ColGroupDef> {
    const agGridGroupDef: Array<ColGroupDef> = [];

    groupStructure.forEach((group: any) => {
      const columnGroup: ColGroupDef = {
        headerName: group.groupName,
        children: this.convertToAgColumnDefinition(group.structure),
      };

      agGridGroupDef.push(columnGroup);
    });

    console.log('Transformed grouped column definition', agGridGroupDef);
    return agGridGroupDef;
  }

  caseInsensitiveSort(valueA: any, valueB: any): number {
    if (!valueA) {
      valueA = '';
    }
    if (!valueB) {
      valueB = '';
    }

    return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
  }

  setGridStatePreferences(gridState: any) {
    // Since we only get updated user-profile on login, we need to ensure this is cached for next refresh;
    if (this.cService.userProfile.preferences) {
      this.cService.userProfile.preferences.gridstate = gridState;
    } else {
      this.cService.userProfile.preferences = {
        gridstate: gridState,
      };
    }

    if (this.cService.userProfile.preferences.gridstate) {
      this.cService.setUserProfileToLocalStorage();
    }
  }

  saveReportState(): Observable<any> {
    const stringifiedGridState = JSON.stringify(this.gridState);
    console.log('%c Stringified Report State', 'color: limegreen', stringifiedGridState);

    return this.http
      .post(
        `${Config.apiRoot.uri}preferences/gridstate`,
        { preferences: stringifiedGridState },
        {
          headers: { vrmCustom_requestName: this.cService.readyTranslations.generic.reportState },
        }
      )
      .pipe(
        map((response) => {
          this.cService.userProfile.hasCustomGridState = true;
          this.setGridStatePreferences(this.gridState);

          console.log('Report State Saved', response);
          return response;
        })
      );
  }

  deleteGridStatePreferences(): Observable<any> {
    return this.http.delete(`${Config.apiRoot.uri}preferences/gridstate`).pipe(
      map((response) => {
        console.log('Report State DELETED', response);
        return response;
      })
    );
  }

  resetReportState() {
    this.clearCachedFilters();

    this.deleteGridStatePreferences().subscribe({
      next: () => {
        this.gridStateDifferentFromDefault = false;
        this.cService.userProfile.hasCustomGridState = false;

        if (this.companyDefaultGridState) {
          this.setGridStatePreferences(this.companyDefaultGridState);
        } else {
          this.setGridStatePreferences(this.defaultGridState());
        }

        this.cService.reloadAndTryToApplyUpdates();
      },
    });
  }

  applyServerFilters(filters: { [key: string]: string[] }) {
    this.requestedServerFilters = filters;

    if (this.cService.navigatedReport) {
      const isCommonReport = this.cService.navigatedReport.commonReportPath ? true : false;
      this.triggerServerFilterChangedObservable(isCommonReport);

      this.persistedStateChanged = true;
      this.gridState.selectedServerFilter = filters;
    }
  }

  autoPopulateManagedDriversForManagement(reportData?: Array<any>, gridOptions?: GridOptions) {
    if (this.cService.userProfile.manages && this.cService.userProfile.manages.length > 0) {
      if (reportData && this.currentReportData.length === 0 && reportData.length > 0) {
        this.currentReportData = reportData;
      }

      this.cService.userProfile.manages.forEach((driverPin) => {
        const driverData = this.currentReportData.find((data) => data.pin == driverPin);

        if (driverData) {
          this.selectedRows.push(driverData);
        }

        const selectedRowIndex: number = this.currentReportData.findIndex((data) => data.pin == driverPin);

        if (gridOptions) {
          gridOptions.api.forEachNode((node) => {
            if (node.data.pin == driverPin) {
              node.setSelected(true);
            }
          });
        }
      });
    }

    this.ensureDistinctRowSelection();

    console.log('%c Load drivers managed by this user', 'color: orange', {
      selectedRows: this.selectedRows,
      currentReportData: this.currentReportData,
    });
  }

  ensureDistinctRowSelection() {
    if (this.selectedRows.length > 0 && this.selectedRows[0] && this.selectedRows[0].pin) {
      this.selectedRows = Array.from(new Set(this.selectedRows.map((item: any) => item)));
    }
  }

  private hasServerFiltersInRequest(): boolean {
    let hasFilters: boolean = false;
    const filterKeys = Object.keys(this.requestedServerFilters);

    for (const key of filterKeys) {
      if (this.requestedServerFilters[key] && this.requestedServerFilters[key].length) {
        hasFilters = true;
        break;
      }
    }

    return hasFilters;
  }

  cacheFiltersAcrossClientInstances() {
    localStorage.setItem(this.cService.localStorageKeys.gridFilterCache, JSON.stringify(this.gridState.filterModel));
  }

  clearCachedFilters() {
    localStorage.removeItem(this.cService.localStorageKeys.gridFilterCache);
    localStorage.removeItem(this.cService.localStorageKeys.gridCustomHighMedLow);
  }

  getCachedFilters() {
    const cachedFilters = localStorage.getItem(this.cService.localStorageKeys.gridFilterCache);
    return cachedFilters ? JSON.parse(cachedFilters) : null;
  }

  // ---- COMMON REPORTS ----
  /**
   * Must manually hide indeterminate progress on subscription outcomes (success / fail).
   * This is necessary due to the injected vrmCustom_manuallyHideProgress header.
   */
  commonReport(reportName: string): Observable<any> {
    const requestHasServerFilters: boolean = this.hasServerFiltersInRequest();
    this.showReportSideNav = false;

    let ssrSearchText = '';
    if (this.currentReportMeta.usesServerSideRendering) {
      ssrSearchText = this.gridSearch.text.trim().replace(/\s+/g, ' ');
    }

    this.filterData = {
      filter: requestHasServerFilters ? this.requestedServerFilters : {},

      periodFilter: this.selectedTimeFrame.timeFrame,
      course: this.endpointFilterValue ? this.endpointFilterValue.value : '',

      gridParams: this.cachedAgGridServerParams,

      search: ssrSearchText,
      decorate: Config.disableStationAssignment ? 'no' : 'yes',
      selectedLanguage: this.cService.selectedLanguage.code,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    const payloadCopy = JSON.stringify(this.filterData);
    const payload = JSON.parse(payloadCopy);

    return this.http
      .post(`${Config.apiRoot.uri}reports/${reportName}`, payload, {
        headers: {
          vrmCustom_requestName: reportName,
          vrmCustom_manuallyHideProgress: reportName,
        },
      })
      .pipe(
        map((response) => {
          this.cachedSsrSearchText = this.filterData.search;
          return response;
        })
      );
  }

  commonReportGet(reportName: string) {
    return this.http.get(`${Config.apiRoot.uri}reports/${reportName}`).pipe(
      map((response) => {
        return response;
      })
    );
  }

  /**
   * Must manually hide indeterminate progress on subscription outcomes (success / fail).
   * This is necessary due to the injected vrmCustom_manuallyHideProgress header.
   */
  commonReportMeta(reportName: string): Observable<any> {
    return this.http
      .get(`${Config.apiRoot.uri}reports/${reportName}/meta` + this.attachExtraMetaURLParameters(), {
        headers: {
          vrmCustom_manuallyHideProgress: reportName,
        },
      })
      .pipe(
        map((response: { structure: any; endpoints: any; operations?: any }) => {
          console.log('Report Meta', response);
          if (response.endpoints) {
            this.commonReportDrilldownEndpoints = response.endpoints;
          }
          if (response.operations) {
            this.adminRowOperations = response.operations;
          }
          return response;
        })
      );
  }

  commonReportBatch(endpointPath: string, maxPage: number, rowsPerPage: number): Array<Observable<any>> {
    let startRow: number = 0;
    let endRow: number = rowsPerPage;

    const collatedRequests: Array<Observable<any>> = [];

    this.cachedAgGridServerParams.startRow = startRow;
    this.cachedAgGridServerParams.endRow = endRow;

    for (let i = 1; i <= maxPage; i++) {
      const request = this.commonReport(endpointPath);
      collatedRequests.push(request);

      startRow = startRow + rowsPerPage;
      endRow = endRow + rowsPerPage;

      this.cachedAgGridServerParams.startRow = startRow;
      this.cachedAgGridServerParams.endRow = endRow;
    }

    return collatedRequests;
  }

  getAsyncFilterValuesFromServer(columnIdentifier: string): Observable<any> {
    const currentCommonRptEndpoint = this.currentReportMeta.endpoint
      ? `${Config.apiRoot.uri}reports/${this.currentReportMeta.endpoint.replace(
          'reports/',
          ''
        )}/filterValues?column=${columnIdentifier}`
      : `${this.currentReportMeta.dataUri}/filterValues?column=${columnIdentifier}`;

    return this.http.get(currentCommonRptEndpoint).pipe(
      map((response: { data: Array<number | string> }) => {
        const typeAdjustedData = [];

        if (response.data && response.data.length) {
          response.data.forEach((dataItem: number | string) => {
            if (typeof dataItem === 'number') {
              typeAdjustedData.push(dataItem.toString());
            } else {
              typeAdjustedData.push(dataItem);
            }
          });
        }

        console.log('%c CONVERTED Asynchronous Filter Values', 'color: lightblue', typeAdjustedData);

        return typeAdjustedData;
      })
    );
  }

  // ---- JOINS ----
  commonReportMetaForkJoined(reportName: string): Observable<any> {
    return forkJoin([this.commonReportMeta(reportName), this.commonReport(reportName)]);
  }

  attachExtraMetaURLParameters(): string {
    let extraHeaders = {};
    let urlParameters = '';

    const hasServerFiltersInRequest = this.hasServerFiltersInRequest();

    if (hasServerFiltersInRequest && this.currentReportMeta.singleServerFilter != null) {
      if (this.requestedServerFilters.hasOwnProperty(this.currentReportMeta.singleServerFilter.key)) {
        extraHeaders['filter'] = this.requestedServerFilters[this.currentReportMeta.singleServerFilter.key][0];
      }
    }

    if (hasServerFiltersInRequest && this.currentReportMeta && this.currentReportMeta.navigationFilters != null) {
      extraHeaders = this.requestedServerFilters;
    }

    if (this.currentReportMeta?.defaultPeriod) {
      extraHeaders = this.selectedTimeFrame.timeFrame;
    }

    if (extraHeaders && Object.keys(extraHeaders).length) {
      urlParameters = '?' + new URLSearchParams(extraHeaders);
    }

    return urlParameters;
  }

  // ---- PROFILE INSIGHT ----
  profileInsightForkJoined(): Observable<any> {
    return forkJoin([
      this.commonReport(this.reportEndpoint.roadriskProfileInsight),
      this.commonReportMeta(this.reportEndpoint.roadriskProfileInsight),
    ]);
  }

  // Get total trip session count (Login / Logout)
  sendLoginLogoutCountRequest(secondaryPath: string): Observable<any> {
    return this.cService.commonGet(secondaryPath);
  }

  // Send a alert resolve request
  sendAlertResolveRequest(secondaryPath: string, postData: any): Observable<any> {
    return this.cService.commonPost(secondaryPath, postData);
  }

  getDownloadableReportDataFromServer(reportId: number): Observable<any> {
    return this.cService.commonGetWithResponseType(`reports/custom/report/${reportId}/generate`, 'blob');
  }

  // Assigns the provided recommended coaching interventions.
  assignRecommendedCoaching(ids: number | number[]): Observable<null> {
    return this.cService.commonPost('coaching_interventions/recommendations/accept', {
      recommendations: Array.isArray(ids) ? ids : [ids],
    });
  }

  // Rejects the provided recommended coaching interventions
  rejectRecommendCoaching(ids: number | number[]): Observable<null> {
    return this.cService.commonPost('coaching_interventions/recommendations/reject', {
      recommendations: Array.isArray(ids) ? ids : [ids],
    });
  }

  getEmptyGroupCellRenderer() {
    function renderer() {}

    renderer.prototype.init = function (params) {
      const tempDiv = document.createElement('div');
      if (params.node.group) {
        tempDiv.innerHTML = '<span>' + '</span>';
      } else {
        tempDiv.innerHTML = '<span style="color:black; font-weight:normal; ">' + params.value + '</span>';
      }
      this.eGui = tempDiv.firstChild;
    };
    renderer.prototype.getGui = function () {
      return this.eGui;
    };
    return renderer;
  }

  public transformSingleDataBasedOnAdjacentFields(
    transformedData: TransformedSingleData,
    columnName: string,
    selectedRow: Object
  ) {
    if (columnName.toLowerCase() === ColIdEnum.ratingHigh.toLowerCase() && selectedRow !== null) {
      transformedData.uiValue = `${selectedRow[columnName]} (${selectedRow[ColIdEnum.ratingHighPercent]}%)`;
      transformedData.color = 'red';
    } else if (columnName.toLowerCase() === ColIdEnum.ratingMedium.toLowerCase() && selectedRow !== null) {
      transformedData.uiValue = `${selectedRow[columnName]} (${selectedRow[ColIdEnum.ratingMediumPercent]}%)`;
      transformedData.color = 'orange';
    } else if (columnName.toLowerCase() === ColIdEnum.ratingLow.toLowerCase() && selectedRow !== null) {
      transformedData.uiValue = `${selectedRow[columnName]} (${selectedRow[ColIdEnum.ratingLowPercent]}%)`;
      transformedData.color = 'green';
    } else if (columnName.toLowerCase() === ColIdEnum.averageDriverScore.toLowerCase() && selectedRow !== null) {
      transformedData.color = selectedRow[ColIdEnum.relativeDriverScore];
    } else if (columnName.toLowerCase() === ColIdEnum.averageDriverEvents.toLowerCase() && selectedRow !== null) {
      transformedData.color = selectedRow[ColIdEnum.relativeDriverEvents];
    }
    return transformedData;
  }

  closeSideNav(): void {
    this.showReportSideNav = false;
  }

  private getTranslationsForMultiValuedColumn(columnValue: string, separator: string = ','): string {
    const translationTags = columnValue.split(separator);

    return translationTags
      .map((value: string) => this.cService.getSingleTranslation(`REPORTDATA.${value.trim()}`, value.trim()))
      .join(', ');
  }

  public getRendererConfigFor(colId: string): { [key: string]: any } | null {
    const column = this.currentReportMeta.structure[colId];

    return column?.cellRenderer?.config;
  }

  public getGridStateFilterModelForCurrentReport() {
    const currentMetaColIds = Object.keys(this.currentReportMeta.structure);
    const filterModelKeys = Object.keys(this.gridState.filterModel);
    let filterModelCopy = Object.assign({}, this.gridState.filterModel);

    filterModelKeys.forEach((colId) => {
      if (!currentMetaColIds.includes(colId)) {
        delete filterModelCopy[colId];
      }
    });
    return filterModelCopy;
  }
}
